import { useState, useMemo, useCallback, Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { computeRowProfit, type VariableRatesSummary } from "@/lib/row-profit";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useDashboardCurrency } from "@/hooks/useDashboardCurrency";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartSkeleton,
  DashboardSkeleton,
  KpiGridSkeleton,
  ListSkeleton,
  TableSkeleton,
} from "@/components/skeletons";
import { AdPreviewContent } from "@/components/ad-preview-content";
import { SetterAiAnalysisToggle, CallAnalysisButton } from "@/components/setter-call-analysis";
import { type SourceBucket } from "@/components/source-breakdown-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useDayNotes } from "@/hooks/useDayNotes";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  Eye,
  MousePointerClick,
  Users,
  DollarSign,
  Target,
  BarChart3,
  ArrowLeft,
  LogOut,
  User,
  CalendarIcon,
  ArrowUpDown,
  Percent,
  Wallet,
  UserCheck,
  LineChart as LineChartIcon,
  Settings,
  ChevronRight,
  ChevronDown,
  Search,
  Filter,
  X,
  Check,
  Save,
  Phone,
  PhoneCall,
  PhoneOff,
  Clock,
  CalendarCheck,
  ShoppingCart,
  ExternalLink,
  Sparkles,
  Star,
  Layers,
  Globe,
  GripVertical,
  Plug,
  FileText,
  Monitor,
} from "lucide-react";
import AnalyticsSettingsModal from "@/components/analytics-settings-modal";
import QuarterlyForecastCard from "@/components/quarterly-forecast-card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { toLocalYmd } from "@/lib/date-format";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import LeadJourneyDialog from "@/components/lead-journey-dialog";
import DashboardReportDialog from "@/components/dashboard-report-dialog";
import PreviewBanner from "@/components/preview-banner";
import SaasFunnelCards from "@/components/saas-funnel-cards";
import { ProfitCard } from "@/components/profit-card";
import RoasWorldTab from "@/components/roas-world/roas-world-tab";
import ShopSection from "@/components/shop/shop-section";
import { canAccessShopifyIntegration, type ShopifyAccessUser } from "@shared/shopify-access";
import DashboardMetricCard, { type TrendType } from "@/components/ui/dashboard-overview";
import { ExcelTable } from "@/components/ui/excel-style-table";
import { DropdownRangeDatePicker } from "@/components/ui/dropdown-range-date-picker";
import DashboardControlsMenubar from "@/components/ui/nested-dashboard-menu";
import { useTheme } from "@/components/theme-provider";
import type { TotalsData } from "@/components/customizable-widget-panel";
import { standardAllowedModules } from "@shared/module-access";
import { groupLeadSourceBuckets, PAID_PLATFORM_CHIP_LABELS } from "@shared/lead-source-grouping";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  AreaChart as VisxAreaChart,
  Area as VisxArea,
  Grid as VisxGrid,
  XAxis as VisxXAxis,
  YAxis as VisxYAxis,
  ChartTooltip as VisxChartTooltip,
} from "@/components/ui/area-chart";

interface CampaignInsight {
  campaignId: string;
  campaignName: string;
  status?: string;
  spend: string;
  impressions: string;
  clicks: string;
  cpc: string;
  cpm: string;
  ctr: string;
  reach: string;
  leads: string;
  costPerLead: string;
  trials: string;
  costPerTrial: string;
  subscriptions: string;
  costPerSubscription: string;
  actions: { actionType: string; value: string }[];
  costPerActionType: { actionType: string; value: string }[];
  purchaseValue: string;
  purchaseRoas: string;
  _source?: "meta" | "google" | "tiktok";
  _accountId?: string;
  _accountName?: string;
}

interface UtmAttributionRow {
  metaCampaignId: string;
  metaAdSetId: string;
  leads: number;
  newLeads: number;
  bookedCalls: number;
  showed: number;
  noShow: number;
  cancelled: number;
  pending: number;
  upcoming: number;
  won: number;
  lost: number;
  openDeals: number;
  revenue: number;
  bookingRate: number;
  showRate: number;
  closeRate: number;
}

interface LeadDetail {
  id: string;
  name: string;
  email: string;
  phone: string;
  source: string;
  createdAt: string;
  dateUpdated: string;
  isNew: boolean;
  tags: string[];
  status: string;
  metaCampaignId: string;
  metaAdSetId: string;
  metaAdId: string;
  adName: string | null;
  adAccountId: string | null;
  appointments: { id: string; title: string; status: string; startTime: string }[];
  opportunities: {
    id: string;
    name: string;
    status: string;
    monetaryValue: number;
    createdAt: string;
  }[];
  // Webinar attendance
  webinarAttended?: boolean;
  webinarAttendedAt?: string;
  // Google Sheets extra fields
  budget?: string;
  buyTimeline?: string;
  goal?: string;
  interest?: string;
  notes?: string[];
}

interface UtmSourceBreakdownRow {
  utmSource: string;
  leads: number;
  newLeads: number;
  bookedCalls: number;
  showed: number;
  noShow: number;
  cancelled: number;
  pending: number;
  upcoming: number;
  won: number;
  lost: number;
  openDeals: number;
  revenue: number;
  bookingRate: number;
  showRate: number;
  closeRate: number;
}

type AdUtmData = Record<
  string,
  {
    leads: number;
    newLeads: number;
    bookedCalls: number;
    showed: number;
    noShow: number;
    won: number;
    revenue: number;
  }
>;

interface OrganicSourceItem {
  label: string;
  leads: number;
  newLeads: number;
  bookedCalls: number;
  showed: number;
  noShow: number;
  won: number;
  revenue: number;
  bookingRate: number;
  showRate: number;
  closeRate: number;
}

interface OrganicSourceGroup {
  utmSource: string;
  utmMedium: string;
  leads: number;
  newLeads: number;
  bookedCalls: number;
  showed: number;
  noShow: number;
  won: number;
  revenue: number;
  bookingRate: number;
  showRate: number;
  closeRate: number;
  items: OrganicSourceItem[];
}

interface PreviewMeta {
  enabled: boolean;
  reason: "no_integration" | "token_expired" | "no_accounts" | "no_agency" | "no_campaigns";
  source: "estimated";
}

// Lustro AdPlatformMetrics z server/services/dashboard-ad-platform-totals.ts (pola czytane przez chipy).
type AdPlatformMetricsView = {
  spend: number;
  impressions: number;
  outboundClicks: number;
  uniqueOutboundClicks: number;
  reach: number;
  landingPageViews: number;
  cpm: number;
  cpc: number;
  ctr: number;
};

// VP-214: przychód (netto po zwrotach) + liczba sprzedaży Shopify per obiekt Meta
// (adset/reklama). Threaded do wierszy adsetów/reklam w tabeli kampanii (tryb ecom).
type EcomRevenueMaps = {
  revenueByCampaign?: Record<string, number>;
  revenueByAdSet?: Record<string, number>;
  revenueByAd?: Record<string, number>;
  salesByCampaign?: Record<string, number>;
  salesByAdSet?: Record<string, number>;
  salesByAd?: Record<string, number>;
};

interface DashboardData {
  campaigns: CampaignInsight[];
  adAccountName: string;
  datePreset: string;
  utmAttribution: UtmAttributionRow[];
  adUtmAttribution?: AdUtmData;
  // Per-adset/per-ad liczby leadów z rekordów (tylko gdy metricsV2 ON; puste gdy OFF).
  leadsByAdSet?: Record<string, number>;
  leadsByAd?: Record<string, number>;
  // VP-214: przychód (netto po zwrotach) + sprzedaż Shopify per kampania/adset/reklama.
  revenueByCampaign?: Record<string, number>;
  revenueByAdSet?: Record<string, number>;
  revenueByAd?: Record<string, number>;
  salesByCampaign?: Record<string, number>;
  salesByAdSet?: Record<string, number>;
  salesByAd?: Record<string, number>;
  utmSourceBreakdown?: UtmSourceBreakdownRow[];
  organicSourceGroups?: OrganicSourceGroup[];
  sourceBreakdown?: { buckets: SourceBucket[] };
  adPlatformTotals?: {
    meta: AdPlatformMetricsView;
    google: AdPlatformMetricsView;
    tiktok: AdPlatformMetricsView;
  };
  totals: {
    spend: number;
    impressions: number;
    clicks: number;
    outboundClicks: number;
    uniqueOutboundClicks: number;
    reach: number;
    leads: number;
    trials: number;
    subscriptions: number;
    costPerTrial: number;
    costPerSubscription: number;
    landingPageViews: number;
    costPerLead: number;
    cpm: number;
    cpc: number;
    ctr: number;
    crLpToLead: number;
    crClickToLead: number;
    purchaseValue: number;
    purchases: number;
    roas: number;
    revenueRoas: number;
    cashCollectedRoas: number;
    ltv: number;
    ltvCac: number;
    revenue: number;
    cashCollected: number;
    customers: number;
    bookedAppointments: number;
    showedAppointments: number;
    noShowAppointments: number;
    showRate: number;
    qualificationRate: number;
    crLeadToBooking: number;
    avgSalesCycleDays: number;
    sales: number;
    salesRevenue: number;
  };
  // Both modes are cash-basis (revenue from sales that CLOSED in the window — reconciles with Sales Data).
  // 'attribution' = tracking_conversions available, so each sale is credited to its ad via first-touch.
  // 'cash'        = no tracking_conversions; aggregate revenue from the GHL live fallback (no per-ad credit).
  revenueMode?: "attribution" | "cash";
  attributionFootprint?: {
    customers: number;
    revenue: number;
  } | null;
  comparison?: {
    totals: DashboardData["totals"];
    label: string | null;
  } | null;
  webinarStats?: {
    totalAttended: number;
    totalOfferReached: number;
    byCampaignId: Record<string, { attended: number; offerReached: number }>;
    byAdSetId?: Record<string, { attended: number; offerReached: number }>;
    byAdId?: Record<string, { attended: number; offerReached: number }>;
    byCampaignName?: Record<string, { attended: number; offerReached: number }>;
    byAdSetName?: Record<string, { attended: number; offerReached: number }>;
    byAdName?: Record<string, { attended: number; offerReached: number }>;
  } | null;
  preview?: PreviewMeta | null;
  // VP-138: nowy tor metryk (gdy flaga ON). Obecność oznacza, że konwersyjne pola `totals`/
  // `utmAttribution` pochodzą z JEDNEGO zdeduplikowanego agregatu (kafelek == SUMA == komórki).
  metricsV2?: {
    totals: {
      leads: number;
      revenue: number;
      sales: number;
      bookedCalls: number;
      qualifiedCalls: number;
      spend: number;
      roas: number;
      showRate: number;
      qualificationRate: number;
      crLeadToBooking: number;
    };
    byGroup: Array<{
      groupKey: string;
      source: string;
      isPaid: boolean;
      leads: number;
      bookedCalls: number;
      qualifiedCalls: number;
      sales: number;
      revenue: number;
      spend: number;
      roas: number;
    }>;
  } | null;
}

interface AdSetInsight {
  adSetId: string;
  adSetName: string;
  campaignId: string;
  spend: string;
  impressions: string;
  clicks: string;
  cpc: string;
  cpm: string;
  ctr: string;
  reach: string;
  leads: number;
  costPerLead: number;
}

interface AdInsight {
  adId: string;
  adName: string;
  adSetId: string;
  campaignId: string;
  spend: string;
  impressions: string;
  clicks: string;
  cpc: string;
  cpm: string;
  ctr: string;
  reach: string;
  leads: number;
  costPerLead: number;
}

interface AdAccount {
  id: string;
  name: string;
  accountStatus: number;
  currency: string;
}

interface MetaStatus {
  connected: boolean;
  tokenExpired?: boolean;
  adAccounts?: AdAccount[];
  selectedAdAccountId?: string;
  assignedByAdmin?: boolean;
}

interface SetterStatsData {
  // Call volume
  outgoingCallAttempts: number;
  answeredCalls: number;
  missedCalls: number;
  // Duration
  totalCallDurationSec: number;
  avgCallDurationSec: number;
  // Booking
  bookedMeetings: number;
  // Appointment outcomes
  showedMeetings: number;
  noShowMeetings: number;
  confirmedMeetings: number;
  cancelledMeetings: number;
  // Sales (from LeadConnector opportunities)
  sales: number;
  salesValue: number;
  lost: number;
  // Rates
  contactRate: number;
  bookingRate: number;
  bookingRateFromAttempts: number;
  showRate: number;
  closeRate: number;
  // Breakdowns
  dailyStats: {
    date: string;
    attempts: number;
    answered: number;
    booked: number;
    showed: number;
    sales: number;
    totalDurationSec: number;
  }[];
  recentCalls: {
    id: string;
    contactName: string;
    contactPhone: string;
    direction: string;
    status: string;
    durationSec: number;
    dateAdded: string;
    analysisStatus?: string | null;
    analysisScore?: number | null;
  }[];
}

function round(value: number, decimals = 2): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function buildPreviewSetterStats(
  totals: DashboardData["totals"] | undefined,
  daily: DailyInsight[] | undefined
): SetterStatsData | undefined {
  if (!totals) return undefined;

  const outgoingCallAttempts = Math.max(24, Math.round(totals.bookedAppointments * 2.7));
  const answeredCalls = Math.max(
    totals.bookedAppointments,
    Math.round(outgoingCallAttempts * 0.58)
  );
  const showedMeetings = Math.max(
    totals.showedAppointments,
    Math.round(totals.bookedAppointments * 0.72)
  );
  const noShowMeetings = Math.max(
    totals.noShowAppointments,
    Math.max(0, totals.bookedAppointments - showedMeetings)
  );
  const confirmedMeetings = Math.max(0, Math.round(totals.bookedAppointments * 0.17));
  const sales = Math.max(0, totals.sales);
  const salesValue = Math.max(0, totals.salesRevenue || totals.revenue);
  const dailySource = daily && daily.length > 0 ? daily : [];
  const dailyStats = dailySource.map((point) => {
    const attempts = Math.max(1, Math.round((point.bookedMeetings || 1) * 2.6));
    const answered = Math.max(point.bookedMeetings, Math.round(attempts * 0.57));
    const booked = Math.max(0, point.bookedMeetings || 0);
    const showed = Math.max(0, Math.round(booked * 0.71));
    const pointSales = Math.max(0, Math.round(booked * 0.22));
    return {
      date: point.date,
      attempts,
      answered,
      booked,
      showed,
      sales: pointSales,
      totalDurationSec: answered * 198,
    };
  });

  const recentCalls = Array.from({ length: 6 }, (_, index) => ({
    id: `preview-call-${index + 1}`,
    contactName: [
      "Anna Kowalska",
      "Mark Wilson",
      "Julia Nowak",
      "Kamil Mazur",
      "Eva Green",
      "Peter Smith",
    ][index],
    contactPhone: `+48 600 12${index} ${30 + index}`,
    direction: "outbound",
    status: index < 4 ? "answered" : "missed",
    durationSec: index < 4 ? 180 + index * 46 : 0,
    dateAdded: new Date(Date.now() - index * 3_600_000).toISOString(),
  }));

  return {
    outgoingCallAttempts,
    answeredCalls,
    missedCalls: Math.max(0, outgoingCallAttempts - answeredCalls),
    totalCallDurationSec: dailyStats.reduce((sum, row) => sum + row.totalDurationSec, 0),
    avgCallDurationSec:
      answeredCalls > 0
        ? Math.round(dailyStats.reduce((sum, row) => sum + row.totalDurationSec, 0) / answeredCalls)
        : 0,
    bookedMeetings: totals.bookedAppointments,
    showedMeetings,
    noShowMeetings,
    confirmedMeetings,
    cancelledMeetings: Math.max(0, Math.round(totals.bookedAppointments * 0.09)),
    sales,
    salesValue,
    lost: Math.max(0, Math.round(totals.bookedAppointments * 0.16)),
    contactRate:
      outgoingCallAttempts > 0 ? round((answeredCalls / outgoingCallAttempts) * 100, 2) : 0,
    bookingRate:
      answeredCalls > 0 ? round((totals.bookedAppointments / answeredCalls) * 100, 2) : 0,
    bookingRateFromAttempts:
      outgoingCallAttempts > 0
        ? round((totals.bookedAppointments / outgoingCallAttempts) * 100, 2)
        : 0,
    showRate:
      totals.bookedAppointments > 0
        ? round((showedMeetings / totals.bookedAppointments) * 100, 2)
        : 0,
    closeRate: showedMeetings > 0 ? round((sales / showedMeetings) * 100, 2) : 0,
    dailyStats,
    recentCalls,
  };
}

// ─── AllegroChart — reusable chart with toggleable metrics ───
interface AllegroMetric {
  key: string;
  label: string;
  color: string;
  format: "currency" | "number";
  yAxis: "left" | "right";
}

function AllegroChart({
  title,
  data,
  metrics,
  chartId,
}: {
  title: string;
  data: any[];
  metrics: AllegroMetric[];
  chartId: string;
}) {
  const [selected, setSelected] = useState<string[]>(metrics.slice(0, 2).map((m) => m.key));
  const active = metrics.filter((m) => selected.includes(m.key));
  const hasLeft = active.some((m) => m.yAxis === "left");
  const hasRight = active.some((m) => m.yAxis === "right");
  const dualAxis = hasLeft && hasRight;

  if (!data || data.length < 2) return null;
  return (
    <Card className="bg-muted/50 border-border rounded-xl">
      <div className="p-4 md:p-5 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-dense">
          <h2 className="text-foreground font-semibold text-base flex items-center gap-2">
            <LineChartIcon className="w-4 h-4 text-[#FF5A00]" />
            {title}
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {metrics.map((m) => {
              const isOn = selected.includes(m.key);
              return (
                <button
                  key={m.key}
                  onClick={() =>
                    setSelected((prev) => {
                      if (prev.includes(m.key))
                        return prev.length > 1 ? prev.filter((k) => k !== m.key) : prev;
                      return [...prev, m.key];
                    })
                  }
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border ${isOn ? "border-border text-foreground" : "border-transparent text-muted-foreground hover:text-muted-foreground hover:border-border"}`}
                  style={
                    isOn
                      ? { backgroundColor: `${m.color}20`, borderColor: `${m.color}50` }
                      : undefined
                  }
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: isOn ? m.color : "var(--border)" }}
                  />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <CardContent className="p-4 md:p-5">
        <div className="h-72 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 5, right: dualAxis ? 20 : 5, bottom: 5, left: 5 }}
            >
              <defs>
                {active.map((m) => (
                  <linearGradient
                    key={m.key}
                    id={`${chartId}-${m.key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor={m.color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={m.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
              <XAxis
                dataKey="date"
                tickFormatter={(d: string) => {
                  const dt = new Date(d);
                  return `${dt.getDate().toString().padStart(2, "0")}.${(dt.getMonth() + 1).toString().padStart(2, "0")}`;
                }}
                stroke="var(--chart-foreground-muted)"
                tick={{ fill: "var(--chart-foreground-muted)", fontSize: 11 }}
                axisLine={{ stroke: "var(--chart-grid)" }}
              />
              <YAxis
                yAxisId="left"
                stroke="var(--chart-foreground-muted)"
                tick={{ fill: "var(--chart-foreground-muted)", fontSize: 11 }}
                axisLine={{ stroke: "var(--chart-grid)" }}
                tickFormatter={(v: number) =>
                  v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v * 100) / 100)
                }
                width={55}
              />
              {dualAxis && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="var(--chart-foreground-muted)"
                  tick={{ fill: "var(--chart-foreground-muted)", fontSize: 11 }}
                  axisLine={{ stroke: "var(--chart-grid)" }}
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v))
                  }
                  width={55}
                />
              )}
              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--chart-tooltip-background)",
                  border: "1px solid var(--border)",
                  borderRadius: "10px",
                  padding: "10px 14px",
                }}
                labelStyle={{ color: "var(--chart-tooltip-muted)", fontSize: 11, marginBottom: 4 }}
                labelFormatter={(label: string) =>
                  new Date(label).toLocaleDateString("pl-PL", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                }
                formatter={(value: number, name: string) => {
                  const met = metrics.find((m) => m.key === name);
                  const fmt =
                    met?.format === "currency" ? formatCurrency(value) : formatNumber(value);
                  return [fmt, met?.label || name];
                }}
                itemStyle={{ color: "var(--chart-tooltip-foreground)", fontSize: 12 }}
              />
              <Legend
                formatter={(v: string) => {
                  const met = metrics.find((m) => m.key === v);
                  return (
                    <span style={{ color: "var(--chart-tooltip-muted)", fontSize: 11 }}>
                      {met?.label || v}
                    </span>
                  );
                }}
              />
              {active.map((m) => (
                <Area
                  key={m.key}
                  type="monotone"
                  dataKey={m.key}
                  yAxisId={dualAxis ? m.yAxis : "left"}
                  stroke={m.color}
                  strokeWidth={2}
                  fill={`url(#${chartId}-${m.key})`}
                  dot={
                    data.length <= 14
                      ? { r: 3, fill: m.color, stroke: "var(--chart-background)", strokeWidth: 2 }
                      : false
                  }
                  activeDot={{
                    r: 5,
                    fill: m.color,
                    stroke: "var(--chart-background)",
                    strokeWidth: 2,
                  }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

type DashboardMode =
  | "ecommerce"
  | "lead_gen_basic"
  | "lead_gen_advanced"
  | "saas"
  | "autowebinar_book_a_call"
  | "vsl_funnel";

type AnalyticsSettingsResponse = {
  dashboardMode?: DashboardMode;
  setterCalendarIds?: string[];
  setterCallSource?: string;
  ecommerceSource?: string;
  dataSources?: { meta: boolean; ghl: boolean; googleSheets: boolean };
  leadQualityAiEnabled?: boolean;
  aiCallAnalysisEnabled?: boolean;
  aiCallAnalysisModel?: string | null;
  quarterlyTargets?: import("@shared/schema").QuarterlyTargets | null;
  monthlyTargets?: import("@shared/schema").MonthlyTargets | null;
  kpiThresholds?: import("@shared/schema").KpiThresholds | null;
  customKpis?: import("@shared/schema").CustomKpis | null;
};

const buildDashboardModes = (
  t: (k: string) => string
): { value: DashboardMode; label: string }[] => [
  { value: "ecommerce", label: t("dashboard.modes.ecommerce") },
  { value: "lead_gen_basic", label: t("dashboard.modes.leadGenBasic") },
  { value: "lead_gen_advanced", label: t("dashboard.modes.leadGenAdvanced") },
  { value: "saas", label: t("dashboard.modes.saas") },
  { value: "autowebinar_book_a_call", label: t("dashboard.modes.autowebinarBookACall") },
  { value: "vsl_funnel", label: t("dashboard.modes.vslFunnel") },
];

const buildDatePresets = (t: (k: string) => string) => [
  { value: "today", label: t("dashboard.datePresets.today") },
  { value: "yesterday", label: t("dashboard.datePresets.yesterday") },
  { value: "last_7d", label: t("dashboard.datePresets.last7d") },
  { value: "last_14d", label: t("dashboard.datePresets.last14d") },
  { value: "last_30d", label: t("dashboard.datePresets.last30d") },
  { value: "last_90d", label: t("dashboard.datePresets.last90d") },
  { value: "this_month", label: t("dashboard.datePresets.thisMonth") },
  { value: "last_month", label: t("dashboard.datePresets.lastMonth") },
  { value: "custom", label: t("dashboard.datePresets.custom") },
];

const buildComparePresets = (t: (k: string) => string) => [
  { value: "none", label: t("dashboard.comparePresets.none") },
  { value: "previous_period", label: t("dashboard.comparePresets.previousPeriod") },
  { value: "last_7d", label: t("dashboard.comparePresets.last7d") },
  { value: "last_14d", label: t("dashboard.comparePresets.last14d") },
  { value: "last_30d", label: t("dashboard.comparePresets.last30d") },
  { value: "last_month", label: t("dashboard.comparePresets.lastMonth") },
  { value: "custom", label: t("dashboard.comparePresets.custom") },
];

function formatCurrency(value: number, currency = "PLN"): string {
  const locale = currency === "USD" ? "en-US" : currency === "EUR" ? "de-DE" : "pl-PL";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("pl-PL").format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

// Platform breakdown item for MetricCard
interface PlatformBreakdownItem {
  label: string;
  value: string;
}

// Platform color map for breakdown badges
const PLATFORM_STYLES: Record<string, { dot: string; text: string; bg: string }> = {
  meta: { dot: "bg-blue-400", text: "text-blue-300", bg: "bg-blue-500/10" },
  google: { dot: "bg-yellow-400", text: "text-yellow-300", bg: "bg-yellow-500/10" },
  tiktok: { dot: "bg-pink-400", text: "text-pink-300", bg: "bg-pink-500/10" },
  allegro: { dot: "bg-orange-400", text: "text-orange-300", bg: "bg-orange-500/10" },
  organic: { dot: "bg-emerald-400", text: "text-emerald-300", bg: "bg-emerald-500/10" },
};

const DEFAULT_PLATFORM_STYLE = {
  dot: "bg-muted",
  text: "text-muted-foreground",
  bg: "bg-muted/60",
};

function getPlatformStyle(label: string) {
  const lower = label.toLowerCase();
  if (lower.startsWith("google")) return PLATFORM_STYLES["google"];
  // VP-134: "YOUTUBE ORGANIC" itp. — pierwsze słowo to nazwa źródła, więc bez tego lookup
  // trafiałby w domyślny szary zamiast wspólnego zielonego stylu źródeł organicznych.
  if (lower.includes("organic")) return PLATFORM_STYLES["organic"];
  const key = lower.split(" ")[0];
  return PLATFORM_STYLES[key] || DEFAULT_PLATFORM_STYLE;
}

// KPI Metric Card styled to match brand
function MetricCard({
  label,
  value,
  icon: Icon,
  variant = "default",
  delta,
  invertDelta,
  totalLabel,
  breakdowns,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  variant?: "primary" | "default" | "highlight";
  delta?: number | null;
  invertDelta?: boolean; // true = lower is better (costs)
  totalLabel?: string;
  breakdowns?: PlatformBreakdownItem[];
}) {
  const variants = {
    primary: "bg-[#ef3e29]/10 border-[#ef3e29]/30",
    highlight: "bg-accent border-border",
    default: "bg-muted/60 border-border",
  };

  const hasDelta = delta != null && isFinite(delta);
  const isPositive = hasDelta && (invertDelta ? delta! < 0 : delta! > 0);
  const isNegative = hasDelta && (invertDelta ? delta! > 0 : delta! < 0);
  const hasBreakdowns = breakdowns && breakdowns.length > 0;

  return (
    <Card
      className={`${variants[variant]} border rounded-xl transition-all hover:border-[#ef3e29]/40`}
    >
      <CardContent className="p-4 md:p-6">
        <div className="flex items-center gap-2 mb-2 md:mb-3">
          <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-[#ef3e29]/20 flex items-center justify-center shrink-0">
            <Icon className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#ef3e29]" />
          </div>
          <span className="text-[10px] md:text-xs text-muted-foreground font-light uppercase tracking-wider leading-tight">
            {label}
          </span>
        </div>
        {totalLabel && hasBreakdowns && (
          <span className="text-[9px] text-muted-foreground font-light uppercase tracking-wider">
            {totalLabel}:
          </span>
        )}
        <div className="text-xl md:text-heading-2 font-extrabold text-foreground">{value}</div>
        {hasDelta && (
          <div
            className={`flex items-center gap-1 mt-1.5 text-xs font-light ${isPositive ? "text-emerald-400" : isNegative ? "text-red-400" : "text-muted-foreground"}`}
          >
            {isPositive ? (
              <TrendingUp className="w-3 h-3" />
            ) : isNegative ? (
              <TrendingDown className="w-3 h-3" />
            ) : null}
            <span>
              {delta! > 0 ? "+" : ""}
              {delta!.toFixed(1)}%
            </span>
          </div>
        )}
        {hasBreakdowns && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {breakdowns.map((b) => {
              const style = getPlatformStyle(b.label);
              return (
                <div
                  key={b.label}
                  className={`flex items-center gap-1.5 ${style.bg} rounded-md px-2 py-1`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                  <span className={`text-[11px] font-extrabold ${style.text}`}>{b.value}</span>
                  <span className="text-[10px] font-light text-muted-foreground">{b.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Convert delta + invertDelta flag to DashboardMetricCard trend props
function deltaToTrendProps(
  delta: number | null | undefined,
  invertDelta?: boolean
): { trendType: TrendType; trendChange: string } | Record<string, never> {
  if (delta == null || !isFinite(delta)) return {};
  const isGood = invertDelta ? delta < 0 : delta > 0;
  const isBad = invertDelta ? delta > 0 : delta < 0;
  const trendType: TrendType = isGood ? "up" : isBad ? "down" : "neutral";
  return { trendType, trendChange: `${delta > 0 ? "+" : ""}${delta.toFixed(1)}%` };
}

// Helper: calculate percentage delta between current and comparison values
function calcDelta(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

// ----- Metrics Chart -----

interface DailyInsight {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  outboundClicks: number;
  uniqueOutboundClicks: number;
  reach: number;
  leads: number;
  cpc: number;
  cpm: number;
  ctr: number;
  costPerLead: number;
  roas: number;
  bookedMeetings: number;
  costPerBookedMeeting: number;
}

const CHART_METRICS_ALL = [
  {
    key: "spend",
    labelKey: "dashboard.chartMetrics.spend",
    color: "#ef3e29",
    format: "currency",
    modes: ["all"],
  },
  {
    key: "leads",
    labelKey: "dashboard.chartMetrics.leads",
    color: "#10b981",
    format: "number",
    modes: ["lead_gen"],
  },
  {
    key: "impressions",
    labelKey: "dashboard.chartMetrics.impressions",
    color: "#6366f1",
    format: "number",
    modes: ["all"],
  },
  {
    key: "clicks",
    labelKey: "dashboard.chartMetrics.clicks",
    color: "#f59e0b",
    format: "number",
    modes: ["all"],
  },
  {
    key: "reach",
    labelKey: "dashboard.chartMetrics.reach",
    color: "#8b5cf6",
    format: "number",
    modes: ["all"],
  },
  {
    key: "cpc",
    labelKey: "dashboard.chartMetrics.cpc",
    color: "#06b6d4",
    format: "currency",
    modes: ["all"],
  },
  {
    key: "cpm",
    labelKey: "dashboard.chartMetrics.cpm",
    color: "#ec4899",
    format: "currency",
    modes: ["all"],
  },
  {
    key: "ctr",
    labelKey: "dashboard.chartMetrics.ctr",
    color: "#14b8a6",
    format: "percent",
    modes: ["all"],
  },
  {
    key: "costPerLead",
    labelKey: "dashboard.chartMetrics.costPerLead",
    color: "#f97316",
    format: "currency",
    modes: ["lead_gen"],
  },
  {
    key: "roas",
    labelKey: "dashboard.chartMetrics.roas",
    color: "#a855f7",
    format: "roas",
    modes: ["all"],
  },
  {
    key: "bookedMeetings",
    labelKey: "dashboard.chartMetrics.bookedMeetings",
    color: "#22d3ee",
    format: "number",
    modes: ["lead_gen"],
  },
  {
    key: "costPerBookedMeeting",
    labelKey: "dashboard.chartMetrics.costPerBookedMeeting",
    color: "#fb923c",
    format: "currency",
    modes: ["lead_gen"],
  },
] as const;

const CHART_METRICS = CHART_METRICS_ALL;

type MetricKey = (typeof CHART_METRICS)[number]["key"];

/** Merge setter dailyStats (booked meetings) into MetricsChart daily data */
function enrichDailyWithSetter(
  daily: DailyInsight[],
  setterStats?: SetterStatsData
): DailyInsight[] {
  const setterByDay = new Map<string, number>();
  if (setterStats?.dailyStats) {
    for (const d of setterStats.dailyStats) {
      setterByDay.set(d.date, d.booked);
    }
  }
  return daily.map((d) => {
    const booked = setterByDay.get(d.date) || 0;
    return {
      ...d,
      bookedMeetings: booked,
      costPerBookedMeeting: booked > 0 ? d.spend / booked : 0,
    };
  });
}

function MetricsChart({
  daily,
  isLoading,
  dashboardMode,
}: {
  daily: DailyInsight[];
  isLoading: boolean;
  dashboardMode?: string;
}) {
  const { t } = useTranslation();
  const isEcom = dashboardMode === "ecommerce";
  const isSaas = dashboardMode === "saas";
  const chartMetrics = CHART_METRICS_ALL.filter((m) => {
    const modes: readonly string[] = m.modes;
    return (
      modes.includes("all") || (isEcom ? modes.includes("ecommerce") : modes.includes("lead_gen"))
    );
  });
  const [selectedMetrics, setSelectedMetrics] = useState<MetricKey[]>(
    isEcom ? ["spend", "roas"] : isSaas ? ["spend", "leads", "costPerLead"] : ["spend", "leads"]
  );

  const toggleMetric = useCallback((key: MetricKey) => {
    setSelectedMetrics((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }, []);

  const activeMetrics = CHART_METRICS.filter((m) => selectedMetrics.includes(m.key));

  const formatTooltipValue = (value: number, metricKey: string) => {
    const metric = CHART_METRICS.find((m) => m.key === metricKey);
    if (!metric) return String(value);
    switch (metric.format) {
      case "currency":
        return formatCurrency(value);
      case "percent":
        return `${value.toFixed(2)}%`;
      case "roas":
        return `${value.toFixed(2)}x`;
      default:
        return formatNumber(value);
    }
  };

  const chartData = useMemo(() => daily.map((d) => ({ ...d, date: new Date(d.date) })), [daily]);

  if (isLoading) {
    return <ChartSkeleton />;
  }

  return (
    <Card className="bg-muted/50 border-border rounded-xl overflow-hidden">
      <div className="p-4 md:p-5 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-dense">
          <h2 className="text-foreground font-semibold text-base flex items-center gap-2">
            <LineChartIcon className="w-4 h-4 text-[#ef3e29]" />
            Wykres metryki
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {chartMetrics.map((metric) => {
              const isActive = selectedMetrics.includes(metric.key);
              return (
                <button
                  key={metric.key}
                  onClick={() => toggleMetric(metric.key)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border ${
                    isActive
                      ? "border-border text-foreground"
                      : "border-transparent text-muted-foreground hover:text-muted-foreground hover:border-border"
                  }`}
                  style={
                    isActive
                      ? { backgroundColor: `${metric.color}20`, borderColor: `${metric.color}50` }
                      : undefined
                  }
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: isActive ? metric.color : "var(--border)" }}
                  />
                  {t(metric.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <CardContent className="p-2 md:p-3">
        {daily.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
            {t("dashboard.chartEmpty")}
          </div>
        ) : (
          <VisxAreaChart
            data={chartData}
            aspectRatio="3.5 / 1"
            margin={{ top: 20, right: 16, bottom: 36, left: 52 }}
          >
            <VisxGrid horizontal />
            {activeMetrics.map((m) => (
              <VisxArea
                key={m.key}
                dataKey={m.key}
                fill={m.color}
                stroke={m.color}
                fillOpacity={0.25}
                strokeWidth={2}
                fadeEdges
              />
            ))}
            <VisxYAxis
              numTicks={5}
              formatValue={(v: number) =>
                v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v))
              }
            />
            <VisxXAxis numTicks={6} />
            <VisxChartTooltip
              rows={(point) =>
                activeMetrics.map((m) => ({
                  color: m.color,
                  label: t(m.labelKey),
                  value: formatTooltipValue((point[m.key] as number) ?? 0, m.key),
                }))
              }
            />
          </VisxAreaChart>
        )}
      </CardContent>
    </Card>
  );
}

// ----- Setter Tab Content (Phone Call Tracking) -----

/** Format seconds to mm:ss or hh:mm:ss */
function formatDuration(totalSec: number): string {
  if (!totalSec) return "0:00";
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const SETTER_CHART_METRICS = [
  {
    key: "attempts",
    labelKey: "dashboard.setterMetrics.attempts",
    color: "#6366f1",
    format: "number",
  },
  {
    key: "answered",
    labelKey: "dashboard.setterMetrics.answered",
    color: "#10b981",
    format: "number",
  },
  { key: "booked", labelKey: "dashboard.setterMetrics.booked", color: "#f59e0b", format: "number" },
  { key: "showed", labelKey: "dashboard.setterMetrics.showed", color: "#22d3ee", format: "number" },
  { key: "sales", labelKey: "dashboard.setterMetrics.sales", color: "#ef3e29", format: "number" },
  {
    key: "leads",
    labelKey: "dashboard.setterMetrics.leadsMeta",
    color: "#a855f7",
    format: "number",
  },
  {
    key: "spend",
    labelKey: "dashboard.setterMetrics.spendMeta",
    color: "#fb923c",
    format: "currency",
  },
  {
    key: "bookingRateAttempts",
    labelKey: "dashboard.setterMetrics.bookingRateAttempts",
    color: "#14b8a6",
    format: "percent",
  },
  {
    key: "contactRate",
    labelKey: "dashboard.setterMetrics.contactRate",
    color: "#ec4899",
    format: "percent",
  },
  {
    key: "costPerBooked",
    labelKey: "dashboard.setterMetrics.costPerBooked",
    color: "#f97316",
    format: "currency",
  },
] as const;

type SetterMetricKey = (typeof SETTER_CHART_METRICS)[number]["key"];

/** Toggleable chart for setter tab — merges setter daily + Meta daily data */
function SetterMetricsChart({
  setterDaily,
  metaDaily,
}: {
  setterDaily: SetterStatsData["dailyStats"];
  metaDaily: DailyInsight[];
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<SetterMetricKey[]>(["attempts", "booked", "leads"]);

  const toggle = useCallback((key: SetterMetricKey) => {
    setSelected((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }, []);

  // Merge setter + Meta daily into one dataset by date
  const merged = useMemo(() => {
    const metaByDate = new Map<string, DailyInsight>();
    for (const d of metaDaily) metaByDate.set(d.date, d);

    const allDates = new Set<string>();
    for (const d of setterDaily) allDates.add(d.date);
    for (const d of metaDaily) allDates.add(d.date);

    return Array.from(allDates)
      .sort()
      .map((date) => {
        const s = setterDaily.find((d) => d.date === date);
        const m = metaByDate.get(date);
        const attempts = s?.attempts || 0;
        const answered = s?.answered || 0;
        const booked = s?.booked || 0;
        return {
          date,
          attempts,
          answered,
          booked,
          showed: s?.showed || 0,
          sales: s?.sales || 0,
          leads: m?.leads || 0,
          spend: m?.spend || 0,
          bookingRateAttempts: attempts > 0 ? (booked / attempts) * 100 : 0,
          contactRate: attempts > 0 ? (answered / attempts) * 100 : 0,
          costPerBooked: booked > 0 && m ? m.spend / booked : 0,
        };
      });
  }, [setterDaily, metaDaily]);

  const active = SETTER_CHART_METRICS.filter((m) => selected.includes(m.key));
  const isLarge = (k: string) => ["leads", "spend"].includes(k);
  const hasLarge = active.some((m) => isLarge(m.key));
  const hasSmall = active.some((m) => !isLarge(m.key));
  const useDual = hasLarge && hasSmall;

  const fmtTooltip = (value: number, key: string) => {
    const metric = SETTER_CHART_METRICS.find((m) => m.key === key);
    if (!metric) return String(value);
    switch (metric.format) {
      case "currency":
        return formatCurrency(value);
      case "percent":
        return `${value.toFixed(1)}%`;
      default:
        return formatNumber(value);
    }
  };

  if (merged.length === 0) return null;

  return (
    <Card className="bg-muted/50 border-border rounded-xl">
      <div className="p-4 md:p-5 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-dense">
          <h2 className="text-foreground font-semibold text-base flex items-center gap-2">
            <LineChartIcon className="w-4 h-4 text-[#ef3e29]" />
            Wykres metryki settera
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {SETTER_CHART_METRICS.map((metric) => {
              const isActive = selected.includes(metric.key);
              return (
                <button
                  key={metric.key}
                  onClick={() => toggle(metric.key)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border ${
                    isActive
                      ? "border-border text-foreground"
                      : "border-transparent text-muted-foreground hover:text-muted-foreground hover:border-border"
                  }`}
                  style={
                    isActive
                      ? { backgroundColor: `${metric.color}20`, borderColor: `${metric.color}50` }
                      : undefined
                  }
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: isActive ? metric.color : "var(--border)" }}
                  />
                  {t(metric.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <CardContent className="p-4 md:p-5">
        <div className="h-72 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={merged}
              margin={{ top: 5, right: useDual ? 20 : 5, bottom: 5, left: 5 }}
            >
              <defs>
                {active.map((m) => (
                  <linearGradient key={m.key} id={`sg-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={m.color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={m.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
              <XAxis
                dataKey="date"
                tickFormatter={(d: string) => {
                  const p = d.split("-");
                  return `${p[2]}.${p[1]}`;
                }}
                stroke="var(--chart-foreground-muted)"
                tick={{ fill: "var(--chart-foreground-muted)", fontSize: 11 }}
                axisLine={{ stroke: "var(--chart-grid)" }}
              />
              <YAxis
                yAxisId="left"
                stroke="var(--chart-foreground-muted)"
                tick={{ fill: "var(--chart-foreground-muted)", fontSize: 11 }}
                axisLine={{ stroke: "var(--chart-grid)" }}
                tickFormatter={(v: number) =>
                  v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v * 10) / 10)
                }
                width={55}
              />
              {useDual && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="var(--chart-foreground-muted)"
                  tick={{ fill: "var(--chart-foreground-muted)", fontSize: 11 }}
                  axisLine={{ stroke: "var(--chart-grid)" }}
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v))
                  }
                  width={55}
                />
              )}
              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--chart-tooltip-background)",
                  border: "1px solid var(--border)",
                  borderRadius: "10px",
                  padding: "10px 14px",
                }}
                labelStyle={{ color: "var(--chart-tooltip-muted)", fontSize: 11, marginBottom: 4 }}
                labelFormatter={(d: string) =>
                  new Date(d).toLocaleDateString(t("common.locale", { defaultValue: "pl-PL" }), {
                    day: "numeric",
                    month: "short",
                  })
                }
                formatter={(value: number, name: string) => {
                  const metric = SETTER_CHART_METRICS.find((m) => m.key === name);
                  return [fmtTooltip(value, name), metric ? t(metric.labelKey) : name];
                }}
                itemStyle={{ color: "var(--chart-tooltip-foreground)", fontSize: 12 }}
              />
              <Legend
                formatter={(value: string) => {
                  const metric = SETTER_CHART_METRICS.find((m) => m.key === value);
                  return (
                    <span style={{ color: "var(--chart-tooltip-muted)", fontSize: 11 }}>
                      {metric ? t(metric.labelKey) : value}
                    </span>
                  );
                }}
              />
              {active.map((m) => (
                <Area
                  key={m.key}
                  type="monotone"
                  dataKey={m.key}
                  yAxisId={useDual && isLarge(m.key) ? "right" : "left"}
                  stroke={m.color}
                  strokeWidth={2}
                  fill={`url(#sg-${m.key})`}
                  dot={
                    merged.length <= 14
                      ? { r: 3, fill: m.color, stroke: "var(--chart-background)", strokeWidth: 2 }
                      : false
                  }
                  activeDot={{
                    r: 5,
                    fill: m.color,
                    stroke: "var(--chart-background)",
                    strokeWidth: 2,
                  }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

const ZEROED_SETTER_STATS: SetterStatsData = {
  outgoingCallAttempts: 0,
  answeredCalls: 0,
  missedCalls: 0,
  totalCallDurationSec: 0,
  avgCallDurationSec: 0,
  bookedMeetings: 0,
  showedMeetings: 0,
  noShowMeetings: 0,
  confirmedMeetings: 0,
  cancelledMeetings: 0,
  sales: 0,
  salesValue: 0,
  lost: 0,
  contactRate: 0,
  bookingRate: 0,
  bookingRateFromAttempts: 0,
  showRate: 0,
  closeRate: 0,
  dailyStats: [],
  recentCalls: [],
};

function SetterTabContent({
  stats,
  isLoading,
  isError,
  onRetry,
  onOpenSettings,
  metaTotals,
  metaDaily,
  forUserId,
  aiAnalysisEnabled,
  aiAnalysisModel,
  setterCallSource,
}: {
  stats: SetterStatsData | undefined;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onOpenSettings: () => void;
  metaTotals?: DashboardData["totals"];
  metaDaily?: DailyInsight[];
  forUserId?: string | null;
  aiAnalysisEnabled?: boolean;
  aiAnalysisModel?: string | null;
  setterCallSource?: string | null;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    // Skeleton odwzorowuje realny układ: KPI rozmów (5) + lejek spotkań (7) + wykres.
    return (
      <>
        <KpiGridSkeleton
          count={5}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-dense"
        />
        <KpiGridSkeleton
          count={7}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-dense"
        />
        <ChartSkeleton />
      </>
    );
  }

  // Distinct error state — a failed/timed-out fetch must NOT look like loading forever.
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
        <p className="text-muted-foreground text-sm max-w-md">{t("dashboard.setter.error")}</p>
        <div className="flex gap-2">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 text-sm rounded-md bg-[#ef3e29] text-white hover:bg-[#d63620] transition-colors"
            >
              {t("dashboard.setter.retry")}
            </button>
          )}
          <button
            onClick={onOpenSettings}
            className="px-4 py-2 text-sm rounded-md border border-border hover:bg-muted transition-colors"
          >
            {t("dashboard.setter.openSettings")}
          </button>
        </div>
      </div>
    );
  }

  // Empty state: render the full grid with zeroed metrics (matches the "no data yet" look)
  // plus a hint, instead of a perpetual loading message when data is simply absent.
  const isEmpty = !stats;
  stats = stats ?? ZEROED_SETTER_STATS;

  const costPerBookedMeeting =
    metaTotals && stats.bookedMeetings > 0 ? metaTotals.spend / stats.bookedMeetings : 0;

  return (
    <>
      {isEmpty && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <p className="text-sm text-muted-foreground">{t("dashboard.setter.empty")}</p>
          <button
            onClick={onOpenSettings}
            className="shrink-0 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted transition-colors"
          >
            {t("dashboard.setter.openSettings")}
          </button>
        </div>
      )}
      {/* ── AI call analysis toggle ── */}
      <SetterAiAnalysisToggle
        forUserId={forUserId}
        initialEnabled={aiAnalysisEnabled}
        initialModel={aiAnalysisModel}
        source={setterCallSource}
      />

      {/* ── Call KPIs ── */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
          <Phone className="w-4 h-4 text-[#ef3e29]" />
          {t("dashboard.setter.callActivity")}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-dense">
          <DashboardMetricCard
            label={t("dashboard.setterMetrics.attempts")}
            value={formatNumber(stats.outgoingCallAttempts)}
            icon={PhoneCall}
            variant="primary"
          />
          <DashboardMetricCard
            label="Odebrane"
            value={formatNumber(stats.answeredCalls)}
            icon={Phone}
            variant="highlight"
          />
          <DashboardMetricCard
            label="Nieodebrane"
            value={formatNumber(stats.missedCalls)}
            icon={PhoneOff}
          />
          <DashboardMetricCard
            label={t("dashboard.setter.avgCallDuration")}
            value={formatDuration(stats.avgCallDurationSec)}
            icon={Clock}
          />
          <DashboardMetricCard
            label={t("dashboard.setter.totalCallDuration")}
            value={formatDuration(stats.totalCallDurationSec)}
            icon={Clock}
          />
        </div>
      </div>

      {/* ── Appointment Funnel ── */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-[#ef3e29]" />
          {t("dashboard.setter.meetingsAndSales")}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-dense">
          <DashboardMetricCard
            label={t("dashboard.setterMetrics.booked")}
            value={formatNumber(stats.bookedMeetings)}
            icon={CalendarCheck}
            variant="primary"
          />
          <DashboardMetricCard
            label="Odbyte (showed)"
            value={formatNumber(stats.showedMeetings)}
            icon={UserCheck}
            variant="highlight"
          />
          <DashboardMetricCard
            label="No-show"
            value={formatNumber(stats.noShowMeetings)}
            icon={PhoneOff}
          />
          <DashboardMetricCard
            label={t("dashboard.setter.pending")}
            value={formatNumber(stats.confirmedMeetings)}
            icon={Clock}
          />
          <DashboardMetricCard
            label={t("dashboard.setterMetrics.sales")}
            value={formatNumber(stats.sales)}
            icon={TrendingUp}
            variant="primary"
          />
          <DashboardMetricCard
            label={t("dashboard.setter.salesValue")}
            value={stats.salesValue > 0 ? formatCurrency(stats.salesValue) : "—"}
            icon={DollarSign}
            variant="highlight"
          />
          <DashboardMetricCard
            label="Przegrane"
            value={formatNumber(stats.lost)}
            icon={TrendingDown}
          />
        </div>
      </div>

      {/* ── Rates ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-dense">
        <DashboardMetricCard
          label={t("dashboard.setter.bookingRateFromAttempts")}
          value={formatPercent(stats.bookingRateFromAttempts)}
          icon={Percent}
          variant="primary"
        />
        <DashboardMetricCard
          label={t("dashboard.setter.bookingRateFromAnswered")}
          value={formatPercent(stats.bookingRate)}
          icon={Percent}
          variant="highlight"
        />
        <DashboardMetricCard
          label="Contact rate"
          value={formatPercent(stats.contactRate)}
          icon={Percent}
        />
        <DashboardMetricCard
          label="Show rate"
          value={
            stats.showedMeetings + stats.noShowMeetings > 0
              ? formatPercent(stats.showRate)
              : stats.bookedMeetings > 0
                ? "—"
                : formatPercent(0)
          }
          icon={Percent}
        />
        <DashboardMetricCard
          label="Close rate"
          value={formatPercent(stats.closeRate)}
          icon={Percent}
          variant="highlight"
        />
        <DashboardMetricCard
          label="Koszt / spotkanie"
          value={costPerBookedMeeting > 0 ? formatCurrency(costPerBookedMeeting) : "—"}
          icon={DollarSign}
        />
      </div>

      {/* ── Setter Metrics Chart (toggleable like main chart) ── */}
      <SetterMetricsChart setterDaily={stats.dailyStats} metaDaily={metaDaily || []} />

      {/* ── Recent Calls Table ── */}
      {stats.recentCalls.length > 0 && (
        <Card className="bg-muted/50 border-border rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="text-foreground font-semibold text-sm flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-[#ef3e29]" />
              {t("dashboard.setter.recentCalls", { count: stats.recentCalls.length })}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-muted-foreground font-medium px-4 py-3 text-xs uppercase tracking-wide">
                    {t("dashboard.setter.tableContact")}
                  </th>
                  <th className="text-left text-muted-foreground font-medium px-4 py-3 text-xs uppercase tracking-wide">
                    {t("dashboard.setter.tablePhone")}
                  </th>
                  <th className="text-left text-muted-foreground font-medium px-4 py-3 text-xs uppercase tracking-wide">
                    {t("dashboard.setter.tableStatus")}
                  </th>
                  <th className="text-right text-muted-foreground font-medium px-4 py-3 text-xs uppercase tracking-wide">
                    {t("dashboard.setter.tableDuration")}
                  </th>
                  <th className="text-left text-muted-foreground font-medium px-4 py-3 text-xs uppercase tracking-wide">
                    {t("dashboard.setter.tableDate")}
                  </th>
                  <th className="text-right text-muted-foreground font-medium px-4 py-3 text-xs uppercase tracking-wide">
                    AI
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.recentCalls.map((call) => {
                  const statusLabels: Record<string, string> = {
                    completed: "Odebrane",
                    answered: "Odebrane",
                    connected: "Odebrane",
                    "no-answer": "Brak odpowiedzi",
                    busy: t("dashboard.setter.callBusy"),
                    voicemail: t("dashboard.setter.callVoicemail"),
                    failed: "Nieudane",
                    cancelled: "Anulowane",
                  };
                  const statusColors: Record<string, string> = {
                    completed: "text-emerald-400",
                    answered: "text-emerald-400",
                    connected: "text-emerald-400",
                    "no-answer": "text-amber-400",
                    busy: "text-amber-400",
                    voicemail: "text-blue-400",
                    failed: "text-red-400",
                    cancelled: "text-red-400",
                  };
                  const statusLabel = statusLabels[call.status] || call.status || "—";
                  const statusColor = statusColors[call.status] || "text-muted-foreground";
                  return (
                    <tr key={call.id} className="border-b border-border hover:bg-muted/40">
                      <td className="px-4 py-3 text-foreground">{call.contactName || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                        {call.contactPhone || "—"}
                      </td>
                      <td className={`px-4 py-3 font-medium ${statusColor}`}>{statusLabel}</td>
                      <td className="px-4 py-3 text-muted-foreground text-right font-mono">
                        {call.durationSec > 0 ? formatDuration(call.durationSec) : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {call.dateAdded
                          ? new Date(call.dateAdded).toLocaleDateString("pl-PL", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <CallAnalysisButton
                          callId={call.id}
                          forUserId={forUserId}
                          status={call.analysisStatus}
                          score={call.analysisScore}
                          source={setterCallSource}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Info */}
      <p className="text-[10px] text-muted-foreground pl-1">{t("dashboard.setter.infoText")}</p>
    </>
  );
}

// ----- Lead Detail Dialog (first-touch attribution popup) -----

function getStatusLabels(
  t: (k: string) => string
): Record<string, { label: string; color: string }> {
  return {
    sprzedaz: {
      label: t("dashboard.leadStatus.sprzedaz"),
      color: "text-emerald-400 bg-emerald-400/10",
    },
    przegrana: { label: t("dashboard.leadStatus.przegrana"), color: "text-red-400 bg-red-400/10" },
    "rozmowa-kwalifikowana": {
      label: t("dashboard.leadStatus.rozmowakwalifikowana"),
      color: "text-blue-400 bg-blue-400/10",
    },
    "rozmowa-niekwalifikowana": {
      label: t("dashboard.leadStatus.rozmowaniekwalifikowana"),
      color: "text-orange-400 bg-orange-400/10",
    },
    "no-show": { label: t("dashboard.leadStatus.noshow"), color: "text-red-400/70 bg-red-400/10" },
    kwalifikowany: {
      label: t("dashboard.leadStatus.kwalifikowany"),
      color: "text-cyan-400 bg-cyan-400/10",
    },
    niekwalifikowany: {
      label: t("dashboard.leadStatus.niekwalifikowany"),
      color: "text-yellow-400 bg-yellow-400/10",
    },
    lead: { label: t("dashboard.leadStatus.lead"), color: "text-muted-foreground bg-muted/50" },
  };
}

function getApptStatusLabels(
  t: (k: string) => string
): Record<string, { label: string; color: string }> {
  return {
    showed: { label: t("dashboard.apptStatus.showed"), color: "text-emerald-400" },
    noshow: { label: t("dashboard.apptStatus.noshow"), color: "text-red-400" },
    confirmed: { label: t("dashboard.apptStatus.confirmed"), color: "text-blue-400" },
    cancelled: { label: t("dashboard.apptStatus.cancelled"), color: "text-muted-foreground" },
  };
}

function LeadCard({
  lead,
  onClickJourney,
}: {
  lead: LeadDetail;
  onClickJourney?: (lead: LeadDetail) => void;
}) {
  const { t } = useTranslation();
  const STATUS_LABELS = getStatusLabels(t);
  const APPT_STATUS_LABELS = getApptStatusLabels(t);
  const statusInfo = STATUS_LABELS[lead.status] || STATUS_LABELS.lead;
  return (
    <div
      className={`bg-muted/50 border border-border rounded-lg p-3 hover:bg-accent transition-colors ${onClickJourney ? "cursor-pointer" : ""}`}
      onClick={() => onClickJourney?.(lead)}
    >
      <div className="flex items-start justify-between gap-dense">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-foreground truncate">
              {lead.name || t("dashboard.leadCard.noName")}
            </span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusInfo.color}`}
            >
              {statusInfo.label}
            </span>
            {lead.webinarAttended && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium text-purple-400 bg-purple-400/10 flex items-center gap-1">
                <Monitor className="w-2.5 h-2.5" />
                LIVE
              </span>
            )}
          </div>
          <div className="flex items-center gap-dense text-xs text-muted-foreground">
            {lead.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {lead.phone}
              </span>
            )}
            {lead.email && <span className="truncate">{lead.email}</span>}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(lead.createdAt).toLocaleDateString("pl")}
            </span>
          </div>
        </div>
        {onClickJourney && (
          <div
            className="flex-shrink-0 p-1 rounded hover:bg-accent transition-colors"
            title={t("dashboard.leadCard.customerJourney")}
          >
            <MousePointerClick className="w-3.5 h-3.5 text-[#ef3e29]/60" />
          </div>
        )}
      </div>

      {lead.webinarAttended && (
        <div className="mt-2 pl-3 border-l-2 border-purple-500/20 space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <Monitor className="w-3 h-3 text-purple-400/50" />
            <span className="text-purple-400 font-medium">
              {t("dashboard.leadCard.autowebinarLive")}
            </span>
            {lead.webinarAttendedAt && (
              <span className="text-muted-foreground">
                {new Date(lead.webinarAttendedAt).toLocaleDateString("pl")}
              </span>
            )}
          </div>
        </div>
      )}

      {lead.appointments.length > 0 && (
        <div className="mt-2 pl-3 border-l-2 border-border space-y-1">
          {lead.appointments.map((appt) => {
            const apptInfo = APPT_STATUS_LABELS[appt.status] || {
              label: appt.status,
              color: "text-muted-foreground",
            };
            return (
              <div key={appt.id} className="flex items-center gap-2 text-xs">
                <CalendarCheck className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {appt.title || t("dashboard.leadCard.callTitle")}
                </span>
                <span className={apptInfo.color}>{apptInfo.label}</span>
                <span className="text-muted-foreground">
                  {new Date(appt.startTime).toLocaleDateString("pl")}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {lead.opportunities.length > 0 && (
        <div className="mt-2 pl-3 border-l-2 border-emerald-500/20 space-y-1">
          {lead.opportunities.map((opp) => (
            <div key={opp.id} className="flex items-center gap-2 text-xs">
              <ShoppingCart className="w-3 h-3 text-emerald-400/50" />
              <span className="text-muted-foreground">{opp.name}</span>
              <span
                className={
                  opp.status === "won"
                    ? "text-emerald-400 font-medium"
                    : opp.status === "lost"
                      ? "text-red-400"
                      : "text-blue-400"
                }
              >
                {opp.status === "won"
                  ? t("dashboard.leadCard.oppWon")
                  : opp.status === "lost"
                    ? t("dashboard.leadCard.oppLost")
                    : t("dashboard.leadCard.oppOpen")}
              </span>
              {opp.monetaryValue > 0 && (
                <span className="text-emerald-400">{formatCurrency(opp.monetaryValue)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Google Sheets extra fields */}
      {(lead.budget || lead.goal || lead.interest || lead.buyTimeline) && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {lead.budget && <span>💰 {lead.budget}</span>}
          {lead.buyTimeline && <span>📅 {lead.buyTimeline}</span>}
          {lead.goal && <span>🎯 {lead.goal}</span>}
          {lead.interest && <span>❤️ {lead.interest}</span>}
        </div>
      )}

      {lead.notes && lead.notes.length > 0 && (
        <div className="mt-2 pl-3 border-l-2 border-amber-500/20 space-y-1">
          {lead.notes.map((note, i) => (
            <p key={i} className="text-xs text-muted-foreground italic">
              📝 {note}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** Metoda atrybucji z dropdownu — ten sam union, co stan strony i `dashboardAttributionModelSchema`. */
type AttributionModel = "data" | "first_click" | "last_click" | "neuroflow";

function LeadDetailDialog({
  open,
  onClose,
  type,
  id,
  name,
  view,
  datePreset,
  customRange,
  utmSource,
  utmMedium,
  utmCampaign,
  forClientUserId,
  parentAdSetId,
  parentCampaignId,
  attributionModel,
}: {
  open: boolean;
  onClose: () => void;
  type: "campaign" | "adset" | "ad" | "organic";
  id: string;
  name: string;
  view?: "leads" | "nowe-leady" | "rozmowy" | "sprzedaze" | "live" | "zamowienia";
  datePreset: string;
  customRange?: { since: string; until: string };
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  forClientUserId?: string;
  parentAdSetId?: string;
  parentCampaignId?: string;
  /** VP-134: ten sam model co tabela — inaczej modal liczyłby leady inną regułą niż komórka. */
  attributionModel?: AttributionModel;
}) {
  const { t } = useTranslation();
  const requestBody = useMemo(() => {
    const body: any = { datePreset };
    if (customRange) body.customRange = customRange;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    if (view) body.view = view;
    if (attributionModel) body.attributionModel = attributionModel;
    if (type === "organic") {
      if (utmSource) body.utmSource = utmSource;
      if (utmMedium) body.utmMedium = utmMedium;
      if (utmCampaign) body.utmCampaign = utmCampaign;
    } else if (type === "campaign") body.campaignId = id;
    else if (type === "adset") {
      body.adSetId = id;
      // Pass parent campaign so the backend can cascade when contacts lack
      // per-adset UTM tracking (e.g. Meta instant-form leads only store campaignId).
      if (parentCampaignId) body.campaignId = parentCampaignId;
    } else if (type === "ad") {
      body.adId = id;
      // Pass parent identifiers so the backend can cascade when contacts
      // lack per-ad UTM tracking (e.g. Meta instant-form leads).
      if (parentAdSetId) body.adSetId = parentAdSetId;
      if (parentCampaignId) body.campaignId = parentCampaignId;
    }
    return body;
  }, [
    type,
    id,
    datePreset,
    customRange,
    utmSource,
    utmMedium,
    utmCampaign,
    view,
    forClientUserId,
    parentAdSetId,
    parentCampaignId,
    attributionModel,
  ]);

  const { data, isLoading, isError, refetch } = useQuery<{ leads: LeadDetail[]; source?: string }>({
    queryKey: ["/api/dashboard/lead-details", requestBody],
    queryFn: () => apiRequest("POST", "/api/dashboard/lead-details", requestBody),
    enabled: open,
    staleTime: 1000 * 60 * 3,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allLeads = data?.leads || [];
  const dataSource = data?.source; // 'google_sheets' when data comes from sheets
  // Filter based on view type
  const leads = useMemo(() => {
    if (view === "nowe-leady") return allLeads.filter((l) => l.isNew);
    if (view === "rozmowy") return allLeads.filter((l) => l.appointments.length > 0);
    if (view === "sprzedaze") return allLeads.filter((l) => l.status === "sprzedaz");
    if (view === "live") return allLeads.filter((l) => l.webinarAttended);
    return allLeads; // 'leads' or undefined = all
  }, [allLeads, view]);

  const [journeyLead, setJourneyLead] = useState<LeadDetail | null>(null);

  // No split needed — all contacts from fetchContacts are created in range (isNew=true)

  const TYPE_LABEL_MAP: Record<string, string> = {
    campaign: t("dashboard.leadDialog.typeCampaign"),
    adset: t("dashboard.leadDialog.typeAdset"),
    organic: t("dashboard.leadDialog.typeOrganic"),
    ad: t("dashboard.leadDialog.typeAd"),
  };
  const typeLabel = TYPE_LABEL_MAP[type] ?? t("dashboard.leadDialog.typeAd");
  const VIEW_TITLES: Record<string, string> = {
    "nowe-leady": t("dashboard.leadDialog.viewNewLeads"),
    rozmowy: t("dashboard.leadDialog.viewCalls"),
    sprzedaze: t("dashboard.leadDialog.viewSales"),
    live: t("dashboard.leadDialog.viewLive"),
  };
  const viewTitle = VIEW_TITLES[view || ""] || t("dashboard.leadDialog.viewLeads");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-card border-border text-foreground overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2 text-base">
            {view === "live" ? (
              <Monitor className="w-4 h-4 text-purple-400" />
            ) : (
              <ShoppingCart className="w-4 h-4 text-[#ef3e29]" />
            )}
            {viewTitle}
            {dataSource === "google_sheets" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-normal">
                Google Sheets
              </span>
            )}
          </DialogTitle>
          <div className="text-xs text-muted-foreground truncate">
            {typeLabel}: {name}
          </div>
        </DialogHeader>

        {/* Lead list */}
        <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 min-h-0">
          {isLoading ? (
            <ListSkeleton rows={6} className="space-y-1.5" />
          ) : isError ? (
            // VP-134: padnięty/przeterminowany request rysował się identycznie jak pustka
            // („Brak leadów"), więc awaria wyglądała jak brak danych. Teraz to osobny stan.
            <div className="text-center py-12 space-y-3">
              <p className="text-muted-foreground text-sm">
                {t("dashboard.leadDialog.loadFailed")}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                {t("dashboard.leadDialog.retry")}
              </Button>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">{t("dashboard.leadDialog.noLeads")}</p>
              <p className="text-muted-foreground text-xs mt-1">
                {t("dashboard.leadDialog.noLeadsHint")}
              </p>
            </div>
          ) : (
            leads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} onClickJourney={setJourneyLead} />
            ))
          )}
        </div>

        {/* Lead Journey Dialog */}
        {journeyLead && (
          <LeadJourneyDialog
            open={!!journeyLead}
            onClose={() => setJourneyLead(null)}
            email={journeyLead.email}
            phone={journeyLead.phone}
            name={journeyLead.name}
            ghlContactId={journeyLead.id}
            forClientUserId={forClientUserId}
            adId={journeyLead.metaAdId || null}
            adName={journeyLead.adName}
            adAccountId={journeyLead.adAccountId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// VP-214 — drill-down zamówień Shopify pod sprzedażą w tabeli kampanii (tryb ecom).
// Osobny od LeadDetailDialog (leady GHL vs zamówienia Shopify: numer/kwota/produkt).
type OrderDetailRow = {
  id: string;
  orderId: string | null;
  orderName: string | null;
  type: "purchase" | "upsell" | "refund";
  revenue: number;
  currency: string | null;
  convertedAt: string | null;
  products: { title: string; qty: number }[];
};

function OrderDetailDialog({
  open,
  onClose,
  type,
  id,
  name,
  datePreset,
  customRange,
  forClientUserId,
  parentAdSetId,
  parentCampaignId,
}: {
  open: boolean;
  onClose: () => void;
  type: "campaign" | "adset" | "ad";
  id: string;
  name: string;
  datePreset: string;
  customRange?: { since: string; until: string };
  forClientUserId?: string;
  parentAdSetId?: string;
  parentCampaignId?: string;
}) {
  const { t } = useTranslation();
  const requestBody = useMemo(() => {
    const body: Record<string, unknown> = { datePreset };
    if (customRange) body.customRange = customRange;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    if (type === "campaign") body.campaignId = id;
    else if (type === "adset") {
      body.adSetId = id;
      if (parentCampaignId) body.campaignId = parentCampaignId;
    } else if (type === "ad") {
      body.adId = id;
      if (parentAdSetId) body.adSetId = parentAdSetId;
      if (parentCampaignId) body.campaignId = parentCampaignId;
    }
    return body;
  }, [type, id, datePreset, customRange, forClientUserId, parentAdSetId, parentCampaignId]);

  const { data, isLoading } = useQuery<{ orders: OrderDetailRow[] }>({
    queryKey: ["/api/dashboard/order-details", requestBody],
    queryFn: () => apiRequest("POST", "/api/dashboard/order-details", requestBody),
    enabled: open,
    staleTime: 1000 * 60 * 3,
  });
  const orders = data?.orders || [];

  const TYPE_LABEL_MAP: Record<string, string> = {
    campaign: t("dashboard.leadDialog.typeCampaign"),
    adset: t("dashboard.leadDialog.typeAdset"),
    ad: t("dashboard.leadDialog.typeAd"),
  };
  const typeLabel = TYPE_LABEL_MAP[type] ?? t("dashboard.leadDialog.typeAd");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] bg-card border-border text-foreground overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2 text-base">
            <ShoppingCart className="w-4 h-4 text-[#ef3e29]" />
            {t("dashboard.orderDialog.title")}
          </DialogTitle>
          <div className="text-xs text-muted-foreground truncate">
            {typeLabel}: {name}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 min-h-0">
          {isLoading ? (
            <ListSkeleton rows={6} withAvatar={false} className="space-y-1.5" />
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">{t("dashboard.orderDialog.empty")}</p>
              <p className="text-muted-foreground text-xs mt-1">
                {t("dashboard.orderDialog.emptyHint")}
              </p>
            </div>
          ) : (
            orders.map((order) => {
              const isRefund = order.type === "refund";
              const products = order.products.filter((p) => p.title);
              return (
                <div
                  key={order.id}
                  className="rounded-lg border border-border bg-muted/30 px-3 py-2 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground font-medium truncate">
                        {order.orderName || order.orderId || "—"}
                      </span>
                      {isRefund && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                          {t("dashboard.orderDialog.refund")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {products.length > 0
                        ? products
                            .map((p) => `${p.title}${p.qty > 1 ? ` ×${p.qty}` : ""}`)
                            .join(", ")
                        : t("dashboard.orderDialog.noProducts")}
                    </div>
                  </div>
                  <div
                    className={`text-sm font-medium whitespace-nowrap ${isRefund ? "text-red-400" : "text-foreground"}`}
                  >
                    {formatCurrency(order.revenue, order.currency ?? "PLN")}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ----- Channels Breakdown Table -----

interface ChannelRow {
  source: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversionsValue: number;
  ctr: number;
  cpc: number;
  cpm: number;
  roas: number;
  cpa: number;
}

// ----- UTM Sources Breakdown Section -----
const UTM_SOURCE_LABELS: Record<string, { label: string; color: string; icon: JSX.Element }> = {
  facebook: {
    label: "Facebook Ads",
    color: "#1877F2",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#1877F2">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  instagram: {
    label: "Instagram",
    color: "#E4405F",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#E4405F">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  youtube: {
    label: "YouTube",
    color: "#FF0000",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF0000">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  google: {
    label: "Google Ads",
    color: "#4285F4",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
        <path
          d="M3.272 13.446l6.104-10.594c.95-1.647 3.059-2.21 4.706-1.26l.002.001c1.647.95 2.21 3.059 1.26 4.706L9.24 16.893c-.95 1.647-3.059 2.21-4.706 1.26h-.001c-1.647-.95-2.21-3.059-1.26-4.706z"
          fill="#FBBC04"
        />
        <circle cx="6" cy="18.5" r="3.5" fill="#34A853" />
      </svg>
    ),
  },
  tiktok: {
    label: "TikTok",
    color: "#000000",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.16z" />
      </svg>
    ),
  },
  linkedin: {
    label: "LinkedIn",
    color: "#0A66C2",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0A66C2">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  organic: {
    label: "Organic",
    color: "#22c55e",
    icon: <Layers className="w-4 h-4 text-emerald-500" />,
  },
  direct: {
    label: "Direct / Organic",
    color: "#8b5cf6",
    icon: <Globe className="w-4 h-4 text-violet-400" />,
  },
};

function getSourceInfo(source: string): { label: string; color: string; icon: JSX.Element } {
  const lower = source.toLowerCase();
  // Match known sources even if prefix/suffix differs
  for (const [key, val] of Object.entries(UTM_SOURCE_LABELS)) {
    if (lower.includes(key)) return val;
  }
  return {
    label: source || "Nieznane",
    color: "#6b7280",
    icon: <Layers className="w-4 h-4 text-muted-foreground" />,
  };
}

function UtmSourcesSection({
  data,
  currency = "PLN",
}: {
  data: UtmSourceBreakdownRow[];
  currency?: string;
}) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;

  const totalLeads = data.reduce((s, r) => s + r.leads, 0);
  const fmtCurrency = (v: number) => formatCurrency(v, currency);

  return (
    <Card className="bg-muted/50 border-border rounded-xl overflow-hidden">
      <div className="p-4 md:p-5 border-b border-border">
        <h2 className="text-foreground font-semibold text-base flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#ef3e29]" />
          {t("dashboard.utmSources.title")}
        </h2>
        <p className="text-muted-foreground text-xs mt-1">{t("dashboard.utmSources.subtitle")}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                {t("dashboard.utmSources.colSource")}
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Leady
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Nowe
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Rozmowy
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Booking %
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Showed
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                No-show
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Show %
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                {t("dashboard.utmSources.colSales")}
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Close %
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                {t("dashboard.utmSources.colRevenue")}
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                {t("dashboard.utmSources.colShare")}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const info = getSourceInfo(row.utmSource);
              const share = totalLeads > 0 ? (row.leads / totalLeads) * 100 : 0;
              return (
                <tr key={row.utmSource} className="border-b border-border hover:bg-muted/50">
                  <td className="px-4 py-3 text-sm text-foreground">
                    <div className="flex items-center gap-2">
                      {info.icon}
                      <span>{info.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-medium">
                    {row.leads}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right">{row.newLeads}</td>
                  <td className="px-4 py-3 text-sm text-foreground text-right">
                    {row.bookedCalls || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {row.bookingRate > 0 ? (
                      <span
                        className={
                          row.bookingRate >= 30
                            ? "text-emerald-400"
                            : row.bookingRate >= 15
                              ? "text-amber-400"
                              : "text-red-400"
                        }
                      >
                        {row.bookingRate}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right">
                    {row.showed || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right">
                    {row.noShow || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {row.showRate > 0 ? (
                      <span
                        className={
                          row.showRate >= 70
                            ? "text-emerald-400"
                            : row.showRate >= 50
                              ? "text-amber-400"
                              : "text-red-400"
                        }
                      >
                        {row.showRate}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-medium">
                    {row.won || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {row.closeRate > 0 ? (
                      <span
                        className={
                          row.closeRate >= 30
                            ? "text-emerald-400"
                            : row.closeRate >= 15
                              ? "text-amber-400"
                              : "text-red-400"
                        }
                      >
                        {row.closeRate}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium">
                    {row.revenue > 0 ? (
                      <span className="text-emerald-400">{fmtCurrency(row.revenue)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-accent rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${Math.min(share, 100)}%`, backgroundColor: info.color }}
                        />
                      </div>
                      <span className="text-muted-foreground text-xs w-10 text-right">
                        {share.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* Totals row */}
            {data.length > 1 &&
              (() => {
                const tot = data.reduce(
                  (acc, r) => ({
                    leads: acc.leads + r.leads,
                    newLeads: acc.newLeads + r.newLeads,
                    bookedCalls: acc.bookedCalls + r.bookedCalls,
                    showed: acc.showed + r.showed,
                    noShow: acc.noShow + r.noShow,
                    won: acc.won + r.won,
                    revenue: acc.revenue + r.revenue,
                  }),
                  {
                    leads: 0,
                    newLeads: 0,
                    bookedCalls: 0,
                    showed: 0,
                    noShow: 0,
                    won: 0,
                    revenue: 0,
                  }
                );
                const showDenom = tot.showed + tot.noShow;
                return (
                  <tr className="bg-muted/60 font-medium">
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {t("dashboard.utmSources.totalRow", { count: data.length })}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground text-right font-bold">
                      {tot.leads}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground text-right">{tot.newLeads}</td>
                    <td className="px-4 py-3 text-sm text-foreground text-right">
                      {tot.bookedCalls || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {tot.leads > 0 && tot.bookedCalls > 0 ? (
                        <span className="text-muted-foreground">
                          {Math.round((tot.bookedCalls / tot.leads) * 100)}%
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground text-right">
                      {tot.showed || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground text-right">
                      {tot.noShow || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {showDenom > 0 ? (
                        <span className="text-muted-foreground">
                          {Math.round((tot.showed / showDenom) * 100)}%
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground text-right font-bold">
                      {tot.won || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {tot.showed > 0 && tot.won > 0 ? (
                        <span className="text-muted-foreground">
                          {Math.round((tot.won / tot.showed) * 100)}%
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-bold">
                      {tot.revenue > 0 ? (
                        <span className="text-emerald-400">{fmtCurrency(tot.revenue)}</span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground text-right">100%</td>
                  </tr>
                );
              })()}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ChannelsTable({
  datePreset,
  requestBody,
  currency = "PLN",
}: {
  datePreset: string;
  requestBody: any;
  currency?: string;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<{ channels: ChannelRow[]; totals: ChannelRow }>({
    queryKey: [
      "/api/dashboard/channels",
      datePreset,
      requestBody?.adAccountId,
      requestBody?.customRange,
      // VP-135: model atrybucji jest w kluczu, inaczej ta tabela zostałaby na starych liczbach
      // po przełączeniu dropdownu, podczas gdy tabela kampanii obok już się przeliczyła.
      requestBody?.attributionModel,
    ],
    queryFn: () =>
      apiRequest("POST", "/api/dashboard/channels", {
        datePreset,
        adAccountId: requestBody?.adAccountId,
        customRange: requestBody?.customRange,
        attributionModel: requestBody?.attributionModel,
      }),
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <Card className="bg-muted/50 border-border rounded-xl overflow-hidden">
        <div className="p-4 md:p-5 border-b border-border">
          <Skeleton className="h-5 w-36" />
        </div>
        <div className="p-4 md:p-5">
          <TableSkeleton rows={3} columns={6} />
        </div>
      </Card>
    );
  }

  if (!data || data.channels.length === 0) return null;

  const sourceLogos: Record<string, JSX.Element> = {
    Meta: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#1877F2">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    "Google Ads": (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
        <path
          d="M3.272 13.446l6.104-10.594c.95-1.647 3.059-2.21 4.706-1.26l.002.001c1.647.95 2.21 3.059 1.26 4.706L9.24 16.893c-.95 1.647-3.059 2.21-4.706 1.26h-.001c-1.647-.95-2.21-3.059-1.26-4.706z"
          fill="#FBBC04"
        />
        <path
          d="M14.656 13.446l6.104-10.594c.95-1.647-.387-2.21-2.034-1.26l-.002.001-6.104 10.594c-.95 1.647.387 2.21 2.034 1.26h.001z"
          fill="#4285F4"
        />
        <circle cx="6" cy="18.5" r="3.5" fill="#34A853" />
      </svg>
    ),
  };

  const fmtCurrency = (v: number) => formatCurrency(v, currency);
  const fmtNum = (v: number) => new Intl.NumberFormat("pl-PL").format(Math.round(v));
  const fmtPct = (v: number) => `${v.toFixed(2)}%`;
  const fmtRoas = (v: number) => (v > 0 ? `${v.toFixed(2)}x` : "-");

  const renderRow = (row: ChannelRow, isTotal = false) => (
    <tr
      key={row.source}
      className={`border-b border-border ${isTotal ? "bg-muted/60 font-medium" : "hover:bg-muted/50"}`}
    >
      <td className="px-4 py-3 text-sm text-foreground">
        <div className="flex items-center gap-2">
          {!isTotal && sourceLogos[row.source]}
          <span className={isTotal ? "text-muted-foreground" : ""}>{row.source}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.spend > 0 ? fmtCurrency(row.spend) : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.conversionsValue > 0 ? fmtCurrency(row.conversionsValue) : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.roas > 0 ? (
          <span
            className={
              row.roas >= 3
                ? "text-emerald-400 font-semibold"
                : row.roas >= 1
                  ? "text-amber-400"
                  : "text-red-400"
            }
          >
            {fmtRoas(row.roas)}
          </span>
        ) : (
          "-"
        )}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.clicks > 0 ? fmtNum(row.clicks) : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.impressions > 0 ? fmtNum(row.impressions) : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.ctr > 0 ? fmtPct(row.ctr) : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.cpm > 0 ? fmtCurrency(row.cpm) : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.cpc > 0 ? fmtCurrency(row.cpc) : "-"}
      </td>
      <td className="px-4 py-3 text-sm text-foreground text-right">
        {row.cpa > 0 ? fmtCurrency(row.cpa) : "-"}
      </td>
    </tr>
  );

  return (
    <Card className="bg-muted/50 border-border rounded-xl overflow-hidden">
      <div className="p-4 md:p-5 border-b border-border">
        <h2 className="text-foreground font-semibold text-base flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#ef3e29]" />
          {t("dashboard.channels.title")}
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                {t("dashboard.channels.colSource")}
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                Wydatek
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                CV
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                ROAS
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                {t("dashboard.channels.colClicks")}
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                {t("dashboard.channels.colImpressions")}
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                CTR
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                CPM
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                CPC
              </th>
              <th className="text-right text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                CPA
              </th>
            </tr>
          </thead>
          <tbody>
            {data.channels.map((ch) => renderRow(ch))}
            {data.channels.length > 1 && renderRow(data.totals, true)}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ----- Expandable Campaign Table -----

type LeadGenColumnKey =
  | "leads"
  | "spend"
  | "cpl"
  | "webinar_live"
  | "webinar_cost_per_live"
  | "webinar_show_rate"
  | "rozmowy"
  | "costPerRozmowa"
  | "showRate"
  | "sales"
  | "cpc"
  | "cpm"
  | "roas";

const LEAD_GEN_COLUMNS: Array<{ key: LeadGenColumnKey; labelKey: string; sortKey: string }> = [
  { key: "leads", labelKey: "dashboard.table.leads", sortKey: "leads" },
  { key: "spend", labelKey: "dashboard.table.spend", sortKey: "spend" },
  { key: "cpl", labelKey: "dashboard.table.costPerLead", sortKey: "cpl" },
  { key: "webinar_live", labelKey: "dashboard.table.webinarLive", sortKey: "webinar_live" },
  {
    key: "webinar_cost_per_live",
    labelKey: "dashboard.table.webinarCostPerLive",
    sortKey: "webinar_cost_per_live",
  },
  { key: "webinar_show_rate", labelKey: "dashboard.table.showRate", sortKey: "webinar_show_rate" },
  { key: "rozmowy", labelKey: "dashboard.table.calls", sortKey: "rozmowy" },
  { key: "costPerRozmowa", labelKey: "dashboard.table.costPerCall", sortKey: "costPerRozmowa" },
  { key: "showRate", labelKey: "dashboard.table.showRate", sortKey: "showRate" },
  { key: "sales", labelKey: "dashboard.table.sales", sortKey: "sales" },
  { key: "cpc", labelKey: "dashboard.table.cpc", sortKey: "cpc" },
  { key: "cpm", labelKey: "dashboard.table.cpm", sortKey: "cpm" },
  { key: "roas", labelKey: "dashboard.table.roas", sortKey: "roas" },
];

type WebinarStatsData = DashboardData["webinarStats"];

/**
 * Resolves the [since, until] window (YYYY-MM-DD) for a date preset, mirroring the
 * server's `resolveTimeRange`. Used by the "Dane" tab to clip its merged full-history
 * dataset to the currently selected range (e.g. last_30d must not leak March rows).
 * Returns nulls when the range is open (no clipping) — e.g. an incomplete custom range.
 */
function resolveDaneRange(
  datePreset: string,
  customFrom?: Date,
  customTo?: Date
): { since: string | null; until: string | null } {
  const fmt = toLocalYmd;
  const today = new Date();
  const sub = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d;
  };
  switch (datePreset) {
    case "today":
      return { since: fmt(today), until: fmt(today) };
    case "yesterday": {
      const y = sub(1);
      return { since: fmt(y), until: fmt(y) };
    }
    case "last_7d":
      return { since: fmt(sub(7)), until: fmt(sub(1)) };
    case "last_14d":
      return { since: fmt(sub(14)), until: fmt(sub(1)) };
    case "last_30d":
      return { since: fmt(sub(30)), until: fmt(sub(1)) };
    case "last_90d":
      return { since: fmt(sub(90)), until: fmt(sub(1)) };
    case "this_month":
      return { since: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), until: fmt(today) };
    case "last_month":
      return {
        since: fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        until: fmt(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case "custom":
      return customFrom && customTo
        ? { since: fmt(customFrom), until: fmt(customTo) }
        : { since: null, until: null };
    default:
      return { since: null, until: null };
  }
}

function CampaignTable({
  campaigns,
  datePreset,
  customRange,
  adAccountId,
  dashboardMode,
  totals,
  utmAttribution,
  adUtmAttribution,
  leadsByAdSet,
  leadsByAd,
  ecomRevenue,
  metricsV2On = false,
  webinarStats,
  forClientUserId,
  organicSourceGroups,
  googleAdsAdGroups,
  previewEnabled = false,
  attributionModel,
}: {
  campaigns: CampaignInsight[];
  datePreset: string;
  customRange?: { since: string; until: string };
  adAccountId: string;
  dashboardMode?: string;
  totals?: DashboardData["totals"];
  utmAttribution?: UtmAttributionRow[];
  adUtmAttribution?: AdUtmData;
  leadsByAdSet?: Record<string, number>;
  leadsByAd?: Record<string, number>;
  ecomRevenue?: EcomRevenueMaps;
  metricsV2On?: boolean;
  webinarStats?: WebinarStatsData;
  forClientUserId?: string;
  organicSourceGroups?: OrganicSourceGroup[];
  googleAdsAdGroups?: any[];
  previewEnabled?: boolean;
  /** VP-134: przekazywany do modala leadów, żeby drill-down liczył tym samym modelem co komórka. */
  attributionModel?: AttributionModel;
}) {
  const { t } = useTranslation();
  const isEcom = dashboardMode === "ecommerce";
  const isSaasMode = dashboardMode === "saas";
  const isAutowebinarMode = dashboardMode === "autowebinar_book_a_call";
  const columnStorageKey = `dashboard_campaign_columns_${dashboardMode || "lead_gen_basic"}`;
  const leadGenColumnMap = useMemo(() => {
    const map = new Map<
      LeadGenColumnKey,
      { key: LeadGenColumnKey; labelKey: string; sortKey: string }
    >();
    LEAD_GEN_COLUMNS.forEach((column) => map.set(column.key, column));
    return map;
  }, []);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(
    new Set(["_platform_meta", "_platform_google"])
  );
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [leadDetailPopup, setLeadDetailPopup] = useState<{
    type: "campaign" | "adset" | "ad" | "organic";
    id: string;
    name: string;
    view?: "leads" | "nowe-leady" | "rozmowy" | "sprzedaze" | "live" | "zamowienia";
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    parentAdSetId?: string;
    parentCampaignId?: string;
  } | null>(null);
  const [channelFilter, setChannelFilter] = useState<
    "all" | "facebook" | "google" | "tiktok" | "organic" | "other"
  >("all");
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [leadGenColumnOrder, setLeadGenColumnOrder] = useState<LeadGenColumnKey[]>(
    LEAD_GEN_COLUMNS.map((column) => column.key)
  );
  const [hiddenLeadGenColumns, setHiddenLeadGenColumns] = useState<Set<LeadGenColumnKey>>(
    new Set()
  );
  const [columnDraftOrder, setColumnDraftOrder] = useState<LeadGenColumnKey[]>(
    LEAD_GEN_COLUMNS.map((column) => column.key)
  );
  const [hiddenColumnDraft, setHiddenColumnDraft] = useState<Set<LeadGenColumnKey>>(new Set());
  const [draggedColumn, setDraggedColumn] = useState<LeadGenColumnKey | null>(null);

  // VP-213: stawki kosztów zmiennych do Net Profit/Net ROAS per wiersz (tylko ecom).
  // forUserId: na dashboardzie klienta agencji liczymy z KOSZTÓW KLIENTA — revenue
  // w tych samych wierszach też jest klienta (review VP-213).
  const { data: costRates } = useQuery<VariableRatesSummary>({
    queryKey: ["/api/ecommerce-costs/rates", forClientUserId ?? ""],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/ecommerce-costs/rates${forClientUserId ? `?forUserId=${forClientUserId}` : ""}`
      ),
    enabled: isEcom,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (isEcom || isSaasMode) return;
    try {
      const raw = localStorage.getItem(columnStorageKey);
      if (!raw) {
        const defaultOrder = LEAD_GEN_COLUMNS.map((column) => column.key);
        setLeadGenColumnOrder(defaultOrder);
        setHiddenLeadGenColumns(new Set());
        setColumnDraftOrder(defaultOrder);
        setHiddenColumnDraft(new Set());
        return;
      }
      const parsed = JSON.parse(raw) as { order?: LeadGenColumnKey[]; hidden?: LeadGenColumnKey[] };
      const availableKeys = new Set<LeadGenColumnKey>(LEAD_GEN_COLUMNS.map((column) => column.key));
      const savedOrder = (parsed.order || []).filter((key): key is LeadGenColumnKey =>
        availableKeys.has(key)
      );
      const missingKeys = LEAD_GEN_COLUMNS.map((column) => column.key).filter(
        (key) => !savedOrder.includes(key)
      );
      const nextOrder = [...savedOrder, ...missingKeys];
      const nextHidden = new Set(
        (parsed.hidden || []).filter((key): key is LeadGenColumnKey => availableKeys.has(key))
      );

      setLeadGenColumnOrder(nextOrder);
      setHiddenLeadGenColumns(nextHidden);
      setColumnDraftOrder(nextOrder);
      setHiddenColumnDraft(new Set(nextHidden));
    } catch {
      const defaultOrder = LEAD_GEN_COLUMNS.map((column) => column.key);
      setLeadGenColumnOrder(defaultOrder);
      setHiddenLeadGenColumns(new Set());
      setColumnDraftOrder(defaultOrder);
      setHiddenColumnDraft(new Set());
    }
  }, [columnStorageKey, isEcom, isSaasMode]);

  const visibleLeadGenColumns = useMemo(() => {
    return leadGenColumnOrder
      .map((key) => leadGenColumnMap.get(key))
      .filter(
        (column): column is { key: LeadGenColumnKey; labelKey: string; sortKey: string } => !!column
      )
      .filter((column) => !hiddenLeadGenColumns.has(column.key));
  }, [leadGenColumnOrder, leadGenColumnMap, hiddenLeadGenColumns]);

  const isLeadGenColumnCustomized = useMemo(() => {
    const defaultOrder = LEAD_GEN_COLUMNS.map((column) => column.key);
    if (hiddenLeadGenColumns.size > 0) return true;
    if (defaultOrder.length !== leadGenColumnOrder.length) return true;
    return defaultOrder.some((key, index) => leadGenColumnOrder[index] !== key);
  }, [hiddenLeadGenColumns, leadGenColumnOrder]);

  const handleSort = useCallback((key: string) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
        return key;
      }
      setSortDir("desc");
      return key;
    });
  }, []);

  // Build UTM attribution lookup by campaign ID (aggregate all adsets for a campaign)
  const utmByCampaign = useMemo(() => {
    const map = new Map<
      string,
      {
        bookedCalls: number;
        showed: number;
        noShow: number;
        won: number;
        revenue: number;
        leads: number;
        newLeads: number;
      }
    >();
    if (!utmAttribution) return map;
    for (const row of utmAttribution) {
      if (!row.metaCampaignId) continue;
      const existing = map.get(row.metaCampaignId) || {
        bookedCalls: 0,
        showed: 0,
        noShow: 0,
        won: 0,
        revenue: 0,
        leads: 0,
        newLeads: 0,
      };
      existing.bookedCalls += row.bookedCalls;
      existing.showed += row.showed;
      existing.noShow += row.noShow;
      existing.won += row.won;
      existing.revenue += row.revenue;
      existing.leads += row.leads;
      existing.newLeads += row.newLeads;
      map.set(row.metaCampaignId, existing);
    }
    return map;
  }, [utmAttribution]);

  const sortedCampaigns = useMemo(() => {
    if (!sortKey) return campaigns;
    const getValue = (c: CampaignInsight): number => {
      const spend = parseFloat(c.spend) || 0;
      const leads = parseInt(c.leads) || 0;
      const utm = utmByCampaign.get(c.campaignId);
      switch (sortKey) {
        case "newLeads":
          return utm?.newLeads || 0;
        // Conversion-metric sorts use INTEGRATION attribution (utm), matching what the columns
        // display — never the ad platform's leads/purchaseValue/purchaseRoas. Ecom per-campaign
        // has no integration source → 0 (those columns render '—').
        case "leads":
          return utm?.leads || 0;
        case "netProfit":
        case "netRoas": {
          // VP-213: sort liczony identycznie jak komórki (integracja + stawki zmienne).
          const rp = computeRowProfit({
            revenue: utm?.revenue,
            spend,
            purchases: utm?.won,
            campaignId: c.campaignId,
            rates: costRates,
          });
          if (!rp) return 0;
          return sortKey === "netProfit" ? rp.netProfit : (rp.netRoas ?? 0);
        }
        case "spend":
          return spend;
        case "cpl":
          return (utm?.leads || 0) > 0 ? spend / utm!.leads : 0;
        case "cpc":
          return parseFloat(c.cpc) || 0;
        case "cpm":
          return parseFloat(c.cpm) || 0;
        case "roas":
          return (utm?.revenue || 0) > 0 && spend > 0 ? utm!.revenue / spend : 0;
        case "cv":
          return utm?.revenue || 0;
        case "cpa":
          return 0;
        case "rozmowy":
          return utm?.bookedCalls || 0;
        case "costPerRozmowa":
          return (utm?.bookedCalls || 0) > 0 ? spend / utm!.bookedCalls : 0;
        case "showRate": {
          if (!utm) return 0;
          const d = utm.showed + utm.noShow;
          return d > 0 ? (utm.showed / d) * 100 : 0;
        }
        case "sales":
          return utm?.won || 0;
        default:
          return 0;
      }
    };
    return [...campaigns].sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      return sortDir === "desc" ? vb - va : va - vb;
    });
  }, [campaigns, sortKey, sortDir, utmByCampaign, costRates]);

  // Apply channel filter
  const hasGoogleCampaigns = sortedCampaigns.some((c) => c._source === "google");
  const hasTiktokCampaigns = sortedCampaigns.some((c) => c._source === "tiktok");
  const hasMetaCampaigns = sortedCampaigns.some((c) => c._source === "meta" || !c._source);
  const hasOrganicSources = (organicSourceGroups || []).length > 0;

  const channelFilteredCampaigns = useMemo(() => {
    if (channelFilter === "all") return sortedCampaigns;
    if (channelFilter === "facebook")
      return sortedCampaigns.filter((c) => c._source === "meta" || !c._source);
    if (channelFilter === "google") return sortedCampaigns.filter((c) => c._source === "google");
    if (channelFilter === "tiktok") return sortedCampaigns.filter((c) => c._source === "tiktok");
    if (channelFilter === "organic") return []; // paid campaigns hidden when organic filter active
    return sortedCampaigns.filter(
      (c) =>
        !c._source || (c._source !== "meta" && c._source !== "google" && c._source !== "tiktok")
    );
  }, [sortedCampaigns, channelFilter]);

  // Group campaigns hierarchically: Platform → Ad Account → Campaigns
  const platformAccountGroups = useMemo(() => {
    type AccountGroup = {
      accountId: string;
      accountName: string;
      campaigns: CampaignInsight[];
      totalSpend: number;
      totalLeads: number;
      totalClicks: number;
      totalImpressions: number;
      totalPurchaseValue: number;
    };
    type PlatformGroup = {
      platform: "meta" | "google" | "tiktok";
      label: string;
      accounts: AccountGroup[];
      totalSpend: number;
      totalLeads: number;
      totalClicks: number;
      totalImpressions: number;
      totalPurchaseValue: number;
    };

    const platformMap = new Map<string, Map<string, CampaignInsight[]>>();
    for (const c of channelFilteredCampaigns) {
      const platform = c._source || "meta";
      const accountKey =
        c._accountId ||
        (platform === "google"
          ? "google_default"
          : platform === "tiktok"
            ? "tiktok_default"
            : "meta_default");
      if (!platformMap.has(platform)) platformMap.set(platform, new Map());
      const accountMap = platformMap.get(platform)!;
      if (!accountMap.has(accountKey)) accountMap.set(accountKey, []);
      accountMap.get(accountKey)!.push(c);
    }

    const groups: PlatformGroup[] = [];
    const platformOrder: ("meta" | "google" | "tiktok")[] = ["meta", "google", "tiktok"];
    for (const platform of platformOrder) {
      const accountMap = platformMap.get(platform);
      if (!accountMap) continue;
      const accounts: AccountGroup[] = [];
      let pSpend = 0,
        pLeads = 0,
        pClicks = 0,
        pImpressions = 0,
        pPurchaseValue = 0;
      accountMap.forEach((campaigns, accountId) => {
        let aSpend = 0,
          aLeads = 0,
          aClicks = 0,
          aImpressions = 0,
          aPurchaseValue = 0;
        for (const c of campaigns) {
          aSpend += parseFloat(c.spend) || 0;
          aLeads += parseInt(c.leads) || 0;
          aClicks += parseInt(c.clicks) || 0;
          aImpressions += parseInt(c.impressions) || 0;
          aPurchaseValue += parseFloat(c.purchaseValue) || 0;
        }
        accounts.push({
          accountId,
          accountName: campaigns[0]?._accountName || accountId,
          campaigns,
          totalSpend: aSpend,
          totalLeads: aLeads,
          totalClicks: aClicks,
          totalImpressions: aImpressions,
          totalPurchaseValue: aPurchaseValue,
        });
        pSpend += aSpend;
        pLeads += aLeads;
        pClicks += aClicks;
        pImpressions += aImpressions;
        pPurchaseValue += aPurchaseValue;
      });
      groups.push({
        platform,
        label:
          platform === "meta" ? "Meta Ads" : platform === "google" ? "Google Ads" : "TikTok Ads",
        accounts,
        totalSpend: pSpend,
        totalLeads: pLeads,
        totalClicks: pClicks,
        totalImpressions: pImpressions,
        totalPurchaseValue: pPurchaseValue,
      });
    }
    return groups;
  }, [channelFilteredCampaigns]);

  // Whether to show hierarchical view (has multiple platforms or multiple accounts)
  const showHierarchy =
    platformAccountGroups.length > 1 || platformAccountGroups.some((p) => p.accounts.length > 1);

  // Show organic sources only when filter is 'all' or 'organic'
  const showOrganic = channelFilter === "all" || channelFilter === "organic";

  const organicBySource = useMemo(() => {
    const sourceMap = new Map<
      string,
      {
        utmSource: string;
        leads: number;
        newLeads: number;
        bookedCalls: number;
        showed: number;
        noShow: number;
        won: number;
        revenue: number;
        mediums: OrganicSourceGroup[];
      }
    >();

    (organicSourceGroups || []).forEach((group) => {
      const sourceKey = (group.utmSource || "organic").trim().toLowerCase() || "organic";
      const existing = sourceMap.get(sourceKey) || {
        utmSource: sourceKey,
        leads: 0,
        newLeads: 0,
        bookedCalls: 0,
        showed: 0,
        noShow: 0,
        won: 0,
        revenue: 0,
        mediums: [],
      };

      existing.leads += group.leads;
      existing.newLeads += group.newLeads;
      existing.bookedCalls += group.bookedCalls;
      existing.showed += group.showed;
      existing.noShow += group.noShow;
      existing.won += group.won;
      existing.revenue += group.revenue;
      existing.mediums.push(group);
      sourceMap.set(sourceKey, existing);
    });

    return Array.from(sourceMap.values())
      .map((source) => ({
        ...source,
        mediums: [...source.mediums].sort((a, b) => b.leads - a.leads),
      }))
      .sort((a, b) => b.leads - a.leads);
  }, [organicSourceGroups]);

  const toggleCampaign = useCallback((campaignId: string) => {
    setExpandedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  }, []);

  const onColumnDragStart = useCallback((columnKey: LeadGenColumnKey) => {
    setDraggedColumn(columnKey);
  }, []);

  const onColumnDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const onColumnDrop = useCallback(
    (targetKey: LeadGenColumnKey) => {
      if (!draggedColumn || draggedColumn === targetKey) return;
      setColumnDraftOrder((prev) => {
        const fromIndex = prev.indexOf(draggedColumn);
        const toIndex = prev.indexOf(targetKey);
        if (fromIndex < 0 || toIndex < 0) return prev;
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
      setDraggedColumn(null);
    },
    [draggedColumn]
  );

  const toggleDraftColumnVisibility = useCallback(
    (columnKey: LeadGenColumnKey, isVisible: boolean) => {
      setHiddenColumnDraft((prev) => {
        const next = new Set(prev);
        if (isVisible) next.delete(columnKey);
        else next.add(columnKey);
        return next;
      });
    },
    []
  );

  const applyLeadGenColumnSettings = useCallback(() => {
    setLeadGenColumnOrder(columnDraftOrder);
    setHiddenLeadGenColumns(new Set(hiddenColumnDraft));
    try {
      localStorage.setItem(
        columnStorageKey,
        JSON.stringify({
          order: columnDraftOrder,
          hidden: Array.from(hiddenColumnDraft),
        })
      );
    } catch {}
    setColumnSettingsOpen(false);
  }, [columnDraftOrder, hiddenColumnDraft, columnStorageKey]);

  const resetLeadGenColumnSettings = useCallback(() => {
    const defaultOrder = LEAD_GEN_COLUMNS.map((column) => column.key);
    setLeadGenColumnOrder(defaultOrder);
    setHiddenLeadGenColumns(new Set());
    setColumnDraftOrder(defaultOrder);
    setHiddenColumnDraft(new Set());
    try {
      localStorage.removeItem(columnStorageKey);
    } catch {}
    setColumnSettingsOpen(false);
  }, [columnStorageKey]);

  const SortHeader = ({
    label,
    colKey,
    align = "right",
  }: {
    label: string;
    colKey: string;
    align?: string;
  }) => (
    <th
      className={`text-${align} text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 cursor-pointer select-none hover:text-foreground transition-colors`}
      onClick={() => handleSort(colKey)}
    >
      <span className="inline-flex items-center gap-1 justify-end">
        {label}
        {sortKey === colKey ? (
          sortDir === "desc" ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3 rotate-[-90deg]" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </span>
    </th>
  );

  return (
    <>
      {isEcom && (
        <div className="mb-4">
          <ProfitCard datePreset={datePreset} adAccountId={adAccountId} />
        </div>
      )}
      <Card className="bg-muted/50 border-border rounded-xl overflow-hidden">
        <div className="p-4 md:p-5 border-b border-border">
          <div className="flex items-center justify-between flex-wrap gap-dense">
            <h2 className="text-foreground font-semibold text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#ef3e29]" />
              {t("dashboard.campaignOverview")}
              {hasOrganicSources && (
                <span className="text-[10px] text-emerald-500/60 bg-emerald-500/10 px-1.5 py-0.5 rounded font-normal">
                  + organic
                </span>
              )}
              <a
                href="/attribution-overrides"
                className="text-[10px] uppercase tracking-wider text-orange-400/80 bg-orange-500/10 hover:bg-orange-500/20 px-2 py-0.5 rounded font-semibold border border-orange-500/30 transition"
                title="Mapuj tekstowe utm_campaign do prawdziwych Meta ID"
              >
                ⚙ Atrybucja UTM
              </a>
            </h2>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground mr-1">
                <Filter className="w-3.5 h-3.5 inline" /> {t("dashboard.filters")}:
              </span>
              {[
                {
                  key: "all" as const,
                  label: t("dashboard.table.filterAll"),
                  icon: null,
                  disabled: false,
                },
                {
                  key: "facebook" as const,
                  label: "Facebook Ads",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="#1877F2">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  ),
                  disabled: !hasMetaCampaigns,
                },
                {
                  key: "google" as const,
                  label: "Google Ads",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5">
                      <path d="M12 2L8 10h8l-4-8z" fill="#4285F4" />
                      <path d="M8 10l-4 8h8l4-8H8z" fill="#FBBC04" />
                      <path d="M16 10l4 8h-8l-4-8h8z" fill="#34A853" />
                      <circle cx="6" cy="18" r="2" fill="#EA4335" />
                    </svg>
                  ),
                  disabled: !hasGoogleCampaigns,
                },
                {
                  key: "tiktok" as const,
                  label: "TikTok Ads",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  ),
                  disabled: !hasTiktokCampaigns,
                },
                {
                  key: "organic" as const,
                  label: "Organic",
                  icon: <Layers className="w-3.5 h-3.5 text-emerald-500" />,
                  disabled: !hasOrganicSources,
                },
                {
                  key: "other" as const,
                  label: t("dashboard.table.filterOther"),
                  icon: null,
                  disabled: false,
                },
              ]
                // VP-192: Google Ads jest ukryte w UI (integracja niegotowa —
                // aplikacja nie przeszła weryfikacji Google OAuth). Zamiast
                // wyszarzonego chipa „Google Ads" nie pokazujemy go wcale, dopóki
                // nie ma kampanii z tego źródła. Pozostałe platformy zachowują
                // dotychczasowy stan „disabled".
                .filter(({ key, disabled }) => !(key === "google" && disabled))
                .map(({ key, label, icon, disabled }) => (
                  <button
                    key={key}
                    onClick={() => !disabled && setChannelFilter(key)}
                    title={disabled ? t("dashboard.table.noDataFromSource", { label }) : label}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      disabled
                        ? "bg-muted/40 text-muted-foreground border border-border cursor-not-allowed opacity-50"
                        : channelFilter === key
                          ? "bg-[#ef3e29]/20 text-[#ef3e29] border border-[#ef3e29]/40"
                          : "bg-accent text-muted-foreground border border-border hover:bg-accent hover:text-foreground"
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              {!isEcom && (
                <Popover
                  open={columnSettingsOpen}
                  onOpenChange={(open) => {
                    setColumnSettingsOpen(open);
                    if (open) {
                      setColumnDraftOrder(leadGenColumnOrder);
                      setHiddenColumnDraft(new Set(hiddenLeadGenColumns));
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                        isLeadGenColumnCustomized
                          ? "bg-[#ef3e29]/20 text-[#ef3e29] border-[#ef3e29]/40"
                          : "bg-accent text-muted-foreground border-border hover:bg-accent hover:text-foreground"
                      }`}
                      title={t("dashboard.table.columnSettings")}
                    >
                      <Settings className="w-3.5 h-3.5" />
                      {t("dashboard.table.params")}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0 bg-card border-border" align="end">
                    <div className="p-3 border-b border-border">
                      <p className="text-xs font-medium text-foreground">
                        {t("dashboard.table.columnSettings")}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t("dashboard.table.columnSettingsHint")}
                      </p>
                    </div>
                    <div className="p-3 space-y-2 max-h-[320px] overflow-y-auto">
                      {columnDraftOrder.map((columnKey) => {
                        const column = leadGenColumnMap.get(columnKey);
                        if (!column) return null;
                        const isVisible = !hiddenColumnDraft.has(columnKey);
                        return (
                          <div
                            key={columnKey}
                            draggable
                            onDragStart={() => onColumnDragStart(columnKey)}
                            onDragOver={onColumnDragOver}
                            onDrop={() => onColumnDrop(columnKey)}
                            onDragEnd={() => setDraggedColumn(null)}
                            className={`flex items-center justify-between gap-2 bg-muted/50 border rounded-lg px-2 py-1.5 cursor-grab active:cursor-grabbing ${
                              draggedColumn === columnKey
                                ? "border-[#ef3e29]/50 bg-[#ef3e29]/10"
                                : "border-border"
                            }`}
                          >
                            <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isVisible}
                                onChange={(e) =>
                                  toggleDraftColumnVisibility(columnKey, e.target.checked)
                                }
                                className="rounded border-border bg-accent"
                              />
                              {t(column.labelKey)}
                            </label>
                            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                              <GripVertical className="w-3.5 h-3.5" />
                              {t("dashboard.table.drag")}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="p-3 border-t border-border flex items-center justify-between gap-2">
                      <button
                        onClick={resetLeadGenColumnSettings}
                        className="px-3 py-1.5 text-xs text-muted-foreground border border-border rounded-lg hover:text-foreground transition-colors"
                      >
                        {t("dashboard.table.reset")}
                      </button>
                      <button
                        onClick={applyLeadGenColumnSettings}
                        className="px-3 py-1.5 text-xs text-foreground border border-border rounded-lg hover:border-border hover:text-foreground transition-colors"
                      >
                        {t("dashboard.table.apply")}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3 w-8"></th>
                <th className="text-left text-xs font-medium text-[#ef3e29] uppercase tracking-wide px-4 py-3">
                  {t("dashboard.table.campaign")}
                </th>
                {isEcom ? (
                  <>
                    <SortHeader label={t("dashboard.table.spend")} colKey="spend" />
                    <SortHeader label="CV" colKey="cv" />
                    <SortHeader label={t("dashboard.table.cpc")} colKey="cpc" />
                    <SortHeader label={t("dashboard.table.cpm")} colKey="cpm" />
                    <SortHeader label="CPA" colKey="cpa" />
                    <SortHeader label={t("dashboard.table.roas")} colKey="roas" />
                    {/* VP-213: Net Profit / Net ROAS — revenue integracji − spend − koszty zmienne */}
                    <SortHeader label="Net Profit" colKey="netProfit" />
                    <SortHeader label="Net ROAS" colKey="netRoas" />
                  </>
                ) : isSaasMode ? (
                  <>
                    <SortHeader label="Trial" colKey="leads" />
                    <SortHeader label={t("dashboard.table.spend")} colKey="spend" />
                    <SortHeader label="CPA Trial" colKey="cpl" />
                    <SortHeader label={t("dashboard.table.subscriptions")} colKey="sales" />
                    <SortHeader label={t("dashboard.table.crTrialToSub")} colKey="showRate" />
                    <SortHeader label={t("dashboard.table.cpc")} colKey="cpc" />
                    <SortHeader label={t("dashboard.table.cpm")} colKey="cpm" />
                    <SortHeader label="CTR" colKey="ctr" />
                  </>
                ) : (
                  <>
                    <SortHeader label={t("dashboard.table.leads")} colKey="leads" />
                    <SortHeader label={t("dashboard.table.spend")} colKey="spend" />
                    <SortHeader label={t("dashboard.table.costPerLead")} colKey="cpl" />
                    {isAutowebinarMode && (
                      <SortHeader label={t("dashboard.table.webinarLive")} colKey="webinar_live" />
                    )}
                    {isAutowebinarMode && (
                      <SortHeader
                        label={t("dashboard.table.webinarCostPerLive")}
                        colKey="webinar_cost_per_live"
                      />
                    )}
                    {isAutowebinarMode && (
                      <SortHeader
                        label={t("dashboard.table.showRate")}
                        colKey="webinar_show_rate"
                      />
                    )}
                    <SortHeader label={t("dashboard.table.calls")} colKey="rozmowy" />
                    <SortHeader label={t("dashboard.table.costPerCall")} colKey="costPerRozmowa" />
                    <SortHeader label={t("dashboard.table.showRate")} colKey="showRate" />
                    <SortHeader label={t("dashboard.table.sales")} colKey="sales" />
                    <SortHeader label={t("dashboard.table.cpc")} colKey="cpc" />
                    <SortHeader label={t("dashboard.table.cpm")} colKey="cpm" />
                    <SortHeader label={t("dashboard.table.roas")} colKey="roas" />
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {!isEcom && !isSaasMode && totals && (
                <tr className="border-b border-border bg-accent">
                  <td className="px-2 py-3"></td>
                  <td className="px-4 py-3 text-sm text-foreground font-semibold">
                    {t(`dashboard.table.total`)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {totals.leads}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatCurrency(totals.spend)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {totals.costPerLead > 0 ? formatCurrency(totals.costPerLead) : "—"}
                  </td>
                  {isAutowebinarMode && (
                    <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                      {(webinarStats?.totalAttended ?? 0) > 0
                        ? formatNumber(webinarStats!.totalAttended)
                        : "—"}
                    </td>
                  )}
                  {isAutowebinarMode && (
                    <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                      {(webinarStats?.totalAttended ?? 0) > 0
                        ? formatCurrency(totals.spend / webinarStats!.totalAttended)
                        : "—"}
                    </td>
                  )}
                  {isAutowebinarMode && (
                    <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                      {(webinarStats?.totalAttended ?? 0) > 0 && totals.leads > 0
                        ? formatPercent((webinarStats!.totalAttended / totals.leads) * 100)
                        : "—"}
                    </td>
                  )}
                  {/* VP-140: SUMA ROZMOWY = WSZYSTKIE rozmowy w oknie (totals.bookedAppointments), też
                    organiczne/bez źródła — SUMA może być > sumy wierszy (wiersze są Meta-only, z designu).
                    Koszt/rozmowę = wydatek ÷ rozmowy Z REKLAM (utmByCampaign) — realny koszt płatnej
                    rozmowy, a nie rozwodniony darmowymi organicznymi. */}
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatNumber(totals.bookedAppointments)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {(() => {
                      let s = 0;
                      utmByCampaign.forEach((v) => (s += v.bookedCalls));
                      return s > 0 ? formatCurrency(totals.spend / s) : "—";
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {totals.showRate > 0 ? formatPercent(totals.showRate) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-emerald-400 text-right font-semibold">
                    {(totals.sales || 0) > 0 ? formatNumber(totals.sales!) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatCurrency(totals.cpc)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatCurrency(totals.cpm)}
                  </td>
                  {/* ROAS from INTEGRATION revenue (revenueRoas = GHL/CRM/attribution revenue ÷ spend),
                    never totals.roas (the Meta-pixel ecom field, which would leak the ad platform). */}
                  <td
                    className={`px-4 py-3 text-sm text-right font-semibold ${totals.revenueRoas >= 1 ? "text-emerald-400" : totals.revenueRoas > 0 ? "text-red-400" : "text-muted-foreground"}`}
                  >
                    {totals.revenueRoas > 0 ? `${totals.revenueRoas.toFixed(2)}x` : "—"}
                  </td>
                </tr>
              )}
              {isSaasMode && totals && (
                <tr className="border-b border-border bg-accent">
                  <td className="px-2 py-3"></td>
                  <td className="px-4 py-3 text-sm text-foreground font-semibold">
                    {t(`dashboard.table.total`)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatNumber(totals.trials)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatCurrency(totals.spend)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {totals.costPerTrial > 0 ? formatCurrency(totals.costPerTrial) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-emerald-400 text-right font-semibold">
                    {totals.subscriptions > 0 ? formatNumber(totals.subscriptions) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {totals.trials > 0 && totals.subscriptions > 0
                      ? formatPercent((totals.subscriptions / totals.trials) * 100)
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatCurrency(totals.cpc)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                    {formatCurrency(totals.cpm)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">{`${totals.ctr.toFixed(2)}%`}</td>
                </tr>
              )}
              {showHierarchy
                ? /* ─── Hierarchical view: Platform → Account → Campaign ─── */
                  platformAccountGroups.map((platformGroup) => {
                    const platformExpandKey = `_platform_${platformGroup.platform}`;
                    const platformExpanded = expandedCampaigns.has(platformExpandKey);
                    const platformIcon =
                      platformGroup.platform === "meta" ? (
                        <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="#1877F2">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none">
                          <path
                            d="M3.272 13.446l6.104-10.594c.95-1.647 3.059-2.21 4.706-1.26l.002.001c1.647.95 2.21 3.059 1.26 4.706L9.24 16.893c-.95 1.647-3.059 2.21-4.706 1.26h-.001c-1.647-.95-2.21-3.059-1.26-4.706z"
                            fill="#FBBC04"
                          />
                          <circle cx="6" cy="18.5" r="3.5" fill="#34A853" />
                        </svg>
                      );

                    return (
                      <Fragment key={platformExpandKey}>
                        {/* Platform header row */}
                        <tr
                          className="border-b border-border bg-muted/60 hover:bg-accent transition-colors cursor-pointer"
                          onClick={() => toggleCampaign(platformExpandKey)}
                        >
                          <td className="px-2 py-3 text-center">
                            {platformExpanded ? (
                              <ChevronDown className="w-4 h-4 text-[#ef3e29] inline" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-muted-foreground inline" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground font-semibold">
                            <span className="inline-flex items-center gap-2">
                              {platformIcon}
                              {platformGroup.label}
                              <span className="text-muted-foreground font-normal text-xs">
                                (
                                {platformGroup.accounts.reduce((s, a) => s + a.campaigns.length, 0)}{" "}
                                kampanii)
                              </span>
                            </span>
                          </td>
                          {isEcom ? (
                            <>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {formatCurrency(platformGroup.totalSpend)}
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {platformGroup.totalPurchaseValue > 0
                                  ? formatCurrency(platformGroup.totalPurchaseValue)
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {platformGroup.totalClicks > 0
                                  ? formatCurrency(
                                      platformGroup.totalSpend / platformGroup.totalClicks
                                    )
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {platformGroup.totalImpressions > 0
                                  ? formatCurrency(
                                      (platformGroup.totalSpend / platformGroup.totalImpressions) *
                                        1000
                                    )
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                              <td
                                className={`px-4 py-3 text-sm text-right font-semibold ${platformGroup.totalSpend > 0 && platformGroup.totalPurchaseValue / platformGroup.totalSpend >= 1 ? "text-emerald-400" : "text-muted-foreground"}`}
                              >
                                {platformGroup.totalSpend > 0
                                  ? `${(platformGroup.totalPurchaseValue / platformGroup.totalSpend).toFixed(2)}x`
                                  : "—"}
                              </td>
                              {/* VP-213: grupy platform/kont → '—' (sumowanie per-grupa: VP-214) */}
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                            </>
                          ) : isSaasMode ? (
                            <>
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {formatCurrency(platformGroup.totalSpend)}
                              </td>
                              <td colSpan={7} />
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3 text-sm text-foreground font-semibold text-right">
                                {platformGroup.totalLeads > 0
                                  ? formatNumber(platformGroup.totalLeads)
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {formatCurrency(platformGroup.totalSpend)}
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {platformGroup.totalLeads > 0
                                  ? formatCurrency(
                                      platformGroup.totalSpend / platformGroup.totalLeads
                                    )
                                  : "—"}
                              </td>
                              <td colSpan={isAutowebinarMode ? 7 : 4} />
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {platformGroup.totalClicks > 0
                                  ? formatCurrency(
                                      platformGroup.totalSpend / platformGroup.totalClicks
                                    )
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground text-right font-semibold">
                                {platformGroup.totalImpressions > 0
                                  ? formatCurrency(
                                      (platformGroup.totalSpend / platformGroup.totalImpressions) *
                                        1000
                                    )
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                            </>
                          )}
                        </tr>

                        {/* Account + Campaign rows when platform expanded */}
                        {platformExpanded &&
                          platformGroup.accounts.map((accountGroup) => {
                            const accountExpandKey = `_account_${platformGroup.platform}_${accountGroup.accountId}`;
                            const accountExpanded = expandedCampaigns.has(accountExpandKey);
                            const showAccountRow = platformGroup.accounts.length > 1;

                            return (
                              <Fragment key={accountExpandKey}>
                                {/* Account header row (only if multiple accounts) */}
                                {showAccountRow && (
                                  <tr
                                    className="border-b border-border bg-muted/40 hover:bg-muted/60 transition-colors cursor-pointer"
                                    onClick={() => toggleCampaign(accountExpandKey)}
                                  >
                                    <td className="px-2 py-2.5 text-center">
                                      <span className="inline-flex items-center pl-3">
                                        {accountExpanded ? (
                                          <ChevronDown className="w-3.5 h-3.5 text-[#ef3e29]/70 inline" />
                                        ) : (
                                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground inline" />
                                        )}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-foreground font-medium pl-8">
                                      <span className="inline-flex items-center gap-1.5">
                                        <span className="text-muted-foreground">↳</span>
                                        {accountGroup.accountName}
                                        <span className="text-muted-foreground text-[10px]">
                                          ({accountGroup.campaigns.length})
                                        </span>
                                      </span>
                                    </td>
                                    {isEcom ? (
                                      <>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {formatCurrency(accountGroup.totalSpend)}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {accountGroup.totalPurchaseValue > 0
                                            ? formatCurrency(accountGroup.totalPurchaseValue)
                                            : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {accountGroup.totalClicks > 0
                                            ? formatCurrency(
                                                accountGroup.totalSpend / accountGroup.totalClicks
                                              )
                                            : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {accountGroup.totalImpressions > 0
                                            ? formatCurrency(
                                                (accountGroup.totalSpend /
                                                  accountGroup.totalImpressions) *
                                                  1000
                                              )
                                            : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          —
                                        </td>
                                        <td
                                          className={`px-4 py-2.5 text-xs text-right ${accountGroup.totalSpend > 0 && accountGroup.totalPurchaseValue / accountGroup.totalSpend >= 1 ? "text-emerald-400" : "text-muted-foreground"}`}
                                        >
                                          {accountGroup.totalSpend > 0
                                            ? `${(accountGroup.totalPurchaseValue / accountGroup.totalSpend).toFixed(2)}x`
                                            : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          —
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          —
                                        </td>
                                      </>
                                    ) : isSaasMode ? (
                                      <>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          —
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {formatCurrency(accountGroup.totalSpend)}
                                        </td>
                                        <td colSpan={7} />
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {accountGroup.totalLeads > 0
                                            ? formatNumber(accountGroup.totalLeads)
                                            : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {formatCurrency(accountGroup.totalSpend)}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {accountGroup.totalLeads > 0
                                            ? formatCurrency(
                                                accountGroup.totalSpend / accountGroup.totalLeads
                                              )
                                            : "—"}
                                        </td>
                                        <td colSpan={isAutowebinarMode ? 7 : 4} />
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {accountGroup.totalClicks > 0
                                            ? formatCurrency(
                                                accountGroup.totalSpend / accountGroup.totalClicks
                                              )
                                            : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          {accountGroup.totalImpressions > 0
                                            ? formatCurrency(
                                                (accountGroup.totalSpend /
                                                  accountGroup.totalImpressions) *
                                                  1000
                                              )
                                            : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                                          —
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                )}

                                {/* Campaign rows (shown if account expanded or single account) */}
                                {(!showAccountRow || accountExpanded) &&
                                  accountGroup.campaigns.map((campaign) => {
                                    const spend = parseFloat(campaign.spend) || 0;
                                    const leads = parseInt(campaign.leads) || 0;
                                    const cpl = leads > 0 ? spend / leads : 0;
                                    const purchaseVal = parseFloat(campaign.purchaseValue) || 0;
                                    const campaignRoas = parseFloat(campaign.purchaseRoas) || 0;
                                    const isExpanded = expandedCampaigns.has(campaign.campaignId);

                                    return (
                                      <CampaignRowGroup
                                        key={campaign.campaignId}
                                        campaign={campaign}
                                        spend={spend}
                                        leads={leads}
                                        cpl={cpl}
                                        campaignRoas={campaignRoas}
                                        purchaseValue={purchaseVal}
                                        isExpanded={isExpanded}
                                        onToggle={() => toggleCampaign(campaign.campaignId)}
                                        datePreset={datePreset}
                                        customRange={customRange}
                                        adAccountId={adAccountId}
                                        isEcom={isEcom}
                                        isSaasMode={isSaasMode}
                                        isAutowebinarMode={isAutowebinarMode}
                                        utmData={utmByCampaign.get(campaign.campaignId)}
                                        rates={costRates}
                                        utmAttribution={utmAttribution}
                                        adUtmAttribution={adUtmAttribution}
                                        leadsByAdSet={leadsByAdSet}
                                        leadsByAd={leadsByAd}
                                        ecomRevenue={ecomRevenue}
                                        metricsV2On={metricsV2On}
                                        webinarStats={webinarStats}
                                        onShowLeadDetails={
                                          previewEnabled ? undefined : setLeadDetailPopup
                                        }
                                        forClientUserId={forClientUserId}
                                        googleAdsAdGroups={
                                          campaign._source === "google"
                                            ? googleAdsAdGroups
                                            : undefined
                                        }
                                        previewEnabled={previewEnabled}
                                        sortKey={sortKey}
                                        sortDir={sortDir}
                                      />
                                    );
                                  })}
                              </Fragment>
                            );
                          })}
                      </Fragment>
                    );
                  })
                : /* ─── Flat view (single platform, single account) ─── */
                  channelFilteredCampaigns.map((campaign) => {
                    const spend = parseFloat(campaign.spend) || 0;
                    const leads = parseInt(campaign.leads) || 0;
                    const cpl = leads > 0 ? spend / leads : 0;
                    const purchaseVal = parseFloat(campaign.purchaseValue) || 0;
                    const campaignRoas = parseFloat(campaign.purchaseRoas) || 0;
                    const isExpanded = expandedCampaigns.has(campaign.campaignId);

                    return (
                      <CampaignRowGroup
                        key={campaign.campaignId}
                        campaign={campaign}
                        spend={spend}
                        leads={leads}
                        cpl={cpl}
                        campaignRoas={campaignRoas}
                        purchaseValue={purchaseVal}
                        isExpanded={isExpanded}
                        onToggle={() => toggleCampaign(campaign.campaignId)}
                        datePreset={datePreset}
                        customRange={customRange}
                        adAccountId={adAccountId}
                        isEcom={isEcom}
                        isSaasMode={isSaasMode}
                        isAutowebinarMode={isAutowebinarMode}
                        utmData={utmByCampaign.get(campaign.campaignId)}
                        rates={costRates}
                        utmAttribution={utmAttribution}
                        adUtmAttribution={adUtmAttribution}
                        leadsByAdSet={leadsByAdSet}
                        leadsByAd={leadsByAd}
                        ecomRevenue={ecomRevenue}
                        metricsV2On={metricsV2On}
                        webinarStats={webinarStats}
                        onShowLeadDetails={previewEnabled ? undefined : setLeadDetailPopup}
                        forClientUserId={forClientUserId}
                        googleAdsAdGroups={
                          campaign._source === "google" ? googleAdsAdGroups : undefined
                        }
                        previewEnabled={previewEnabled}
                        sortKey={sortKey}
                        sortDir={sortDir}
                      />
                    );
                  })}
              {/* Organic source rows */}
              {showOrganic &&
                organicBySource.length > 0 &&
                (() => {
                  // Aggregate totals across all organic sources for the parent "Organic" row
                  const organicTotal = organicBySource.reduce(
                    (acc, g) => ({
                      leads: acc.leads + g.leads,
                      bookedCalls: acc.bookedCalls + g.bookedCalls,
                      showed: acc.showed + g.showed,
                      noShow: acc.noShow + g.noShow,
                      won: acc.won + g.won,
                      revenue: acc.revenue + g.revenue,
                    }),
                    { leads: 0, bookedCalls: 0, showed: 0, noShow: 0, won: 0, revenue: 0 }
                  );
                  const organicParentKey = "organic_parent";
                  const organicParentExpanded = expandedCampaigns.has(organicParentKey);
                  const organicShowDenom = organicTotal.showed + organicTotal.noShow;

                  return (
                    <>
                      {/* Separator */}
                      {channelFilteredCampaigns.length > 0 && (
                        <tr className="border-b border-border">
                          <td colSpan={20} className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className="h-px flex-1 bg-emerald-500/20" />
                              <span className="text-[10px] font-semibold text-emerald-500/60 uppercase tracking-wider">
                                Organic / UTM
                              </span>
                              <div className="h-px flex-1 bg-emerald-500/20" />
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Organic parent row — campaign level */}
                      <tr
                        className="border-b border-border hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => toggleCampaign(organicParentKey)}
                      >
                        <td className="px-2 py-3 text-center">
                          {organicParentExpanded ? (
                            <ChevronDown className="w-4 h-4 text-emerald-500 inline" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground inline" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <Layers className="w-4 h-4 text-emerald-400" />
                            <span>Organic</span>
                          </span>
                        </td>
                        {isSaasMode ? (
                          <>
                            <td className="px-4 py-3 text-sm text-foreground font-medium text-right">
                              {organicTotal.leads}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td
                              className={`px-4 py-3 text-sm text-right font-medium ${organicTotal.won > 0 ? "text-emerald-400" : "text-muted-foreground"}`}
                            >
                              {organicTotal.won > 0 ? formatNumber(organicTotal.won) : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                          </>
                        ) : isEcom ? (
                          <>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              {organicTotal.revenue > 0 ? (
                                <span className="text-emerald-400">
                                  {formatCurrency(organicTotal.revenue)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 text-sm text-foreground font-medium text-right">
                              {organicTotal.leads}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            {isAutowebinarMode && (
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                            )}
                            {isAutowebinarMode && (
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                            )}
                            {isAutowebinarMode && (
                              <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                                —
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm text-foreground text-right">
                              {organicTotal.bookedCalls > 0
                                ? formatNumber(organicTotal.bookedCalls)
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground text-right">
                              {organicShowDenom > 0
                                ? formatPercent((organicTotal.showed / organicShowDenom) * 100)
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              {organicTotal.won > 0 ? (
                                <span className="text-emerald-400">
                                  {formatNumber(organicTotal.won)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground text-right">
                              —
                            </td>
                          </>
                        )}
                      </tr>

                      {/* Source rows — adset level, only when parent expanded */}
                      {organicParentExpanded &&
                        organicBySource.map((sourceGroup) => {
                          const sourceExpandKey = `organic_source_${sourceGroup.utmSource}`;
                          const sourceExpanded = expandedCampaigns.has(sourceExpandKey);
                          const sourceInfo = getSourceInfo(sourceGroup.utmSource);
                          const sourceShowDenom = sourceGroup.showed + sourceGroup.noShow;

                          return (
                            <Fragment key={sourceExpandKey}>
                              <tr
                                className="border-b border-border bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={() => toggleCampaign(sourceExpandKey)}
                              >
                                <td className="px-2 py-2.5 text-center">
                                  {sourceExpanded ? (
                                    <ChevronDown className="w-3.5 h-3.5 text-emerald-400 inline" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground inline" />
                                  )}
                                </td>
                                <td
                                  className="pl-8 pr-4 py-2.5 text-sm text-foreground font-medium max-w-[280px] truncate"
                                  title={sourceInfo.label}
                                >
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className="text-muted-foreground">↳</span>
                                    {sourceInfo.icon}
                                    <span className="truncate">{sourceInfo.label}</span>
                                  </span>
                                </td>
                                {isSaasMode ? (
                                  <>
                                    <td className="px-4 py-2.5 text-sm text-foreground text-right">
                                      {sourceGroup.leads}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td
                                      className={`px-4 py-2.5 text-sm text-right ${sourceGroup.won > 0 ? "text-emerald-400" : "text-muted-foreground"}`}
                                    >
                                      {sourceGroup.won > 0 ? formatNumber(sourceGroup.won) : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                  </>
                                ) : isEcom ? (
                                  <>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-right">
                                      {sourceGroup.revenue > 0 ? (
                                        <span className="text-emerald-400">
                                          {formatCurrency(sourceGroup.revenue)}
                                        </span>
                                      ) : (
                                        "—"
                                      )}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td className="px-4 py-2.5 text-sm text-right">
                                      <span className="inline-flex items-center gap-1 justify-end">
                                        <span className="text-foreground">{sourceGroup.leads}</span>
                                        {!previewEnabled && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setLeadDetailPopup({
                                                type: "organic",
                                                id: sourceGroup.utmSource,
                                                name: sourceInfo.label,
                                                view: "leads",
                                                utmSource: sourceGroup.utmSource,
                                              });
                                            }}
                                            className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                          </button>
                                        )}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    {isAutowebinarMode && (
                                      <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                        —
                                      </td>
                                    )}
                                    {isAutowebinarMode && (
                                      <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                        —
                                      </td>
                                    )}
                                    {isAutowebinarMode && (
                                      <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                        —
                                      </td>
                                    )}
                                    <td className="px-4 py-2.5 text-sm text-right">
                                      <span className="inline-flex items-center gap-1 justify-end">
                                        <span className="text-muted-foreground">
                                          {sourceGroup.bookedCalls > 0
                                            ? formatNumber(sourceGroup.bookedCalls)
                                            : "—"}
                                        </span>
                                        {!previewEnabled && sourceGroup.bookedCalls > 0 && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setLeadDetailPopup({
                                                type: "organic",
                                                id: sourceGroup.utmSource,
                                                name: sourceInfo.label,
                                                view: "rozmowy",
                                                utmSource: sourceGroup.utmSource,
                                              });
                                            }}
                                            className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                          </button>
                                        )}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      {sourceShowDenom > 0
                                        ? formatPercent(
                                            (sourceGroup.showed / sourceShowDenom) * 100
                                          )
                                        : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-right">
                                      <span className="inline-flex items-center gap-1 justify-end flex-wrap">
                                        <span
                                          className={
                                            sourceGroup.won > 0
                                              ? "text-emerald-400"
                                              : "text-muted-foreground"
                                          }
                                        >
                                          {sourceGroup.won > 0
                                            ? formatNumber(sourceGroup.won)
                                            : "—"}
                                        </span>
                                        {sourceGroup.won > 0 && sourceGroup.revenue === 0 && (
                                          <span
                                            title={t("dashboard.table.noSalesValueWarning")}
                                            className="text-amber-400/70 text-[10px] cursor-help"
                                          >
                                            ⚠
                                          </span>
                                        )}
                                        {!previewEnabled && sourceGroup.won > 0 && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setLeadDetailPopup({
                                                type: "organic",
                                                id: sourceGroup.utmSource,
                                                name: sourceInfo.label,
                                                view: "sprzedaze",
                                                utmSource: sourceGroup.utmSource,
                                              });
                                            }}
                                            className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                          </button>
                                        )}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                      —
                                    </td>
                                  </>
                                )}
                              </tr>

                              {sourceExpanded &&
                                sourceGroup.mediums.map((mediumGroup) => {
                                  const mediumExpandKey = `organic_medium_${sourceGroup.utmSource}_${mediumGroup.utmMedium || "organic"}`;
                                  const mediumExpanded = expandedCampaigns.has(mediumExpandKey);
                                  const mediumShowDenom = mediumGroup.showed + mediumGroup.noShow;
                                  const mediumLabel = mediumGroup.utmMedium || "organic";

                                  return (
                                    <Fragment key={mediumExpandKey}>
                                      <tr
                                        className="border-b border-border bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                                        onClick={() => toggleCampaign(mediumExpandKey)}
                                      >
                                        <td className="px-2 py-2.5 text-center">
                                          {mediumExpanded ? (
                                            <ChevronDown className="w-3.5 h-3.5 text-emerald-400 inline" />
                                          ) : (
                                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground inline" />
                                          )}
                                        </td>
                                        <td
                                          className="pl-14 pr-4 py-2.5 text-sm text-foreground max-w-[280px] truncate"
                                          title={mediumLabel}
                                        >
                                          <span className="inline-flex items-center gap-1.5">
                                            <span className="text-muted-foreground">↳</span>
                                            <span className="truncate">{mediumLabel}</span>
                                          </span>
                                        </td>
                                        {isSaasMode ? (
                                          <>
                                            <td className="px-4 py-2.5 text-sm text-foreground text-right">
                                              {mediumGroup.leads}
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td
                                              className={`px-4 py-2.5 text-sm text-right ${mediumGroup.won > 0 ? "text-emerald-400" : "text-muted-foreground"}`}
                                            >
                                              {mediumGroup.won > 0
                                                ? formatNumber(mediumGroup.won)
                                                : "—"}
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                          </>
                                        ) : isEcom ? (
                                          <>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-right">
                                              {mediumGroup.revenue > 0 ? (
                                                <span className="text-emerald-400">
                                                  {formatCurrency(mediumGroup.revenue)}
                                                </span>
                                              ) : (
                                                "—"
                                              )}
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                          </>
                                        ) : (
                                          <>
                                            <td className="px-4 py-2.5 text-sm text-right">
                                              <span className="inline-flex items-center gap-1 justify-end">
                                                <span className="text-foreground">
                                                  {mediumGroup.leads}
                                                </span>
                                                {!previewEnabled && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setLeadDetailPopup({
                                                        type: "organic",
                                                        id: mediumExpandKey,
                                                        name: `${sourceInfo.label} / ${mediumLabel}`,
                                                        view: "leads",
                                                        utmSource: sourceGroup.utmSource,
                                                        utmMedium:
                                                          mediumGroup.utmMedium || "organic",
                                                      });
                                                    }}
                                                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                                    title={t("dashboard.table.showLeads")}
                                                  >
                                                    <ExternalLink className="w-3 h-3" />
                                                  </button>
                                                )}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            {isAutowebinarMode && (
                                              <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                                —
                                              </td>
                                            )}
                                            {isAutowebinarMode && (
                                              <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                                —
                                              </td>
                                            )}
                                            {isAutowebinarMode && (
                                              <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                                —
                                              </td>
                                            )}
                                            <td className="px-4 py-2.5 text-sm text-right">
                                              <span className="inline-flex items-center gap-1 justify-end">
                                                <span className="text-muted-foreground">
                                                  {mediumGroup.bookedCalls > 0
                                                    ? formatNumber(mediumGroup.bookedCalls)
                                                    : "—"}
                                                </span>
                                                {!previewEnabled && mediumGroup.bookedCalls > 0 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setLeadDetailPopup({
                                                        type: "organic",
                                                        id: mediumExpandKey,
                                                        name: `${sourceInfo.label} / ${mediumLabel}`,
                                                        view: "rozmowy",
                                                        utmSource: sourceGroup.utmSource,
                                                        utmMedium:
                                                          mediumGroup.utmMedium || "organic",
                                                      });
                                                    }}
                                                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                                    title={t("dashboard.table.showCalls")}
                                                  >
                                                    <ExternalLink className="w-3 h-3" />
                                                  </button>
                                                )}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              {mediumShowDenom > 0
                                                ? formatPercent(
                                                    (mediumGroup.showed / mediumShowDenom) * 100
                                                  )
                                                : "—"}
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-right">
                                              <span className="inline-flex items-center gap-1 justify-end">
                                                <span
                                                  className={
                                                    mediumGroup.won > 0
                                                      ? "text-emerald-400"
                                                      : "text-muted-foreground"
                                                  }
                                                >
                                                  {mediumGroup.won > 0
                                                    ? formatNumber(mediumGroup.won)
                                                    : "—"}
                                                </span>
                                                {!previewEnabled && mediumGroup.won > 0 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setLeadDetailPopup({
                                                        type: "organic",
                                                        id: mediumExpandKey,
                                                        name: `${sourceInfo.label} / ${mediumLabel}`,
                                                        view: "sprzedaze",
                                                        utmSource: sourceGroup.utmSource,
                                                        utmMedium:
                                                          mediumGroup.utmMedium || "organic",
                                                      });
                                                    }}
                                                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                                    title={t("dashboard.table.showSales")}
                                                  >
                                                    <ExternalLink className="w-3 h-3" />
                                                  </button>
                                                )}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-muted-foreground text-right">
                                              —
                                            </td>
                                          </>
                                        )}
                                      </tr>

                                      {mediumExpanded &&
                                        mediumGroup.items.map((item, idx) => {
                                          const iShowDenom = item.showed + item.noShow;
                                          return (
                                            <tr
                                              key={`organic_item_${sourceGroup.utmSource}_${mediumLabel}_${idx}`}
                                              className="border-b border-border bg-muted/30"
                                            >
                                              <td className="px-2 py-2"></td>
                                              <td
                                                className="pl-14 pr-4 py-2 text-sm text-muted-foreground max-w-[280px] truncate"
                                                title={item.label}
                                              >
                                                <span className="inline-flex items-center gap-1.5">
                                                  <span className="text-muted-foreground">↳</span>
                                                  <span className="truncate">{item.label}</span>
                                                </span>
                                              </td>
                                              {isSaasMode ? (
                                                <>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    {item.leads}
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td
                                                    className={`px-4 py-2 text-sm text-right ${item.won > 0 ? "text-emerald-400" : "text-muted-foreground"}`}
                                                  >
                                                    {item.won > 0 ? formatNumber(item.won) : "—"}
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                </>
                                              ) : isEcom ? (
                                                <>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-right">
                                                    {item.revenue > 0 ? (
                                                      <span className="text-emerald-400">
                                                        {formatCurrency(item.revenue)}
                                                      </span>
                                                    ) : (
                                                      "—"
                                                    )}
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                </>
                                              ) : (
                                                <>
                                                  <td className="px-4 py-2 text-sm text-right">
                                                    <span className="inline-flex items-center gap-1 justify-end">
                                                      <span className="text-muted-foreground">
                                                        {item.leads}
                                                      </span>
                                                      {!previewEnabled && (
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setLeadDetailPopup({
                                                              type: "organic",
                                                              id: `${sourceGroup.utmSource}_${mediumLabel}_${item.label}`,
                                                              name: item.label,
                                                              view: "leads",
                                                              utmSource: sourceGroup.utmSource,
                                                              utmMedium:
                                                                mediumGroup.utmMedium || "organic",
                                                              utmCampaign:
                                                                item.label !== "[brak miejsca]"
                                                                  ? item.label
                                                                  : undefined,
                                                            });
                                                          }}
                                                          className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                                          title={t("dashboard.table.showLeads")}
                                                        >
                                                          <ExternalLink className="w-3 h-3" />
                                                        </button>
                                                      )}
                                                    </span>
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  {isAutowebinarMode && (
                                                    <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                      —
                                                    </td>
                                                  )}
                                                  {isAutowebinarMode && (
                                                    <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                      —
                                                    </td>
                                                  )}
                                                  {isAutowebinarMode && (
                                                    <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                      —
                                                    </td>
                                                  )}
                                                  <td className="px-4 py-2 text-sm text-right">
                                                    <span className="inline-flex items-center gap-1 justify-end">
                                                      <span className="text-muted-foreground">
                                                        {item.bookedCalls > 0
                                                          ? formatNumber(item.bookedCalls)
                                                          : "—"}
                                                      </span>
                                                      {!previewEnabled && item.bookedCalls > 0 && (
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setLeadDetailPopup({
                                                              type: "organic",
                                                              id: `${sourceGroup.utmSource}_${mediumLabel}_${item.label}`,
                                                              name: item.label,
                                                              view: "rozmowy",
                                                              utmSource: sourceGroup.utmSource,
                                                              utmMedium:
                                                                mediumGroup.utmMedium || "organic",
                                                              utmCampaign:
                                                                item.label !== "[brak miejsca]"
                                                                  ? item.label
                                                                  : undefined,
                                                            });
                                                          }}
                                                          className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                                          title={t("dashboard.table.showCalls")}
                                                        >
                                                          <ExternalLink className="w-3 h-3" />
                                                        </button>
                                                      )}
                                                    </span>
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    {iShowDenom > 0
                                                      ? formatPercent(
                                                          (item.showed / iShowDenom) * 100
                                                        )
                                                      : "—"}
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-right">
                                                    <span className="inline-flex items-center gap-1 justify-end">
                                                      <span
                                                        className={
                                                          item.won > 0
                                                            ? "text-emerald-400"
                                                            : "text-muted-foreground"
                                                        }
                                                      >
                                                        {item.won > 0
                                                          ? formatNumber(item.won)
                                                          : "—"}
                                                      </span>
                                                      {!previewEnabled && item.won > 0 && (
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setLeadDetailPopup({
                                                              type: "organic",
                                                              id: `${sourceGroup.utmSource}_${mediumLabel}_${item.label}`,
                                                              name: item.label,
                                                              view: "sprzedaze",
                                                              utmSource: sourceGroup.utmSource,
                                                              utmMedium:
                                                                mediumGroup.utmMedium || "organic",
                                                              utmCampaign:
                                                                item.label !== "[brak miejsca]"
                                                                  ? item.label
                                                                  : undefined,
                                                            });
                                                          }}
                                                          className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-emerald-400 transition-colors"
                                                          title={t("dashboard.table.showSales")}
                                                        >
                                                          <ExternalLink className="w-3 h-3" />
                                                        </button>
                                                      )}
                                                    </span>
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                  <td className="px-4 py-2 text-sm text-muted-foreground text-right">
                                                    —
                                                  </td>
                                                </>
                                              )}
                                            </tr>
                                          );
                                        })}
                                    </Fragment>
                                  );
                                })}
                            </Fragment>
                          );
                        })}
                    </>
                  );
                })()}
              {/* Reconciliation row: header SUMA leads/sales (integration totals) minus what the
                paid + organic rows display. Without it, leads/sales counted in the header but not
                attributed to any campaign/source row silently vanish (e.g. contacts with no Meta
                ID / out-of-window). Leads + sales only — spend/calls/ROAS carry no meaning for an
                unattributed bucket. Gated to channelFilter==='all' so it never shows an
                unreconciled remainder under a narrower filter. */}
              {!isEcom &&
                !isSaasMode &&
                totals &&
                channelFilter === "all" &&
                (() => {
                  const paidLeadsSum = channelFilteredCampaigns.reduce(
                    (s, c) => s + (utmByCampaign.get(c.campaignId)?.leads || 0),
                    0
                  );
                  const organicLeadsSum = organicBySource.reduce((s, g) => s + (g.leads || 0), 0);
                  const unattributedLeads = (totals.leads || 0) - paidLeadsSum - organicLeadsSum;
                  const paidWonSum = channelFilteredCampaigns.reduce(
                    (s, c) => s + (utmByCampaign.get(c.campaignId)?.won || 0),
                    0
                  );
                  const organicWonSum = organicBySource.reduce((s, g) => s + (g.won || 0), 0);
                  const unattributedSales = (totals.sales || 0) - paidWonSum - organicWonSum;
                  if (unattributedLeads <= 0 && unattributedSales <= 0) {
                    // Świadomy diagnostyk, nie debug: nadmiarowa atrybucja nie ma jak pokazać się
                    // w UI (wiersz „Nieprzypisane" renderuje się tylko dla dodatniej reszty), a to
                    // realny sygnał double-countu — patrz docs/DASHBOARD_DATA_FLOW.md §9.
                    if (unattributedSales < 0)
                      // eslint-disable-next-line no-console
                      console.warn(
                        `[Dashboard] campaign-table sales over-attributed by ${-unattributedSales} (paid ${paidWonSum} + organic ${organicWonSum} > total ${totals.sales}) — possible double-count`
                      );
                    return null;
                  }
                  return (
                    <tr
                      className="border-b border-border"
                      title="Leady/sprzedaże policzone w sumie (integracja), ale bez przypisania do kampanii/źródła (np. brak Meta ID / UTM)."
                    >
                      <td className="px-2 py-3"></td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        Nieprzypisane / Direct
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground text-right">
                        {unattributedLeads > 0 ? formatNumber(unattributedLeads) : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      {isAutowebinarMode && (
                        <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      )}
                      {isAutowebinarMode && (
                        <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      )}
                      {isAutowebinarMode && (
                        <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      )}
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      <td className="px-4 py-3 text-sm text-emerald-400 text-right font-semibold">
                        {unattributedSales > 0 ? formatNumber(unattributedSales) : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground text-right">—</td>
                    </tr>
                  );
                })()}
              {channelFilteredCampaigns.length === 0 &&
                (!showOrganic || !organicSourceGroups || organicSourceGroups.length === 0) && (
                  <tr>
                    <td
                      colSpan={20}
                      className="px-4 py-8 text-center text-sm text-muted-foreground"
                    >
                      {channelFilter === "google"
                        ? t("dashboard.table.noDataGoogle")
                        : channelFilter === "organic"
                          ? t("dashboard.table.noDataOrganic")
                          : channelFilter === "other"
                            ? t("dashboard.table.noDataOther")
                            : t("dashboard.table.noData")}
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Lead Detail Popup (first-touch attribution) */}
      {leadDetailPopup &&
        (leadDetailPopup.view === "zamowienia" && leadDetailPopup.type !== "organic" ? (
          // VP-214: drill-down zamówień Shopify (numer/kwota/produkt) — inny modal niż leady GHL.
          <OrderDetailDialog
            open={!!leadDetailPopup}
            onClose={() => setLeadDetailPopup(null)}
            type={leadDetailPopup.type}
            id={leadDetailPopup.id}
            name={leadDetailPopup.name}
            datePreset={datePreset}
            customRange={customRange}
            forClientUserId={forClientUserId}
            parentAdSetId={leadDetailPopup.parentAdSetId}
            parentCampaignId={leadDetailPopup.parentCampaignId}
          />
        ) : (
          <LeadDetailDialog
            open={!!leadDetailPopup}
            onClose={() => setLeadDetailPopup(null)}
            type={leadDetailPopup.type}
            id={leadDetailPopup.id}
            name={leadDetailPopup.name}
            view={leadDetailPopup.view}
            datePreset={datePreset}
            customRange={customRange}
            utmSource={leadDetailPopup.utmSource}
            utmMedium={leadDetailPopup.utmMedium}
            utmCampaign={leadDetailPopup.utmCampaign}
            forClientUserId={forClientUserId}
            parentAdSetId={leadDetailPopup.parentAdSetId}
            parentCampaignId={leadDetailPopup.parentCampaignId}
            attributionModel={attributionModel}
          />
        ))}
    </>
  );
}

function buildAdPlatformUrl(
  source: "meta" | "google" | "tiktok",
  level: "campaign" | "adset" | "ad" | "adgroup",
  ids: {
    campaignId?: string;
    adSetId?: string;
    adId?: string;
    adGroupId?: string;
    accountId?: string;
  }
): string | null {
  if (source === "tiktok") {
    // TikTok Ads Manager deep link (campaign list for the advertiser).
    return ids.accountId
      ? `https://ads.tiktok.com/i18n/perf/creative?aadvid=${ids.accountId}`
      : "https://ads.tiktok.com/";
  }
  if (source === "meta") {
    const act = ids.accountId?.replace("act_", "") || "";
    const urlMap: Record<string, string> = {
      campaign: `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${act}&selected_campaign_ids=${ids.campaignId || ""}`,
      adset: `https://adsmanager.facebook.com/adsmanager/manage/adsets?act=${act}&selected_adset_ids=${ids.adSetId || ""}`,
      ad: `https://adsmanager.facebook.com/adsmanager/manage/ads?act=${act}&selected_ad_ids=${ids.adId || ""}`,
    };
    return urlMap[level] || null;
  }
  if (source === "google") {
    const acct = ids.accountId?.replace("gads_", "") || "";
    const cid = ids.campaignId?.replace("gads_", "") || "";
    const urlMap: Record<string, string> = {
      campaign: `https://ads.google.com/aw/campaigns?campaignId=${cid}&ocid=${acct}`,
      adgroup: `https://ads.google.com/aw/adgroups?campaignId=${cid}&adGroupId=${ids.adGroupId || ""}&ocid=${acct}`,
    };
    return urlMap[level] || null;
  }
  return null;
}

function AdPlatformLink({ url }: { url: string | null }) {
  const { t } = useTranslation();
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="flex-shrink-0 p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
      title={t("dashboard.table.openAdPanel")}
    >
      <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function CampaignRowGroup({
  campaign,
  spend,
  leads,
  cpl,
  campaignRoas,
  purchaseValue,
  isExpanded,
  onToggle,
  datePreset,
  customRange,
  adAccountId,
  isEcom,
  isSaasMode,
  isAutowebinarMode,
  utmData,
  utmAttribution,
  adUtmAttribution,
  leadsByAdSet,
  leadsByAd,
  ecomRevenue,
  metricsV2On = false,
  webinarStats,
  onShowLeadDetails,
  forClientUserId,
  googleAdsAdGroups,
  previewEnabled = false,
  sortKey,
  sortDir,
  rates,
}: {
  campaign: CampaignInsight;
  spend: number;
  leads: number;
  cpl: number;
  campaignRoas: number;
  purchaseValue: number;
  isExpanded: boolean;
  onToggle: () => void;
  datePreset: string;
  customRange?: { since: string; until: string };
  adAccountId: string;
  isEcom?: boolean;
  isSaasMode?: boolean;
  isAutowebinarMode?: boolean;
  utmData?: {
    bookedCalls: number;
    showed: number;
    noShow: number;
    won: number;
    revenue: number;
    leads: number;
    newLeads: number;
  };
  rates?: VariableRatesSummary;
  utmAttribution?: UtmAttributionRow[];
  adUtmAttribution?: AdUtmData;
  leadsByAdSet?: Record<string, number>;
  leadsByAd?: Record<string, number>;
  ecomRevenue?: EcomRevenueMaps;
  metricsV2On?: boolean;
  webinarStats?: WebinarStatsData;
  onShowLeadDetails?: (info: {
    type: "campaign" | "adset" | "ad";
    id: string;
    name: string;
    view?: "leads" | "nowe-leady" | "rozmowy" | "sprzedaze" | "live" | "zamowienia";
    parentAdSetId?: string;
    parentCampaignId?: string;
  }) => void;
  forClientUserId?: string;
  googleAdsAdGroups?: any[];
  previewEnabled?: boolean;
  sortKey?: string | null;
  sortDir?: "asc" | "desc";
}) {
  const { t } = useTranslation();
  const requestBody = useMemo(() => {
    const body: any = { datePreset, campaignId: campaign.campaignId };
    if (adAccountId) body.adAccountId = adAccountId;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    if (customRange) body.customRange = customRange;
    return body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, campaign.campaignId, adAccountId, customRange]);

  const isGoogleAds = campaign._source === "google";
  const isCampaignActive = campaign.status === "ACTIVE";

  // Webinar stats for this campaign (try ID first, then name fallback)
  const campaignWebinar =
    webinarStats?.byCampaignId?.[campaign.campaignId] ??
    webinarStats?.byCampaignName?.[campaign.campaignName];
  const webinarLive = campaignWebinar?.attended ?? 0;
  const webinarCostPerLive = webinarLive > 0 ? spend / webinarLive : 0;
  const webinarShowRateVal = leads > 0 ? (webinarLive / leads) * 100 : 0;

  // For Google Ads: filter ad groups belonging to this campaign from pre-fetched data
  const googleAdGroupsForCampaign = useMemo(() => {
    if (!isGoogleAds || !googleAdsAdGroups) return [];
    const rawCampaignId = campaign.campaignId.replace("gads_", "");
    return googleAdsAdGroups.filter((ag: any) => String(ag.campaignId) === rawCampaignId);
  }, [isGoogleAds, googleAdsAdGroups, campaign.campaignId]);

  const {
    data: adSetsData,
    isLoading: adSetsLoading,
    error: adSetsError,
  } = useQuery<{ adSets: AdSetInsight[] }>({
    queryKey: ["/api/dashboard/meta-ads/adsets", requestBody],
    queryFn: () => apiRequest("POST", "/api/dashboard/meta-ads/adsets", requestBody),
    enabled: isExpanded && !isGoogleAds && !previewEnabled,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Mirror the parent table's active sort onto the ad sets so an expanded campaign's
  // sub-rows reorder together with the header. Native columns use the ad set's own
  // Meta fields; conversion columns use per-ad-set integration attribution (same
  // semantics the cells render), so sorting matches what the user sees.
  const sortedAdSets = useMemo(() => {
    const list = adSetsData?.adSets ?? [];
    if (!sortKey) return list;
    const getVal = (a: AdSetInsight): number => {
      const aSpend = parseFloat(a.spend) || 0;
      const utm = utmAttribution?.find((r) => r.metaAdSetId === a.adSetId);
      switch (sortKey) {
        case "spend":
          return aSpend;
        case "cpc":
          return parseFloat(a.cpc) || 0;
        case "cpm":
          return parseFloat(a.cpm) || 0;
        case "leads":
          return metricsV2On ? (leadsByAdSet?.[a.adSetId] ?? 0) : utm?.leads || 0;
        case "newLeads":
          return utm?.newLeads || 0;
        case "cpl": {
          const l = metricsV2On ? (leadsByAdSet?.[a.adSetId] ?? 0) : utm?.leads || 0;
          return l > 0 ? aSpend / l : 0;
        }
        case "rozmowy":
          return utm?.bookedCalls || 0;
        case "costPerRozmowa":
          return (utm?.bookedCalls || 0) > 0 ? aSpend / utm!.bookedCalls : 0;
        case "showRate": {
          if (!utm) return 0;
          const d = utm.showed + utm.noShow;
          return d > 0 ? (utm.showed / d) * 100 : 0;
        }
        case "sales":
          return utm?.won || 0;
        case "cv":
          return utm?.revenue || 0;
        case "roas":
          return (utm?.revenue || 0) > 0 && aSpend > 0 ? utm!.revenue / aSpend : 0;
        default:
          return 0;
      }
    };
    return [...list].sort((a, b) => {
      const va = getVal(a),
        vb = getVal(b);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [adSetsData?.adSets, sortKey, sortDir, utmAttribution, metricsV2On, leadsByAdSet]);

  return (
    <>
      <tr
        className={`border-b border-border transition-colors ${previewEnabled ? "cursor-default" : "cursor-pointer hover:bg-muted/50"} ${!isCampaignActive ? "opacity-50" : ""}`}
        onClick={() => {
          if (!previewEnabled) onToggle();
        }}
      >
        <td className="px-2 py-3 text-center">
          {previewEnabled ? (
            <span className="text-[10px] uppercase tracking-wide text-white/30">preview</span>
          ) : isExpanded ? (
            <ChevronDown className="w-4 h-4 text-[#ef3e29] inline" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground inline" />
          )}
        </td>
        <td
          className="px-4 py-3 text-sm text-foreground max-w-[280px] truncate font-medium"
          title={campaign.campaignName}
        >
          <span className="inline-flex items-center gap-1.5">
            {campaign._source === "google" ? (
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 flex-shrink-0" fill="none">
                <path
                  d="M3.272 13.446l6.104-10.594c.95-1.647 3.059-2.21 4.706-1.26l.002.001c1.647.95 2.21 3.059 1.26 4.706L9.24 16.893c-.95 1.647-3.059 2.21-4.706 1.26h-.001c-1.647-.95-2.21-3.059-1.26-4.706z"
                  fill="#FBBC04"
                />
                <circle cx="6" cy="18.5" r="3.5" fill="#34A853" />
              </svg>
            ) : campaign._source === "tiktok" ? (
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 flex-shrink-0" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            )}
            <span className="truncate">{campaign.campaignName}</span>
            {!isCampaignActive && campaign.status && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground flex-shrink-0 uppercase">
                {campaign.status === "PAUSED"
                  ? t("dashboard.table.campaignPaused")
                  : campaign.status}
              </span>
            )}
            <AdPlatformLink
              url={buildAdPlatformUrl(campaign._source || "meta", "campaign", {
                campaignId: campaign.campaignId,
                accountId: campaign._accountId || adAccountId,
              })}
            />
          </span>
        </td>
        {isSaasMode ? (
          (() => {
            const trials = parseInt(campaign.trials) || 0;
            const subs = parseInt(campaign.subscriptions) || 0;
            const cpTrial = trials > 0 ? spend / trials : 0;
            return (
              <>
                <td className="px-4 py-3 text-sm text-foreground font-medium text-right">
                  {trials > 0 ? formatNumber(trials) : "—"}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {formatCurrency(spend)}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {cpTrial > 0 ? formatCurrency(cpTrial) : "—"}
                </td>
                <td
                  className={`px-4 py-3 text-sm text-right font-medium ${subs > 0 ? "text-emerald-400" : "text-muted-foreground"}`}
                >
                  {subs > 0 ? formatNumber(subs) : "—"}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {trials > 0 && subs > 0 ? formatPercent((subs / trials) * 100) : "—"}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {parseFloat(campaign.cpc) > 0 ? formatCurrency(parseFloat(campaign.cpc)) : "—"}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {formatCurrency(parseFloat(campaign.cpm) || 0)}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">{`${(parseFloat(campaign.ctr) || 0).toFixed(2)}%`}</td>
              </>
            );
          })()
        ) : isEcom ? (
          // VP-214: kampania na TYM SAMYM źródle co adsety/reklamy — revenue (netto po
          // zwrotach) i sprzedaż z tracking_conversions (aggregatePurchaseRevenue), nigdy
          // z Meta-pixela. Dzięki temu kampania == suma adsetów, a modal zamówień schodzi
          // się z komórką. '—' gdy brak przypisanego revenue (lub metricsV2 OFF).
          (() => {
            const campaignRevenue = ecomRevenue?.revenueByCampaign?.[campaign.campaignId];
            const campaignSales = ecomRevenue?.salesByCampaign?.[campaign.campaignId];
            const campaignRoas =
              campaignRevenue != null && spend > 0 ? campaignRevenue / spend : null;
            const campaignCpa = campaignSales && campaignSales > 0 ? spend / campaignSales : null;
            // VP-213: Net Profit / Net ROAS = revenue integracji − spend − koszty ZMIENNE.
            const rp = computeRowProfit({
              revenue: campaignRevenue,
              spend,
              purchases: campaignSales,
              campaignId: campaign.campaignId,
              rates,
            });
            // '—' z DWÓCH powodów: brak stawek (/rates się ładuje lub 403 dla członka
            // agencji) vs brak revenue z integracji — rozróżniamy w tooltipie (review VP-213).
            const tip = rp
              ? t("dashboard.table.netProfitTip", {
                  revenue: formatCurrency(campaignRevenue ?? 0),
                  spend: formatCurrency(spend),
                  costs: formatCurrency(rp.variableCosts),
                })
              : !rates
                ? t("dashboard.table.netProfitNoRates")
                : t("dashboard.table.netProfitNoRevenue");
            return (
              <>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {formatCurrency(spend)}
                </td>
                {/* Conversion Value + drill-down zamówień (numer/kwota/produkt). */}
                <td className="px-4 py-3 text-sm text-right">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <span className="text-foreground">
                      {campaignRevenue != null ? formatCurrency(campaignRevenue) : "—"}
                    </span>
                    {onShowLeadDetails && campaignRevenue != null && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onShowLeadDetails({
                            type: "campaign",
                            id: campaign.campaignId,
                            name: campaign.campaignName,
                            view: "zamowienia",
                          });
                        }}
                        className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                        title={t("dashboard.orderDialog.title")}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {parseFloat(campaign.cpc) > 0 ? formatCurrency(parseFloat(campaign.cpc)) : "-"}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {formatCurrency(parseFloat(campaign.cpm) || 0)}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {campaignCpa != null ? formatCurrency(campaignCpa) : "—"}
                </td>
                <td className="px-4 py-3 text-sm text-foreground text-right">
                  {campaignRoas != null ? `${campaignRoas.toFixed(2)}x` : "—"}
                </td>
                <td
                  className={`px-4 py-3 text-sm text-right font-medium ${rp ? (rp.netProfit >= 0 ? "text-emerald-400" : "text-red-400") : "text-muted-foreground"}`}
                  title={tip}
                >
                  {rp ? formatCurrency(rp.netProfit) : "—"}
                </td>
                <td
                  className={`px-4 py-3 text-sm text-right font-medium ${rp?.netRoas != null ? (rp.netRoas >= 0 ? "text-emerald-400" : "text-red-400") : "text-muted-foreground"}`}
                  title={tip}
                >
                  {rp?.netRoas != null ? `${rp.netRoas.toFixed(2)}x` : "—"}
                </td>
              </>
            );
          })()
        ) : (
          <>
            <td className="px-4 py-3 text-sm text-right">
              <span className="inline-flex items-center gap-1 justify-end">
                {/* Leads come from the INTEGRATION (GHL/CRM), never the ad platform — the ad
                    platform's lead count lies. '—' when no integration attribution for this campaign. */}
                <span className="text-foreground font-medium">
                  {utmData ? formatNumber(utmData.leads) : "—"}
                </span>
                {/* VP-134: bez leadów nie ma czego pokazać — strzałka przy '—' otwierała modal
                    „Brak leadów dla tego elementu", co wyglądało jak awaria (spójnie z gate'em
                    na wierszach organic). */}
                {onShowLeadDetails && (utmData?.leads ?? 0) > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLeadDetails({
                        type: "campaign",
                        id: campaign.campaignId,
                        name: campaign.campaignName,
                        view: "leads",
                      });
                    }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                    title={t("dashboard.table.showLeads")}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            </td>
            <td className="px-4 py-3 text-sm text-foreground text-right">
              {formatCurrency(spend)}
            </td>
            <td className="px-4 py-3 text-sm text-foreground text-right">
              {utmData?.leads ? formatCurrency(spend / utmData.leads) : "—"}
            </td>
            {isAutowebinarMode && (
              <td className="px-4 py-3 text-sm text-right">
                <span className="inline-flex items-center gap-1 justify-end">
                  <span className="text-foreground font-medium">
                    {webinarLive > 0 ? formatNumber(webinarLive) : "—"}
                  </span>
                  {onShowLeadDetails && webinarLive > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowLeadDetails({
                          type: "campaign",
                          id: campaign.campaignId,
                          name: campaign.campaignName,
                          view: "live",
                        });
                      }}
                      className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                      title={t("dashboard.table.showLive")}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  )}
                </span>
              </td>
            )}
            {isAutowebinarMode && (
              <td className="px-4 py-3 text-sm text-foreground text-right">
                {webinarCostPerLive > 0 ? formatCurrency(webinarCostPerLive) : "—"}
              </td>
            )}
            {isAutowebinarMode && (
              <td className="px-4 py-3 text-sm text-foreground text-right">
                {webinarShowRateVal > 0 ? formatPercent(webinarShowRateVal) : "—"}
              </td>
            )}
            <td className="px-4 py-3 text-sm text-right">
              <span className="inline-flex items-center gap-1 justify-end">
                <span className="text-foreground">
                  {utmData?.bookedCalls ? formatNumber(utmData.bookedCalls) : "—"}
                </span>
                {onShowLeadDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLeadDetails({
                        type: "campaign",
                        id: campaign.campaignId,
                        name: campaign.campaignName,
                        view: "rozmowy",
                      });
                    }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                    title={t("dashboard.table.showCalls")}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            </td>
            <td className="px-4 py-3 text-sm text-foreground text-right">
              {utmData?.bookedCalls ? formatCurrency(spend / utmData.bookedCalls) : "—"}
            </td>
            <td className="px-4 py-3 text-sm text-foreground text-right">
              {(() => {
                if (!utmData) return "—";
                const d = utmData.showed + utmData.noShow;
                return d > 0 ? formatPercent((utmData.showed / d) * 100) : "—";
              })()}
            </td>
            <td className="px-4 py-3 text-sm text-right">
              <span className="inline-flex items-center gap-1 justify-end">
                <span
                  className={
                    utmData?.won ? "text-emerald-400 font-medium" : "text-muted-foreground"
                  }
                >
                  {utmData?.won ? formatNumber(utmData.won) : "—"}
                </span>
                {onShowLeadDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLeadDetails({
                        type: "campaign",
                        id: campaign.campaignId,
                        name: campaign.campaignName,
                        view: "sprzedaze",
                      });
                    }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                    title={t("dashboard.table.showSales")}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            </td>
            <td className="px-4 py-3 text-sm text-foreground text-right">
              {parseFloat(campaign.cpc) > 0 ? formatCurrency(parseFloat(campaign.cpc)) : "-"}
            </td>
            <td className="px-4 py-3 text-sm text-foreground text-right">
              {formatCurrency(parseFloat(campaign.cpm) || 0)}
            </td>
            {(() => {
              // ROAS from INTEGRATION revenue (GHL won) ÷ ad spend — never the ad platform's ROAS.
              const ghlRoas = utmData?.revenue && spend > 0 ? utmData.revenue / spend : 0;
              return (
                <td
                  className={`px-4 py-3 text-sm text-right font-medium ${ghlRoas >= 1 ? "text-emerald-400" : ghlRoas > 0 ? "text-red-400" : "text-muted-foreground"}`}
                >
                  {ghlRoas > 0 ? `${ghlRoas.toFixed(2)}x` : "—"}
                </td>
              );
            })()}
          </>
        )}
      </tr>
      {isExpanded && (
        <>
          {isGoogleAds ? (
            /* Google Ads: show ad groups from pre-fetched data */
            googleAdGroupsForCampaign.length > 0 ? (
              googleAdGroupsForCampaign.map((ag: any) => {
                const agSpend = (ag.costMicros || 0) / 1_000_000;
                const agClicks = ag.clicks || 0;
                const agImpressions = ag.impressions || 0;
                const agConversionsValue = ag.conversionsValue || 0;
                const agConversions = ag.conversions || 0;
                return (
                  <tr
                    key={ag.adGroupId}
                    className="border-b border-border hover:bg-muted/40 transition-colors bg-muted/30"
                  >
                    <td className="px-2 py-2.5 text-center">
                      <span className="w-4 h-4 inline-block" />
                    </td>
                    <td
                      className="px-4 py-2.5 text-xs text-muted-foreground max-w-[260px] truncate pl-8"
                      title={ag.adGroupName}
                    >
                      <span className="inline-flex items-center gap-1">
                        <span className="text-muted-foreground mr-0.5">↳</span>
                        <span className="truncate">{ag.adGroupName}</span>
                        <AdPlatformLink
                          url={buildAdPlatformUrl("google", "adgroup", {
                            campaignId: campaign.campaignId,
                            adGroupId: ag.adGroupId,
                            accountId: campaign._accountId,
                          })}
                        />
                        {ag.status !== "ENABLED" && (
                          <span className="ml-0.5 text-[10px] text-muted-foreground uppercase">
                            {ag.status}
                          </span>
                        )}
                      </span>
                    </td>
                    {isEcom ? (
                      <>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {formatCurrency(agSpend)}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {agConversionsValue > 0 ? formatCurrency(agConversionsValue) : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {agClicks > 0 ? formatCurrency(agSpend / agClicks) : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {agImpressions > 0
                            ? formatCurrency((agSpend / agImpressions) * 1000)
                            : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {agConversions > 0 ? formatCurrency(agSpend / agConversions) : "—"}
                        </td>
                        <td
                          className={`px-4 py-2.5 text-xs text-right ${agSpend > 0 && agConversionsValue / agSpend >= 1 ? "text-emerald-400" : "text-muted-foreground"}`}
                        >
                          {agSpend > 0 ? `${(agConversionsValue / agSpend).toFixed(2)}x` : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {formatCurrency(agSpend)}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                        {isAutowebinarMode && (
                          <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                            —
                          </td>
                        )}
                        {isAutowebinarMode && (
                          <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                            —
                          </td>
                        )}
                        {isAutowebinarMode && (
                          <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                            —
                          </td>
                        )}
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {agClicks > 0 ? formatCurrency(agSpend / agClicks) : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                          {agImpressions > 0
                            ? formatCurrency((agSpend / agImpressions) * 1000)
                            : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
                      </>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={12} className="px-8 py-3 text-xs text-muted-foreground">
                  Brak grup reklam z danymi w tym okresie
                </td>
              </tr>
            )
          ) : /* Meta Ads: load ad sets on demand */
          adSetsLoading ? (
            <tr>
              <td colSpan={12} className="px-8 py-3">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </td>
            </tr>
          ) : adSetsError ? (
            <tr>
              <td colSpan={12} className="px-8 py-3 text-xs text-white/40">
                {t("dashboard.table.refreshAdsets")}
              </td>
            </tr>
          ) : sortedAdSets.length > 0 ? (
            sortedAdSets.map((adSet) => (
              <AdSetRowGroup
                key={adSet.adSetId}
                adSet={adSet}
                datePreset={datePreset}
                customRange={customRange}
                adAccountId={adAccountId}
                isEcom={isEcom}
                isSaasMode={isSaasMode}
                isAutowebinarMode={isAutowebinarMode}
                utmAttribution={utmAttribution}
                adUtmAttribution={adUtmAttribution}
                leadsByAdSet={leadsByAdSet}
                leadsByAd={leadsByAd}
                ecomRevenue={ecomRevenue}
                rates={rates}
                metricsV2On={metricsV2On}
                campaignId={campaign.campaignId}
                onShowLeadDetails={onShowLeadDetails}
                forClientUserId={forClientUserId}
                webinarStats={webinarStats}
              />
            ))
          ) : (
            <tr>
              <td colSpan={12} className="px-8 py-3 text-xs text-muted-foreground">
                {t("dashboard.table.noAdsets")}
              </td>
            </tr>
          )}
        </>
      )}
    </>
  );
}

function AdSetRowGroup({
  adSet,
  datePreset,
  customRange,
  adAccountId,
  isEcom,
  isSaasMode,
  isAutowebinarMode,
  utmAttribution,
  adUtmAttribution,
  leadsByAdSet,
  leadsByAd,
  ecomRevenue,
  rates,
  metricsV2On = false,
  campaignId,
  onShowLeadDetails,
  forClientUserId,
  webinarStats,
}: {
  adSet: AdSetInsight;
  datePreset: string;
  customRange?: { since: string; until: string };
  adAccountId: string;
  isEcom?: boolean;
  isSaasMode?: boolean;
  isAutowebinarMode?: boolean;
  utmAttribution?: UtmAttributionRow[];
  adUtmAttribution?: AdUtmData;
  leadsByAdSet?: Record<string, number>;
  leadsByAd?: Record<string, number>;
  ecomRevenue?: EcomRevenueMaps;
  rates?: VariableRatesSummary;
  metricsV2On?: boolean;
  campaignId?: string;
  onShowLeadDetails?: (info: {
    type: "campaign" | "adset" | "ad";
    id: string;
    name: string;
    view?: "leads" | "nowe-leady" | "rozmowy" | "sprzedaze" | "live" | "zamowienia";
    parentAdSetId?: string;
    parentCampaignId?: string;
  }) => void;
  forClientUserId?: string;
  webinarStats?: WebinarStatsData;
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewAd, setPreviewAd] = useState<{ adId: string; adName: string } | null>(null);
  const spend = parseFloat(adSet.spend) || 0;
  const impressions = parseInt(adSet.impressions) || 0;

  // Find UTM attribution data for this adset
  const utmData = useMemo(() => {
    if (!utmAttribution) return undefined;
    const row = utmAttribution.find((r) => r.metaAdSetId === adSet.adSetId);
    return row;
  }, [utmAttribution, adSet.adSetId]);

  // Webinar stats for this adset (try ID first, then name fallback)
  const adSetWebinar =
    webinarStats?.byAdSetId?.[adSet.adSetId] ?? webinarStats?.byAdSetName?.[adSet.adSetName];
  const adSetLeads = parseInt(adSet.leads as any) || 0;
  // LEADY adsetu: metricsV2 ON → realne rekordy (leadsByAdSet, spójne z nagłówkiem kampanii
  // i dialogiem); OFF → stary tor (utmData z CRM, fallback do liczby Meta z adSet.leads).
  const displayAdSetLeads = metricsV2On
    ? (leadsByAdSet?.[adSet.adSetId] ?? 0)
    : utmData
      ? utmData.leads
      : adSetLeads;
  const displayAdSetCpl = displayAdSetLeads > 0 ? spend / displayAdSetLeads : 0;
  const webinarLive = adSetWebinar?.attended ?? 0;
  const webinarCostPerLive = webinarLive > 0 ? spend / webinarLive : 0;
  const webinarShowRateVal = adSetLeads > 0 ? (webinarLive / adSetLeads) * 100 : 0;

  // VP-214: przychód (netto po zwrotach) + sprzedaż TEGO adsetu z rekordów (metricsV2 ON).
  // CPA/ROAS/Net Profit liczone z integracji, nie z Meta-pixela. '—' gdy brak danych.
  const adSetRevenue = ecomRevenue?.revenueByAdSet?.[adSet.adSetId];
  const adSetSales = ecomRevenue?.salesByAdSet?.[adSet.adSetId];
  const adSetRoas = adSetRevenue != null && spend > 0 ? adSetRevenue / spend : null;
  const adSetCpa = adSetSales && adSetSales > 0 ? spend / adSetSales : null;
  const adSetProfit = computeRowProfit({
    revenue: adSetRevenue,
    spend,
    purchases: adSetSales,
    campaignId: campaignId ?? adSet.adSetId,
    rates,
  });

  const requestBody = useMemo(() => {
    const body: any = { datePreset, adSetId: adSet.adSetId };
    if (adAccountId) body.adAccountId = adAccountId;
    if (customRange) body.customRange = customRange;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    return body;
  }, [datePreset, adSet.adSetId, adAccountId, customRange, forClientUserId]);

  const { data: adsData, isLoading: adsLoading } = useQuery<{ ads: AdInsight[] }>({
    queryKey: ["/api/dashboard/meta-ads/ads", requestBody],
    queryFn: () => apiRequest("POST", "/api/dashboard/meta-ads/ads", requestBody),
    enabled: isExpanded,
    staleTime: 1000 * 60 * 5,
  });

  return (
    <>
      <tr
        className="border-b border-border hover:bg-muted/40 transition-colors cursor-pointer bg-muted/30"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <td className="px-2 py-2.5 text-center">
          <span className="inline-flex items-center pl-3">
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-[#ef3e29]/70" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </span>
        </td>
        <td
          className="px-4 py-2.5 text-xs text-muted-foreground max-w-[260px] truncate pl-8"
          title={adSet.adSetName}
        >
          <span className="inline-flex items-center gap-1">
            <span className="text-muted-foreground mr-0.5">↳</span>
            <span className="truncate">{adSet.adSetName}</span>
            <AdPlatformLink
              url={buildAdPlatformUrl("meta", "adset", {
                adSetId: adSet.adSetId,
                accountId: adAccountId,
              })}
            />
          </span>
        </td>
        {isSaasMode ? (
          <>
            <td className="px-4 py-2.5 text-xs text-foreground text-right">{adSet.leads}</td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {formatCurrency(spend)}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {adSet.costPerLead > 0 ? formatCurrency(adSet.costPerLead) : "—"}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">—</td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {parseFloat(adSet.cpc) > 0 ? formatCurrency(parseFloat(adSet.cpc)) : "—"}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {formatCurrency(parseFloat(adSet.cpm) || 0)}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">{`${(parseFloat(adSet.ctr) || 0).toFixed(2)}%`}</td>
          </>
        ) : isEcom ? (
          <>
            {/* VP-214: Conversion Value / CPA / ROAS / Net Profit z revenue INTEGRACJI per adset
                (tracking_conversions, netto po zwrotach), nigdy z Meta-pixela. '—' gdy brak danych. */}
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {formatCurrency(spend)}
            </td>
            <td className="px-4 py-2.5 text-xs text-right">
              <span className="inline-flex items-center gap-1 justify-end">
                <span className="text-foreground">
                  {adSetRevenue != null ? formatCurrency(adSetRevenue) : "—"}
                </span>
                {onShowLeadDetails && adSetRevenue != null && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLeadDetails({
                        type: "adset",
                        id: adSet.adSetId,
                        name: adSet.adSetName,
                        view: "zamowienia",
                        parentCampaignId: campaignId,
                      });
                    }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                    title={t("dashboard.orderDialog.title")}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </span>
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {parseFloat(adSet.cpc) > 0 ? formatCurrency(parseFloat(adSet.cpc)) : "-"}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {formatCurrency(parseFloat(adSet.cpm) || 0)}
            </td>
            <td className="px-4 py-2.5 text-xs text-foreground text-right">
              {adSetCpa != null ? formatCurrency(adSetCpa) : "—"}
            </td>
            <td className="px-4 py-2.5 text-xs text-foreground text-right">
              {adSetRoas != null ? `${adSetRoas.toFixed(2)}x` : "—"}
            </td>
            <td
              className={`px-4 py-2.5 text-xs text-right ${adSetProfit ? (adSetProfit.netProfit >= 0 ? "text-emerald-400" : "text-red-400") : "text-muted-foreground"}`}
            >
              {adSetProfit ? formatCurrency(adSetProfit.netProfit) : "—"}
            </td>
            <td
              className={`px-4 py-2.5 text-xs text-right ${adSetProfit?.netRoas != null ? (adSetProfit.netRoas >= 0 ? "text-emerald-400" : "text-red-400") : "text-muted-foreground"}`}
            >
              {adSetProfit?.netRoas != null ? `${adSetProfit.netRoas.toFixed(2)}x` : "—"}
            </td>
          </>
        ) : (
          <>
            <td className="px-4 py-2.5 text-xs text-right">
              <span className="inline-flex items-center gap-1 justify-end">
                {/* Leady: metricsV2 ON → rekordy (leadsByAdSet); OFF → CRM (utmData) z fallbackiem do Meta. */}
                <span className="text-foreground">
                  {displayAdSetLeads > 0 ? formatNumber(displayAdSetLeads) : "—"}
                </span>
                {/* VP-134: drill-down tylko gdy jest co pokazać (patrz komórka kampanii). */}
                {onShowLeadDetails && displayAdSetLeads > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLeadDetails({
                        type: "adset",
                        id: adSet.adSetId,
                        name: adSet.adSetName,
                        view: "leads",
                        parentCampaignId: campaignId,
                      });
                    }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                    title={t("dashboard.table.showLeads")}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </span>
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {formatCurrency(spend)}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {displayAdSetCpl > 0 ? formatCurrency(displayAdSetCpl) : "—"}
            </td>
            {isAutowebinarMode && (
              <td className="px-4 py-2.5 text-xs text-right">
                <span className="inline-flex items-center gap-1 justify-end">
                  <span className="text-foreground font-medium">
                    {webinarLive > 0 ? formatNumber(webinarLive) : "—"}
                  </span>
                  {onShowLeadDetails && webinarLive > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowLeadDetails({
                          type: "adset",
                          id: adSet.adSetId,
                          name: adSet.adSetName,
                          view: "live",
                        });
                      }}
                      className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                      title={t("dashboard.table.showLive")}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                </span>
              </td>
            )}
            {isAutowebinarMode && (
              <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                {webinarCostPerLive > 0 ? formatCurrency(webinarCostPerLive) : "—"}
              </td>
            )}
            {isAutowebinarMode && (
              <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
                {webinarShowRateVal > 0 ? formatPercent(webinarShowRateVal) : "—"}
              </td>
            )}
            <td className="px-4 py-2.5 text-xs text-right">
              <span className="inline-flex items-center gap-1 justify-end">
                <span className="text-muted-foreground">
                  {utmData?.bookedCalls ? formatNumber(utmData.bookedCalls) : "—"}
                </span>
                {onShowLeadDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLeadDetails({
                        type: "adset",
                        id: adSet.adSetId,
                        name: adSet.adSetName,
                        view: "rozmowy",
                        parentCampaignId: campaignId,
                      });
                    }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                    title={t("dashboard.table.showCalls")}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </span>
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {utmData?.bookedCalls ? formatCurrency(spend / utmData.bookedCalls) : "—"}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {(() => {
                if (!utmData) return "—";
                const d = utmData.showed + utmData.noShow;
                return d > 0 ? formatPercent((utmData.showed / d) * 100) : "—";
              })()}
            </td>
            <td className="px-4 py-2.5 text-xs text-right">
              <span className="inline-flex items-center gap-1 justify-end">
                <span className={utmData?.won ? "text-emerald-400" : "text-muted-foreground"}>
                  {utmData?.won ? formatNumber(utmData.won) : "—"}
                </span>
                {onShowLeadDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLeadDetails({
                        type: "adset",
                        id: adSet.adSetId,
                        name: adSet.adSetName,
                        view: "sprzedaze",
                        parentCampaignId: campaignId,
                      });
                    }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                    title={t("dashboard.table.showSales")}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </span>
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {parseFloat(adSet.cpc) > 0 ? formatCurrency(parseFloat(adSet.cpc)) : "-"}
            </td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {formatCurrency(parseFloat(adSet.cpm) || 0)}
            </td>
            {/* ROAS = INTEGRATION revenue (GHL won) ÷ ad spend. */}
            <td className="px-4 py-2.5 text-xs text-muted-foreground text-right">
              {utmData?.revenue && spend > 0 ? `${(utmData.revenue / spend).toFixed(2)}x` : "—"}
            </td>
          </>
        )}
      </tr>
      {isExpanded && (
        <>
          {adsLoading ? (
            <tr>
              <td colSpan={12} className="px-12 py-2">
                <div className="space-y-2">
                  <Skeleton className="h-3.5 w-full" />
                  <Skeleton className="h-3.5 w-4/5" />
                </div>
              </td>
            </tr>
          ) : adsData?.ads && adsData.ads.length > 0 ? (
            adsData.ads.map((ad) => {
              const adSpend = parseFloat(ad.spend) || 0;
              const adUtm = adUtmAttribution?.[ad.adId];
              const adWebinar =
                webinarStats?.byAdId?.[ad.adId] ?? webinarStats?.byAdName?.[ad.adName];
              const adLeads = parseInt(ad.leads as any) || 0;
              // LEADY reklamy: metricsV2 ON → rekordy (leadsByAd po adId); OFF → stary tor (adUtm/Meta).
              const displayAdLeads = metricsV2On
                ? (leadsByAd?.[ad.adId] ?? 0)
                : adUtm
                  ? adUtm.leads
                  : adLeads;
              const displayAdCpl = displayAdLeads > 0 ? adSpend / displayAdLeads : 0;
              // VP-214: przychód (netto po zwrotach) + sprzedaż TEJ reklamy z rekordów (metricsV2 ON).
              const adRevenue = ecomRevenue?.revenueByAd?.[ad.adId];
              const adSales = ecomRevenue?.salesByAd?.[ad.adId];
              const adRoas = adRevenue != null && adSpend > 0 ? adRevenue / adSpend : null;
              const adCpa = adSales && adSales > 0 ? adSpend / adSales : null;
              const adProfit = computeRowProfit({
                revenue: adRevenue,
                spend: adSpend,
                purchases: adSales,
                campaignId: campaignId ?? adSet.adSetId,
                rates,
              });
              const adWebinarLive = adWebinar?.attended ?? 0;
              const adWebinarCostPerLive = adWebinarLive > 0 ? adSpend / adWebinarLive : 0;
              const adWebinarShowRate = adLeads > 0 ? (adWebinarLive / adLeads) * 100 : 0;
              return (
                <tr key={ad.adId} className="border-b border-border bg-muted/30">
                  <td className="px-2 py-2"></td>
                  <td
                    className="px-4 py-2 text-xs text-muted-foreground max-w-[240px] pl-14 overflow-hidden"
                    title={ad.adName}
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      <span className="text-muted-foreground mr-0.5 flex-shrink-0">↳</span>
                      <span className="truncate min-w-0 flex-1">{ad.adName}</span>
                      <AdPlatformLink
                        url={buildAdPlatformUrl("meta", "ad", {
                          adId: ad.adId,
                          accountId: adAccountId,
                        })}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewAd({ adId: ad.adId, adName: ad.adName });
                        }}
                        className="flex-shrink-0 p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                        title={t("dashboard.table.adPreview")}
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  {isSaasMode ? (
                    <>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {ad.leads}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {formatCurrency(adSpend)}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {ad.costPerLead > 0 ? formatCurrency(ad.costPerLead) : "—"}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">—</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">—</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {parseFloat(ad.cpc) > 0 ? formatCurrency(parseFloat(ad.cpc)) : "—"}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {formatCurrency(parseFloat(ad.cpm) || 0)}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">{`${(parseFloat(ad.ctr) || 0).toFixed(2)}%`}</td>
                    </>
                  ) : isEcom ? (
                    <>
                      {/* VP-214: Conversion Value / CPA / ROAS / Net Profit z revenue INTEGRACJI per
                          reklama (tracking_conversions, netto po zwrotach), nigdy z Meta-pixela. */}
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {formatCurrency(adSpend)}
                      </td>
                      <td className="px-4 py-2 text-xs text-right">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <span className="text-foreground">
                            {adRevenue != null ? formatCurrency(adRevenue) : "—"}
                          </span>
                          {onShowLeadDetails && adRevenue != null && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onShowLeadDetails({
                                  type: "ad",
                                  id: ad.adId,
                                  name: ad.adName,
                                  view: "zamowienia",
                                  parentAdSetId: adSet.adSetId,
                                  parentCampaignId: campaignId,
                                });
                              }}
                              className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                              title={t("dashboard.orderDialog.title")}
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {parseFloat(ad.cpc) > 0 ? formatCurrency(parseFloat(ad.cpc)) : "-"}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {formatCurrency(parseFloat(ad.cpm) || 0)}
                      </td>
                      <td className="px-4 py-2 text-xs text-foreground text-right">
                        {adCpa != null ? formatCurrency(adCpa) : "—"}
                      </td>
                      <td className="px-4 py-2 text-xs text-foreground text-right">
                        {adRoas != null ? `${adRoas.toFixed(2)}x` : "—"}
                      </td>
                      <td
                        className={`px-4 py-2 text-xs text-right ${adProfit ? (adProfit.netProfit >= 0 ? "text-emerald-400" : "text-red-400") : "text-muted-foreground"}`}
                      >
                        {adProfit ? formatCurrency(adProfit.netProfit) : "—"}
                      </td>
                      <td
                        className={`px-4 py-2 text-xs text-right ${adProfit?.netRoas != null ? (adProfit.netRoas >= 0 ? "text-emerald-400" : "text-red-400") : "text-muted-foreground"}`}
                      >
                        {adProfit?.netRoas != null ? `${adProfit.netRoas.toFixed(2)}x` : "—"}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2 text-xs text-right">
                        <span className="inline-flex items-center gap-1 justify-end">
                          {/* Leads: prefer CRM (adUtm per ad); fall back to Meta API when UTM has no
                              per-ad breakdown (metaAdId not tracked at ad level). */}
                          <span className="text-muted-foreground">
                            {displayAdLeads > 0 ? formatNumber(displayAdLeads) : "—"}
                          </span>
                          {/* VP-134: drill-down tylko gdy jest co pokazać (patrz komórka kampanii). */}
                          {onShowLeadDetails && displayAdLeads > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onShowLeadDetails({
                                  type: "ad",
                                  id: ad.adId,
                                  name: ad.adName,
                                  view: "leads",
                                  parentAdSetId: adSet.adSetId,
                                  parentCampaignId: campaignId,
                                });
                              }}
                              className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                              title={t("dashboard.table.showLeads")}
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {formatCurrency(adSpend)}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {displayAdCpl > 0 ? formatCurrency(displayAdCpl) : "—"}
                      </td>
                      {isAutowebinarMode && (
                        <td className="px-4 py-2 text-xs text-right">
                          <span className="inline-flex items-center gap-1 justify-end">
                            <span className="text-muted-foreground font-medium">
                              {adWebinarLive > 0 ? formatNumber(adWebinarLive) : "—"}
                            </span>
                            {onShowLeadDetails && adWebinarLive > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onShowLeadDetails({
                                    type: "ad",
                                    id: ad.adId,
                                    name: ad.adName,
                                    view: "live",
                                  });
                                }}
                                className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                                title={t("dashboard.table.showLive")}
                              >
                                <ExternalLink className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </span>
                        </td>
                      )}
                      {isAutowebinarMode && (
                        <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                          {adWebinarCostPerLive > 0 ? formatCurrency(adWebinarCostPerLive) : "—"}
                        </td>
                      )}
                      {isAutowebinarMode && (
                        <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                          {adWebinarShowRate > 0 ? formatPercent(adWebinarShowRate) : "—"}
                        </td>
                      )}
                      <td className="px-4 py-2 text-xs text-right">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <span className="text-muted-foreground">
                            {adUtm?.bookedCalls ? formatNumber(adUtm.bookedCalls) : "—"}
                          </span>
                          {onShowLeadDetails && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onShowLeadDetails({
                                  type: "ad",
                                  id: ad.adId,
                                  name: ad.adName,
                                  view: "rozmowy",
                                  parentAdSetId: adSet.adSetId,
                                  parentCampaignId: campaignId,
                                });
                              }}
                              className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                              title={t("dashboard.table.showCalls")}
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {adUtm?.bookedCalls ? formatCurrency(adSpend / adUtm.bookedCalls) : "—"}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {(() => {
                          if (!adUtm) return "—";
                          const d = adUtm.showed + adUtm.noShow;
                          return d > 0 ? formatPercent((adUtm.showed / d) * 100) : "—";
                        })()}
                      </td>
                      <td className="px-4 py-2 text-xs text-right">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <span
                            className={adUtm?.won ? "text-emerald-400" : "text-muted-foreground"}
                          >
                            {adUtm?.won ? formatNumber(adUtm.won) : "—"}
                          </span>
                          {onShowLeadDetails && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onShowLeadDetails({
                                  type: "ad",
                                  id: ad.adId,
                                  name: ad.adName,
                                  view: "sprzedaze",
                                  parentAdSetId: adSet.adSetId,
                                  parentCampaignId: campaignId,
                                });
                              }}
                              className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-[#ef3e29] transition-colors"
                              title={t("dashboard.table.showSales")}
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {parseFloat(ad.cpc) > 0 ? formatCurrency(parseFloat(ad.cpc)) : "-"}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {formatCurrency(parseFloat(ad.cpm) || 0)}
                      </td>
                      {/* ROAS = INTEGRATION revenue (GHL won) ÷ ad spend. */}
                      <td className="px-4 py-2 text-xs text-muted-foreground text-right">
                        {adUtm?.revenue && adSpend > 0
                          ? `${(adUtm.revenue / adSpend).toFixed(2)}x`
                          : "—"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={12} className="px-12 py-2 text-xs text-muted-foreground">
                Brak reklam z danymi
              </td>
            </tr>
          )}
        </>
      )}
      {previewAd && (
        <AdPreviewDialog
          adId={previewAd.adId}
          adName={previewAd.adName}
          adAccountId={adAccountId}
          forClientUserId={forClientUserId}
          onClose={() => setPreviewAd(null)}
        />
      )}
    </>
  );
}

function AdPreviewDialog({
  adId,
  adName,
  adAccountId,
  forClientUserId,
  onClose,
}: {
  adId: string;
  adName: string;
  adAccountId?: string;
  forClientUserId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#1a1a2e] border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground text-sm truncate pr-8">
            {t("dashboard.adPreview.titlePrefix")}
            {adName}
          </DialogTitle>
        </DialogHeader>
        <AdPreviewContent adId={adId} adAccountId={adAccountId} forClientUserId={forClientUserId} />
      </DialogContent>
    </Dialog>
  );
}

export default function DashboardPage(
  props: { params?: { [param: string]: string | undefined }; embedded?: boolean } = {}
) {
  const { embedded = false } = props;
  const { t } = useTranslation();
  const DASHBOARD_MODES = useMemo(() => buildDashboardModes(t), [t]);
  const DATE_PRESETS = useMemo(() => buildDatePresets(t), [t]);
  const COMPARE_PRESETS = useMemo(() => buildComparePresets(t), [t]);
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const [datePreset, setDatePreset] = useState("last_7d");
  const [comparePreset, setComparePreset] = useState("none");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const forcePreview = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("preview") === "1";
  }, []);
  const [selectedAdAccountId, setSelectedAdAccountId] = useState<string>("");
  const [campaignFilter, setCampaignFilter] = useState(() => {
    try {
      return localStorage.getItem("dashboard_campaign_filter") || "";
    } catch {
      return "";
    }
  });
  const [filterDraft, setFilterDraft] = useState(campaignFilter);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "overview" | "setter" | "lead_quality" | "dane" | "roas_world"
  >("overview");
  const [daneCache, setDaneCache] = useState<Record<string, DailyInsight>>({});
  const [noteEditorDate, setNoteEditorDate] = useState<string | null>(null);
  const [noteEditorDraft, setNoteEditorDraft] = useState<string>("");
  const [attributionModel, setAttributionModel] = useState<
    "data" | "first_click" | "last_click" | "neuroflow"
  >("data");
  const [clientSearch, setClientSearch] = useState("");
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);

  // Custom date ranges
  const [customFrom, setCustomFrom] = useState<Date | undefined>();
  const [customTo, setCustomTo] = useState<Date | undefined>();
  const [compareFrom, setCompareFrom] = useState<Date | undefined>();
  const [compareTo, setCompareTo] = useState<Date | undefined>();

  // Check if user has only dashboard access (opt-in modules don't count — see shared/module-access.ts)
  const allowedModules = standardAllowedModules((user as any)?.allowedModules ?? null);
  const isDashboardOnly =
    allowedModules !== null && allowedModules.length === 1 && allowedModules[0] === "dashboard";

  // Check if user has expert plan (required for Swiat ROASu)
  const userPlanKey = (user as any)?.subscriptionPlanKey as string | undefined;
  const isSuperAdmin = (user as any)?.role === "super_admin";
  // Agency clients inherit Expert access — their data is managed by the agency owner
  const isAgencyClient = !!(user as any)?.agencyId;
  const isExpertPlan =
    isSuperAdmin || isAgencyClient || (userPlanKey?.includes(":expert:") ?? false);

  // Employee/owner: can select which client to view — uses global store
  const {
    clients: myClients,
    selectedClientUserId: globalSelectedClientUserId,
    selectedClient,
    setSelectedClient,
    isAgencyUser: isEmployee,
  } = useAgencyClients();
  const selectedClientUserId = globalSelectedClientUserId ?? "";
  const forClientUserId = selectedClientUserId || undefined;

  // Fetch analytics settings (for dashboard mode and setter calendar IDs)
  // When viewing a client's dashboard, fetch the client's settings instead of admin's
  const analyticsSettingsUrl = forClientUserId
    ? `/api/analytics-settings?forClientUserId=${forClientUserId}`
    : "/api/analytics-settings";

  const { data: analyticsSettingsData } = useQuery<AnalyticsSettingsResponse>({
    queryKey: ["/api/analytics-settings", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", analyticsSettingsUrl),
    enabled: !!user,
    staleTime: 1000 * 60 * 2,
  });

  const dashboardMode: DashboardMode = analyticsSettingsData?.dashboardMode || "lead_gen_basic";
  const setterCalendarIds = analyticsSettingsData?.setterCalendarIds || [];
  const setterCallSource = analyticsSettingsData?.setterCallSource || "ghl";
  const ecommerceSource = analyticsSettingsData?.ecommerceSource || "meta";
  const showLeadQuality =
    analyticsSettingsData?.dataSources?.googleSheets && analyticsSettingsData?.leadQualityAiEnabled;

  // Fetch cached lead quality results
  const {
    data: leadQualityData,
    isLoading: isLeadQualityLoading,
    refetch: refetchLeadQuality,
  } = useQuery<{
    ok: boolean;
    hasData: boolean;
    averageScore?: number;
    totalLeads?: number;
    scoredLeads?: number;
    scores?: {
      leadName: string;
      qualityScore: number;
      reasoning: string;
      campaignId: string;
      adsetId: string;
      adId: string;
    }[];
    byCampaign?: Record<string, { avg: number; count: number }>;
    byAdset?: Record<string, { avg: number; count: number }>;
    byAd?: Record<string, { avg: number; count: number }>;
    nameMap?: Record<string, string>;
    analyzedAt?: string;
  }>({
    queryKey: ["/api/analytics-settings/lead-quality/results"],
    queryFn: () => apiRequest("GET", "/api/analytics-settings/lead-quality/results"),
    enabled: !!user && !!showLeadQuality,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch available ad accounts (for selected client if viewing as employee)
  const metaStatusParam = forClientUserId ? `?forUserId=${forClientUserId}` : "";
  const { data: metaStatus } = useQuery<MetaStatus>({
    queryKey: ["/api/integrations/meta/status", forClientUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/meta/status${metaStatusParam}`),
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
  });

  // Fetch Google Ads status
  const googleAdsStatusParam = forClientUserId ? `?forUserId=${forClientUserId}` : "";
  const { data: googleAdsStatus } = useQuery<{
    connected: boolean;
    selectedCustomerId?: string;
    customerAccounts?: { id: string; descriptiveName: string }[];
  }>({
    queryKey: ["/api/integrations/google-ads/status", forClientUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/google-ads/status${googleAdsStatusParam}`),
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
  });

  // Determine which Google Ads accounts to fetch for the selected client
  const clientGoogleAccountIds = useMemo(() => {
    // Priority 1: client's assigned ad accounts (employee viewing client)
    if (selectedClient?.adAccounts) {
      const googleAccounts = (
        selectedClient.adAccounts as { id: string; name: string; platform: string }[]
      )
        .filter((a) => a.platform === "google")
        .map((a) => a.id);
      if (googleAccounts.length > 0) return googleAccounts;
    }
    // Priority 2: selectedCustomerId from Google Ads settings (may be comma-separated for multi-select)
    if (googleAdsStatus?.selectedCustomerId) {
      const ids = googleAdsStatus.selectedCustomerId.split(",").filter(Boolean);
      if (ids.length > 0) return ids;
    }
    return null;
  }, [selectedClient, googleAdsStatus?.selectedCustomerId]);

  const hasMultipleGoogleAccounts = (clientGoogleAccountIds?.length || 0) > 1;

  // Fetch Google Ads campaign insights (multi-account when multiple accounts connected)
  const { data: googleAdsInsights } = useQuery<{
    campaigns: any[];
    adGroups?: any[];
    perAccount?: { accountId: string; accountName: string; campaigns: any[]; adGroups: any[] }[];
    customerId?: string;
    customerName?: string;
  }>({
    queryKey: [
      "/api/integrations/google-ads/insights",
      datePreset,
      hasMultipleGoogleAccounts,
      forClientUserId,
      clientGoogleAccountIds,
    ],
    queryFn: () =>
      apiRequest("POST", "/api/integrations/google-ads/insights", {
        datePreset,
        multiAccount: hasMultipleGoogleAccounts,
        forUserId: forClientUserId,
        customerIds: clientGoogleAccountIds,
      }).catch((err: any) => {
        // Awaria Google Ads jest tu przełykana (dashboard renderuje się bez tych kampanii),
        // więc konsola jest jedynym śladem — bez tego błąd znika bez ostrzeżenia.
        // eslint-disable-next-line no-console
        console.error("[Dashboard] Google Ads insights fetch failed:", err);
        return { campaigns: [] };
      }),
    enabled:
      !!user &&
      !!googleAdsStatus?.connected &&
      (!!googleAdsStatus?.selectedCustomerId || hasMultipleGoogleAccounts),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const fmtDate = toLocalYmd;

  const requestBody = useMemo(() => {
    const body: any = { datePreset };
    if (forcePreview) body.forcePreview = true;
    if (selectedAdAccountId) body.adAccountId = selectedAdAccountId;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    // VP-135: model atrybucji przelicza leady/rozmowy/sprzedaże/ROAS per kampania → adset →
    // reklama po stronie backendu. Jest częścią `requestBody`, więc siedzi też w queryKey —
    // zmiana w dropdownie unieważnia zapytanie i tabela odświeża się sama.
    body.attributionModel = attributionModel;
    if (datePreset === "custom" && customFrom && customTo) {
      body.customRange = { since: fmtDate(customFrom), until: fmtDate(customTo) };
    }
    if (comparePreset !== "none") {
      body.comparePreset = comparePreset;
      if (comparePreset === "custom" && compareFrom && compareTo) {
        body.compareCustomRange = { since: fmtDate(compareFrom), until: fmtDate(compareTo) };
      }
    }
    return body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    datePreset,
    comparePreset,
    customFrom,
    customTo,
    compareFrom,
    compareTo,
    selectedAdAccountId,
    forClientUserId,
    forcePreview,
    attributionModel,
  ]);

  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery<DashboardData>({
    queryKey: ["/api/dashboard/meta-ads", requestBody],
    queryFn: () => apiRequest("POST", "/api/dashboard/meta-ads", requestBody),
    enabled: !!user && !(datePreset === "custom" && (!customFrom || !customTo)),
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, err) => {
      if (
        err instanceof Error &&
        (err.message.startsWith("401:") ||
          err.message.startsWith("403:") ||
          err.message.startsWith("404:"))
      )
        return false;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });
  const dashboardPreview = dashboardData?.preview?.enabled === true;
  const previewBannerConfig = useMemo(() => {
    const reason = dashboardData?.preview?.reason;
    const META_CONNECT_HREF = "/integracje?open=meta";
    type PreviewBannerState = {
      title?: string;
      description: string;
      ctaLabel?: string;
      ctaHref?: string;
      showConnectCta: boolean;
    };
    const stateByReason: Record<PreviewMeta["reason"], PreviewBannerState | undefined> = {
      no_integration: {
        title: t("previewBanner.states.noIntegration.title"),
        description: t("previewBanner.states.noIntegration.subtitle"),
        ctaLabel: t("previewBanner.states.noIntegration.cta"),
        ctaHref: META_CONNECT_HREF,
        showConnectCta: true,
      },
      token_expired: {
        title: t("previewBanner.states.tokenExpired.title"),
        description: t("previewBanner.states.tokenExpired.subtitle"),
        ctaLabel: t("previewBanner.states.tokenExpired.cta"),
        ctaHref: META_CONNECT_HREF,
        showConnectCta: true,
      },
      no_campaigns: {
        title: t("previewBanner.states.noCampaigns.title"),
        description: t("previewBanner.states.noCampaigns.subtitle"),
        showConnectCta: false,
      },
      no_accounts: {
        description: t("dashboard.previewDesc.noAccounts"),
        showConnectCta: true,
      },
      no_agency: {
        description: t("dashboard.previewDesc.noAccounts"),
        showConnectCta: true,
      },
    };
    const matched = reason ? stateByReason[reason] : undefined;
    return (
      matched ?? {
        description: t("dashboard.previewDesc.default"),
        showConnectCta: true,
      }
    );
  }, [dashboardData?.preview?.reason, t]);

  // Daily time-series data for chart
  const dailyRequestBody = useMemo(() => {
    const body: any = { datePreset };
    if (forcePreview) body.forcePreview = true;
    if (selectedAdAccountId) body.adAccountId = selectedAdAccountId;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    if (datePreset === "custom" && customFrom && customTo) {
      body.customRange = { since: fmtDate(customFrom), until: fmtDate(customTo) };
    }
    return body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, customFrom, customTo, selectedAdAccountId, forClientUserId, forcePreview]);

  const { data: dailyData, isLoading: isDailyLoading } = useQuery<{
    daily: DailyInsight[];
    webinarDaily?: Record<string, { attended: number; offerReached: number }> | null;
    preview?: PreviewMeta | null;
  }>({
    queryKey: ["/api/dashboard/meta-ads/daily", dailyRequestBody],
    queryFn: () => apiRequest("POST", "/api/dashboard/meta-ads/daily", dailyRequestBody),
    enabled: !!user && !(datePreset === "custom" && (!customFrom || !customTo)),
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, err) => {
      if (
        err instanceof Error &&
        (err.message.startsWith("401:") || err.message.startsWith("403:"))
      )
        return false;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });

  // Full cached daily history (Supabase-backed) for the "Dane" tab.
  // Lazy-loaded: only fetched when the user opens the Dane tab.
  const dailyHistoryRequestBody = useMemo(() => {
    const body: { adAccountId?: string; forClientUserId?: string; forcePreview?: boolean } = {};
    if (forcePreview) body.forcePreview = true;
    if (selectedAdAccountId) body.adAccountId = selectedAdAccountId;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    return body;
  }, [selectedAdAccountId, forClientUserId, forcePreview]);

  const { data: dailyHistoryData, isLoading: isDailyHistoryLoading } = useQuery<{
    daily: DailyInsight[];
    preview?: PreviewMeta | null;
  }>({
    queryKey: ["/api/dashboard/meta-ads/daily/history", dailyHistoryRequestBody],
    queryFn: () =>
      apiRequest("POST", "/api/dashboard/meta-ads/daily/history", dailyHistoryRequestBody),
    enabled: !!user && activeTab === "dane",
    staleTime: 1000 * 60 * 5,
  });

  // ── Dane cache: persist daily insights in localStorage per account ──
  const daneCacheKey = selectedAdAccountId ? `dane_cache_v1_${selectedAdAccountId}` : null;
  useEffect(() => {
    if (!daneCacheKey) return;
    try {
      const raw = localStorage.getItem(daneCacheKey);
      const stored: Record<string, DailyInsight> = raw ? JSON.parse(raw) : {};
      setDaneCache(stored);
    } catch {
      setDaneCache({});
    }
  }, [daneCacheKey]);
  useEffect(() => {
    if (!daneCacheKey || !dailyData?.daily?.length) return;
    setDaneCache((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const d of dailyData.daily) {
        if (!next[d.date]) {
          next[d.date] = d;
          changed = true;
        }
      }
      if (changed) {
        try {
          localStorage.setItem(daneCacheKey, JSON.stringify(next));
        } catch {}
        return next;
      }
      return prev;
    });
  }, [dailyData?.daily, daneCacheKey]);

  // ── Day notes: user-authored comments per date, persisted per scope ──
  // Scope key falls back through: ad account → client → current user, so notes
  // always get a stable storage key even for the default account view.
  const dayNotesScopeKey =
    selectedAdAccountId ||
    forClientUserId ||
    ((user as { id?: string | number } | null)?.id?.toString() ?? null);
  const { notes: dayNotes, setNote: setDayNote } = useDayNotes(dayNotesScopeKey);

  // Setter stats (only for lead_gen_advanced with setter tab active)
  const setterRequestBody = useMemo(() => {
    if (datePreset === "custom" && customFrom && customTo) {
      const request: {
        dateFrom: string;
        dateTo: string;
        setterCalendarIds: string[];
        forUserId?: string;
      } = {
        dateFrom: fmtDate(customFrom),
        dateTo: fmtDate(customTo),
        setterCalendarIds,
      };
      if (forClientUserId) request.forUserId = forClientUserId;
      return request;
    }
    // Convert presets to concrete dates for CRM call
    const today = new Date();
    const sub = (days: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() - days);
      return d;
    };
    let from: Date, to: Date;
    switch (datePreset) {
      case "today":
        from = today;
        to = today;
        break;
      case "yesterday":
        from = sub(1);
        to = sub(1);
        break;
      case "last_14d":
        from = sub(14);
        to = sub(1);
        break;
      case "last_30d":
        from = sub(30);
        to = sub(1);
        break;
      case "last_90d":
        from = sub(90);
        to = sub(1);
        break;
      case "this_month":
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = today;
        break;
      case "last_month":
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        from = sub(7);
        to = sub(1);
        break;
    }
    const request: {
      dateFrom: string;
      dateTo: string;
      setterCalendarIds: string[];
      forUserId?: string;
    } = {
      dateFrom: fmtDate(from),
      dateTo: fmtDate(to),
      setterCalendarIds,
    };
    if (forClientUserId) request.forUserId = forClientUserId;
    return request;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, customFrom, customTo, setterCalendarIds, forClientUserId]);

  // Determine setter stats API endpoint based on selected source
  const setterStatsEndpoint =
    setterCallSource === "ringostat"
      ? "/api/integrations/ringostat/setter-stats"
      : "/api/integrations/crm/setter-stats";

  // For Ringostat, we don't need setterCalendarIds — just dates
  const isSetterMode =
    dashboardMode === "lead_gen_advanced" ||
    dashboardMode === "autowebinar_book_a_call" ||
    dashboardMode === "vsl_funnel";
  const setterEnabled =
    !!user && isSetterMode && (setterCallSource === "ringostat" || setterCalendarIds.length > 0);

  const {
    data: setterStats,
    isLoading: isSetterLoading,
    isError: isSetterError,
    refetch: refetchSetterStats,
  } = useQuery<SetterStatsData>({
    queryKey: [setterStatsEndpoint, setterRequestBody],
    // Fail fast (30s) instead of hanging on the global 180s timeout, and never retry —
    // a slow/down upstream (e.g. GHL) should surface an error state, not a perpetual spinner.
    queryFn: () =>
      apiRequest("POST", setterStatsEndpoint, setterRequestBody, { timeoutMs: 30_000 }),
    enabled: setterEnabled && !dashboardPreview,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  // Allegro dashboard data (ecommerce + allegro source)
  const isAllegroMode =
    dashboardMode === "ecommerce" && ecommerceSource === "allegro" && !dashboardPreview;
  const allegroRequestBody = useMemo(() => {
    const today = new Date();
    const sub = (days: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() - days);
      return d;
    };
    let from: Date, to: Date;
    if (datePreset === "custom" && customFrom && customTo) {
      from = customFrom;
      to = customTo;
    } else {
      switch (datePreset) {
        case "today":
          from = today;
          to = today;
          break;
        case "yesterday":
          from = sub(1);
          to = sub(1);
          break;
        case "last_14d":
          from = sub(14);
          to = sub(1);
          break;
        case "last_30d":
          from = sub(30);
          to = sub(1);
          break;
        case "last_90d":
          from = sub(90);
          to = sub(1);
          break;
        case "this_month":
          from = new Date(today.getFullYear(), today.getMonth(), 1);
          to = today;
          break;
        case "last_month":
          from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          to = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        default:
          from = sub(7);
          to = sub(1);
          break;
      }
    }
    const body: any = { dateFrom: fmtDate(from), dateTo: fmtDate(to) };
    // Comparison period
    if (comparePreset && comparePreset !== "none") {
      if (comparePreset === "previous_period") {
        const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
        const compTo = new Date(from);
        compTo.setDate(compTo.getDate() - 1);
        const compFrom = new Date(compTo);
        compFrom.setDate(compFrom.getDate() - days + 1);
        body.compareDateFrom = fmtDate(compFrom);
        body.compareDateTo = fmtDate(compTo);
      } else if (comparePreset === "custom" && compareFrom && compareTo) {
        body.compareDateFrom = fmtDate(compareFrom);
        body.compareDateTo = fmtDate(compareTo);
      } else {
        // Preset-based comparison
        let compDays = 7;
        if (comparePreset === "last_7d") compDays = 7;
        else if (comparePreset === "last_14d") compDays = 14;
        else if (comparePreset === "last_30d") compDays = 30;
        else if (comparePreset === "last_month") {
          const lmFrom = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lmTo = new Date(today.getFullYear(), today.getMonth(), 0);
          body.compareDateFrom = fmtDate(lmFrom);
          body.compareDateTo = fmtDate(lmTo);
          compDays = 0;
        }
        if (compDays > 0) {
          body.compareDateFrom = fmtDate(sub(compDays));
          body.compareDateTo = fmtDate(sub(1));
        }
      }
    }
    return body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, customFrom, customTo, comparePreset, compareFrom, compareTo]);

  const {
    data: allegroData,
    isLoading: isAllegroLoading,
    error: allegroError,
  } = useQuery<{
    allegroLogin: string;
    totals: {
      revenue: number;
      unitsSold: number;
      currency: string;
      totalVisits: number;
      totalWatchers: number;
    };
    dailyOrders: { date: string; revenue: number; unitsSold: number }[];
    ads: {
      spend: number;
      clicks: number;
      views: number;
      avgCpc: number;
      conversions: number;
      conversionRate: number;
      returnOnAdSpend: number;
      interest: number;
      currency: string;
      daily: {
        date: string;
        spend: number;
        clicks: number;
        views: number;
        conversions: number;
        interest: number;
      }[];
      campaigns: {
        id: string;
        name: string;
        status: string;
        spend: number;
        clicks: number;
        views: number;
        ctr: number;
        avgCpc: number;
        conversions: number;
        interest: number;
      }[];
    } | null;
    comparison: {
      totals: { revenue: number; unitsSold: number };
      ads: {
        spend: number;
        interest: number;
        clicks: number;
        views: number;
        conversions: number;
        returnOnAdSpend: number;
        avgCpc: number;
      } | null;
      label: string;
    } | null;
    topOffers: {
      id: string;
      name: string;
      price: string | null;
      priceAmount: number;
      sold: number;
      available: number;
      watchers: number;
      visits: number;
      image: string | null;
      status: string;
    }[];
  }>({
    queryKey: ["/api/integrations/allegro/dashboard", allegroRequestBody],
    queryFn: () => apiRequest("POST", "/api/integrations/allegro/dashboard", allegroRequestBody),
    enabled: !!user && isAllegroMode,
    staleTime: 1000 * 60 * 5,
  });

  const totals = dashboardData?.totals;
  const compTotals = dashboardData?.comparison?.totals;
  const effectiveSetterStats = useMemo(
    () =>
      setterStats ??
      (dashboardPreview && isSetterMode
        ? buildPreviewSetterStats(totals, dailyData?.daily)
        : undefined),
    [setterStats, dashboardPreview, isSetterMode, totals, dailyData?.daily]
  );

  // Google Ads aggregated totals for custom KPI panel
  const isLeadGenMode =
    dashboardMode === "lead_gen_basic" ||
    dashboardMode === "lead_gen_advanced" ||
    dashboardMode === "autowebinar_book_a_call" ||
    dashboardMode === "vsl_funnel";
  const googleAdsTotals = useMemo((): TotalsData | null => {
    const gCampaigns = googleAdsInsights?.campaigns || [];
    if (gCampaigns.length === 0) return null;
    const t: TotalsData = {
      spend: 0,
      impressions: 0,
      clicks: 0,
      outboundClicks: 0,
      uniqueOutboundClicks: 0,
      reach: 0,
      leads: 0,
      trials: 0,
      subscriptions: 0,
      costPerTrial: 0,
      costPerSubscription: 0,
      landingPageViews: 0,
      costPerLead: 0,
      cpm: 0,
      cpc: 0,
      ctr: 0,
      crLpToLead: 0,
      crClickToLead: 0,
      purchaseValue: 0,
      purchases: 0,
      roas: 0,
      revenueRoas: 0,
      cashCollectedRoas: 0,
      ltv: 0,
      ltvCac: 0,
      revenue: 0,
      cashCollected: 0,
      customers: 0,
      bookedAppointments: 0,
      showedAppointments: 0,
      noShowAppointments: 0,
      showRate: 0,
      qualificationRate: 0,
      crLeadToBooking: 0,
      avgSalesCycleDays: 0,
      sales: 0,
      salesRevenue: 0,
    };
    for (const c of gCampaigns) {
      t.spend += (c.costMicros || 0) / 1_000_000;
      t.impressions += c.impressions || 0;
      t.clicks += c.clicks || 0;
      // Google conversions (conversions/conversionsValue) are ad-platform numbers — they do NOT
      // count as integration leads/purchases/revenue. Google contributes spend/traffic only.
    }
    if (t.impressions > 0) {
      t.cpm = (t.spend / t.impressions) * 1000;
      t.ctr = (t.clicks / t.impressions) * 100;
    }
    if (t.clicks > 0) t.cpc = t.spend / t.clicks;
    // Google has no outbound/unique split — treat its clicks as the outbound count
    // so the combined dashboard tiles/breakdowns include Google traffic.
    t.outboundClicks = t.clicks;
    t.uniqueOutboundClicks = t.clicks;
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleAdsInsights?.campaigns, isLeadGenMode]);

  // Per-account Google Ads totals for multi-account breakdown
  const googleAdsPerAccountTotals = useMemo((): Map<
    string,
    { name: string; totals: TotalsData }
  > => {
    const map = new Map<string, { name: string; totals: TotalsData }>();
    const perAccount = googleAdsInsights?.perAccount;
    if (!perAccount || perAccount.length <= 1) return map;
    for (const account of perAccount) {
      if (account.campaigns.length === 0) continue;
      const t: TotalsData = {
        spend: 0,
        impressions: 0,
        clicks: 0,
        outboundClicks: 0,
        uniqueOutboundClicks: 0,
        reach: 0,
        leads: 0,
        trials: 0,
        subscriptions: 0,
        costPerTrial: 0,
        costPerSubscription: 0,
        landingPageViews: 0,
        costPerLead: 0,
        cpm: 0,
        cpc: 0,
        ctr: 0,
        crLpToLead: 0,
        crClickToLead: 0,
        purchaseValue: 0,
        purchases: 0,
        roas: 0,
        revenueRoas: 0,
        cashCollectedRoas: 0,
        ltv: 0,
        ltvCac: 0,
        revenue: 0,
        cashCollected: 0,
        customers: 0,
        bookedAppointments: 0,
        showedAppointments: 0,
        noShowAppointments: 0,
        showRate: 0,
        qualificationRate: 0,
        crLeadToBooking: 0,
        avgSalesCycleDays: 0,
        sales: 0,
        salesRevenue: 0,
      };
      for (const c of account.campaigns) {
        t.spend += (c.costMicros || 0) / 1_000_000;
        t.impressions += c.impressions || 0;
        t.clicks += c.clicks || 0;
        // Google conversions are ad-platform numbers — not integration leads/purchases/revenue.
      }
      if (t.impressions > 0) {
        t.cpm = (t.spend / t.impressions) * 1000;
        t.ctr = (t.clicks / t.impressions) * 100;
      }
      if (t.clicks > 0) t.cpc = t.spend / t.clicks;
      t.outboundClicks = t.clicks;
      t.uniqueOutboundClicks = t.clicks;
      map.set(account.accountId, { name: account.accountName, totals: t });
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleAdsInsights?.perAccount, isLeadGenMode]);

  const emptyTotals: TotalsData = {
    spend: 0,
    impressions: 0,
    clicks: 0,
    outboundClicks: 0,
    uniqueOutboundClicks: 0,
    reach: 0,
    leads: 0,
    trials: 0,
    subscriptions: 0,
    costPerTrial: 0,
    costPerSubscription: 0,
    landingPageViews: 0,
    costPerLead: 0,
    cpm: 0,
    cpc: 0,
    ctr: 0,
    crLpToLead: 0,
    crClickToLead: 0,
    purchaseValue: 0,
    purchases: 0,
    roas: 0,
    revenueRoas: 0,
    cashCollectedRoas: 0,
    ltv: 0,
    ltvCac: 0,
    revenue: 0,
    cashCollected: 0,
    customers: 0,
    bookedAppointments: 0,
    showedAppointments: 0,
    noShowAppointments: 0,
    showRate: 0,
    qualificationRate: 0,
    crLeadToBooking: 0,
    avgSalesCycleDays: 0,
    sales: 0,
    salesRevenue: 0,
  };

  // Combined totals (Meta + Google Ads) for primary dashboard display
  const combinedTotals = useMemo((): TotalsData => {
    const meta = totals || emptyTotals;
    const google = dashboardPreview ? emptyTotals : googleAdsTotals || emptyTotals;
    // VP-138: gdy nowy tor aktywny (metricsV2), `meta.*` zawiera JUŻ pełną, zdeduplikowaną liczbę
    // konwersji ze wszystkich źródeł (też google) → NIE dodajemy konwersji google drugi raz.
    // Spend/impresje/kliknięcia platform łączymy normalnie (to nie konwersje).
    const hasV2 = !dashboardPreview && !!dashboardData?.metricsV2;
    const gConv = hasV2 ? emptyTotals : google;
    // TikTok ma `clicks`, ale nie `outboundClicks` → blended `totals` gubi kliki TikToka.
    // Doliczamy je jawnie, by kafelek KLIKNIĘCIA i CPC obejmowały TikToka (addytywnie).
    const ttClicks = dashboardPreview
      ? 0
      : (dashboardData?.adPlatformTotals?.tiktok?.outboundClicks ?? 0);
    const ttUniqueClicks = dashboardPreview
      ? 0
      : (dashboardData?.adPlatformTotals?.tiktok?.uniqueOutboundClicks ?? 0);
    const totalSpend = meta.spend + google.spend;
    const totalImpressions = meta.impressions + google.impressions;
    const totalClicks = meta.clicks + google.clicks;
    const totalOutbound = meta.outboundClicks + google.outboundClicks + ttClicks;
    const totalUniqueOutbound =
      meta.uniqueOutboundClicks + google.uniqueOutboundClicks + ttUniqueClicks;
    const totalReach = meta.reach + google.reach;
    const totalLeads = meta.leads + gConv.leads;
    const totalPurchaseValue = meta.purchaseValue + gConv.purchaseValue;
    const totalPurchases = meta.purchases + gConv.purchases;
    return {
      ...meta,
      spend: totalSpend,
      impressions: totalImpressions,
      clicks: totalClicks,
      outboundClicks: totalOutbound,
      uniqueOutboundClicks: totalUniqueOutbound,
      reach: totalReach,
      leads: totalLeads,
      trials: meta.trials + gConv.trials,
      subscriptions: meta.subscriptions + gConv.subscriptions,
      landingPageViews: meta.landingPageViews + google.landingPageViews,
      purchaseValue: totalPurchaseValue,
      purchases: totalPurchases,
      costPerLead: totalLeads > 0 ? totalSpend / totalLeads : 0,
      costPerTrial: meta.trials + gConv.trials > 0 ? totalSpend / (meta.trials + gConv.trials) : 0,
      costPerSubscription:
        meta.subscriptions + gConv.subscriptions > 0
          ? totalSpend / (meta.subscriptions + gConv.subscriptions)
          : 0,
      cpm: totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0,
      // PO: CPC = spend / unique outbound clicks; CTR = unique outbound / reach
      // (Meta's "unique outbound CTR" — matches Ads Manager). crClickToLead uses
      // the displayed outbound click count for consistency.
      cpc: totalUniqueOutbound > 0 ? totalSpend / totalUniqueOutbound : 0,
      // CTR = unique outbound / reach is a Meta-specific metric (matches Ads Manager).
      // Google Ads has no "reach" equivalent, so blending Google clicks into the numerator
      // while reach stays Meta-only would inflate CTR. Use Meta data only for this metric.
      ctr: meta.reach > 0 ? (meta.uniqueOutboundClicks / meta.reach) * 100 : 0,
      crLpToLead:
        meta.landingPageViews + google.landingPageViews > 0
          ? (totalLeads / (meta.landingPageViews + google.landingPageViews)) * 100
          : 0,
      crClickToLead: totalOutbound > 0 ? (totalLeads / totalOutbound) * 100 : 0,
      roas: totalSpend > 0 ? totalPurchaseValue / totalSpend : 0,
      revenueRoas: totalSpend > 0 && meta.revenue > 0 ? meta.revenue / totalSpend : 0,
      ltvCac: meta.ltv > 0 && totalLeads > 0 ? meta.ltv / (totalSpend / totalLeads) : 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totals,
    googleAdsTotals,
    dashboardPreview,
    dashboardData?.metricsV2,
    dashboardData?.adPlatformTotals?.tiktok,
  ]);

  // Available ad accounts for the selector
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const adAccounts = metaStatus?.adAccounts || [];

  // Resolve display currency from user preference + active ad account
  const activeAccountCurrency = useMemo(() => {
    if (!selectedAdAccountId) return adAccounts[0]?.currency ?? null;
    return adAccounts.find((a) => a.id === selectedAdAccountId)?.currency ?? null;
  }, [selectedAdAccountId, adAccounts]);
  const dc = useDashboardCurrency(activeAccountCurrency);

  // Shadow module-level formatCurrency with resolved currency
  const formatCurrency = dc.fmt;

  // Build per-platform breakdowns for metrics
  const platformBreakdowns = useMemo(() => {
    const meta = totals || emptyTotals;
    const google = dashboardPreview ? emptyTotals : googleAdsTotals || emptyTotals;
    const hasPerAccount = !dashboardPreview && googleAdsPerAccountTotals.size > 1;
    // Metryki reklamowe per platforma z backendu. metaOnly = czysta Meta (bez doklejonego TikToka,
    // który blended `totals` zlewa). tiktok = osobna składowa. Fallback: gdy brak adPlatformTotals
    // (stary cache/preview) → chip meta degraduje do blended `totals` (obecne zachowanie).
    const apt = dashboardPreview ? undefined : dashboardData?.adPlatformTotals;
    const metaAds = apt?.meta;
    const tiktokAds = apt?.tiktok;
    // Google-primary (brak Meta): frontowy googleAdsTotals jest null, a Google leci w backendowych
    // campaigns → bierzemy go z adPlatformTotals.google. Dla klientów Meta+Google backendowy
    // adPlatformTotals.google jest zerowy (Google nie wchodzi do backendowych campaigns) → wtedy
    // fallback = null, a chipy google (też per-konto) liczy istniejący addGoogleBreakdown.
    const aptGoogle = apt?.google;
    const googleAdsFallback =
      aptGoogle && (aptGoogle.spend > 0 || aptGoogle.impressions > 0) ? aptGoogle : null;
    // Rozbicie per-platforma (adPlatformTotals) dotyczy TYLKO metryk reklamowych. Metryki
    // konwersyjne/wartościowe (purchaseValue/purchases/roas) nie są w AdPlatformMetricsView →
    // dla nich chip meta bierzemy ze zlanego `totals` i zachowujemy oryginalne zachowanie.
    const AD_PLATFORM_METRIC_KEYS: ReadonlySet<string> = new Set([
      "spend",
      "impressions",
      "outboundClicks",
      "uniqueOutboundClicks",
      "reach",
      "landingPageViews",
      "cpm",
      "cpc",
      "ctr",
    ]);

    const addGoogleBreakdown = (
      items: PlatformBreakdownItem[],
      metricKey: keyof TotalsData,
      formatFn: (v: number) => string
    ) => {
      if (!hasPerAccount) {
        const googleVal = google[metricKey] as number;
        if (googleVal > 0)
          items.push({ label: PAID_PLATFORM_CHIP_LABELS.google, value: formatFn(googleVal) });
        return;
      }
      googleAdsPerAccountTotals.forEach(({ name, totals: at }) => {
        const val = at[metricKey] as number;
        if (val > 0)
          items.push({
            label: `${PAID_PLATFORM_CHIP_LABELS.google}: ${name}`,
            value: formatFn(val),
          });
      });
    };

    // Metryki reklamowe: chip meta z metaOnly (fallback blended), chip tiktok z adPlatformTotals,
    // chip google z istniejącego toru (lub adPlatformTotals.google dla google-primary). Wartość 0 → chip pominięty.
    const buildBreakdown = (
      metricKey: keyof TotalsData,
      formatFn: (v: number) => string
    ): PlatformBreakdownItem[] => {
      const items: PlatformBreakdownItem[] = [];
      // Per-platforma tylko dla metryk reklamowych; inne klucze → zachowane stare zachowanie.
      const usePerPlatform = AD_PLATFORM_METRIC_KEYS.has(metricKey as string);
      const metaSource = (usePerPlatform && metaAds ? metaAds : meta) as Record<string, number>;
      const metaVal = metaSource[metricKey as string] ?? 0;
      if (metaVal > 0)
        items.push({ label: PAID_PLATFORM_CHIP_LABELS.meta, value: formatFn(metaVal) });
      if (usePerPlatform && googleAdsFallback) {
        const gVal = (googleAdsFallback as Record<string, number>)[metricKey as string] ?? 0;
        if (gVal > 0)
          items.push({ label: PAID_PLATFORM_CHIP_LABELS.google, value: formatFn(gVal) });
      } else {
        addGoogleBreakdown(items, metricKey, formatFn);
      }
      if (usePerPlatform && tiktokAds) {
        const ttVal = (tiktokAds as Record<string, number>)[metricKey as string] ?? 0;
        if (ttVal > 0)
          items.push({ label: PAID_PLATFORM_CHIP_LABELS.tiktok, value: formatFn(ttVal) });
      }
      return items;
    };

    const leadBreakdown = (): PlatformBreakdownItem[] => {
      // VP-138: gdy nowy tor aktywny, chipy bierzemy z sourceBreakdown (per źródło, z kubełkiem
      // 'unattributed'), który SUMUJE się do nagłówka — koniec rozjazdu 'meta+google != total'.
      // VP-138/VP-133: chipy z sourceBreakdown regrupowane do meta/google/tiktok/organic/Nieprzypisane,
      // sumują się 1:1 do nagłówka LEADY (unattributed = wyliczana reszta).
      if (dashboardData?.metricsV2 && dashboardData?.sourceBreakdown?.buckets?.length) {
        return groupLeadSourceBuckets(
          dashboardData.sourceBreakdown.buckets.map((b) => ({
            key: b.key,
            label: b.label,
            leads: b.leads,
          }))
        ).map((c) => ({ label: c.label, value: String(c.value) }));
      }
      // Legacy (flaga OFF): ad-platform integrations ONLY (Meta Ads / Google Ads) — Meta value =
      // leads attributed to Meta campaigns (utmAttribution); organic/direct intentionally not shown.
      const items: PlatformBreakdownItem[] = [];
      const metaLeads = (dashboardData?.utmAttribution || []).reduce(
        (s, r) => s + (r.leads || 0),
        0
      );
      if (metaLeads > 0)
        items.push({ label: PAID_PLATFORM_CHIP_LABELS.meta, value: String(metaLeads) });
      if (!hasPerAccount) {
        if (google.leads > 0)
          items.push({ label: PAID_PLATFORM_CHIP_LABELS.google, value: String(google.leads) });
      } else {
        googleAdsPerAccountTotals.forEach(({ name, totals: at }) => {
          if (at.leads > 0)
            items.push({
              label: `${PAID_PLATFORM_CHIP_LABELS.google}: ${name}`,
              value: String(at.leads),
            });
        });
      }
      return items;
    };

    const buildDerivedBreakdown = (
      numeratorKey: keyof TotalsData,
      denominatorKey: keyof TotalsData,
      formatFn: (v: number) => string
    ): PlatformBreakdownItem[] => {
      const items: PlatformBreakdownItem[] = [];
      const metaNum = meta[numeratorKey] as number;
      const metaDen = meta[denominatorKey] as number;
      if (metaDen > 0 && metaNum > 0)
        items.push({ label: PAID_PLATFORM_CHIP_LABELS.meta, value: formatFn(metaNum / metaDen) });
      if (!hasPerAccount) {
        const gNum = google[numeratorKey] as number;
        const gDen = google[denominatorKey] as number;
        if (gDen > 0 && gNum > 0)
          items.push({ label: PAID_PLATFORM_CHIP_LABELS.google, value: formatFn(gNum / gDen) });
      } else {
        googleAdsPerAccountTotals.forEach(({ name, totals: at }) => {
          const num = at[numeratorKey] as number;
          const den = at[denominatorKey] as number;
          if (den > 0 && num > 0)
            items.push({
              label: `${PAID_PLATFORM_CHIP_LABELS.google}: ${name}`,
              value: formatFn(num / den),
            });
        });
      }
      return items;
    };

    // KOSZT ZA LEADA per platforma: spend platformy (z adPlatformTotals) / leady platformy (z
    // kubełków sourceBreakdown: meta_paid / google_paid / src_tiktok). Organic/Nieprzypisane nie
    // mają spendu → brak chipa kosztu. Gdy brak metricsV2/kubełków → stara logika (buildDerivedBreakdown).
    const costPerLeadBreakdown = (): PlatformBreakdownItem[] => {
      const buckets = dashboardData?.sourceBreakdown?.buckets;
      if (!(dashboardData?.metricsV2 && buckets?.length)) {
        return buildDerivedBreakdown("spend", "leads", formatCurrency);
      }
      const leadsOf = (key: string): number => buckets.find((b) => b.key === key)?.leads ?? 0;
      const metaSpend = (metaAds ?? meta).spend;
      const googleSpend = googleAdsFallback?.spend ?? google.spend;
      const tiktokSpend = tiktokAds?.spend ?? 0;
      const rows: Array<{ label: string; spend: number; leads: number }> = [
        { label: PAID_PLATFORM_CHIP_LABELS.meta, spend: metaSpend, leads: leadsOf("meta_paid") },
        {
          label: PAID_PLATFORM_CHIP_LABELS.google,
          spend: googleSpend,
          leads: leadsOf("google_paid"),
        },
        {
          label: PAID_PLATFORM_CHIP_LABELS.tiktok,
          spend: tiktokSpend,
          leads: leadsOf("src_tiktok"),
        },
      ];
      return rows
        .filter((r) => r.spend > 0 && r.leads > 0)
        .map((r) => ({ label: r.label, value: formatCurrency(r.spend / r.leads) }));
    };

    return {
      spend: buildBreakdown("spend", formatCurrency),
      impressions: buildBreakdown("impressions", formatNumber),
      clicks: buildBreakdown("outboundClicks", formatNumber),
      leads: leadBreakdown(),
      landingPageViews: buildBreakdown("landingPageViews", formatNumber),
      reach: buildBreakdown("reach", formatNumber),
      purchaseValue: buildBreakdown("purchaseValue", formatCurrency),
      purchases: buildBreakdown("purchases", formatNumber),
      roas: buildBreakdown("roas", (v) => `${v.toFixed(2)}x`),
      cpm: buildBreakdown("cpm", formatCurrency),
      cpc: buildBreakdown("cpc", formatCurrency),
      ctr: buildBreakdown("ctr", (v) => `${v.toFixed(2)}%`),
      cpa: buildDerivedBreakdown("spend", "purchases", formatCurrency),
      aov: buildDerivedBreakdown("purchaseValue", "purchases", formatCurrency),
      costPerLead: costPerLeadBreakdown(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totals,
    googleAdsTotals,
    googleAdsPerAccountTotals,
    dashboardData?.organicSourceGroups,
    dashboardData?.utmAttribution,
    dashboardData?.adPlatformTotals,
    dashboardData?.sourceBreakdown,
    dashboardData?.metricsV2,
    dashboardPreview,
  ]);

  // Lead breakdown chips: ad-platform integrations ONLY (Meta/Google), kept clean — we do NOT
  // explode into per-source/per-campaign attribution rows here (that was visual noise).
  const effectiveLeadBreakdowns = platformBreakdowns.leads;

  // VP-135: leady liczy backend wg wybranego modelu atrybucji (`attributionModel` leci w body
  // `meta-ads`), więc kafelek czyta TĘ SAMĄ liczbę co wiersz SUMA i komórki per-kampania —
  // koniec rozjazdu „kafelek z jednego źródła, tabela z innego".
  //
  // Wcześniej ten kafelek dociągał osobno `GET /api/tracking/attribution-report`, który
  // agregował konwersje WSZYSTKICH kont (brak filtra ownera — naprawione w tym samym zadaniu)
  // i grupował tylko po `source`, więc nie dawał się pogodzić z tabelą. Raport pozostaje
  // dostępny jako samodzielny widok (`attribution-report.tsx`).
  const effectiveTotalLeads = combinedTotals.leads;

  // Normalize Google Ads campaigns to CampaignInsight format and merge
  const allCampaigns = useMemo(() => {
    // Backend dopisuje wiersze TikTok z _source:'tiktok' do dashboardData.campaigns.
    // NIE nadpisuj ich na 'meta' — inaczej chip TikTok (c._source === 'tiktok') nigdy sie nie zapali.
    const rawCampaigns = dashboardData?.campaigns || [];
    const metaCampaigns: CampaignInsight[] = rawCampaigns
      .filter((c) => (c as any)._source !== "tiktok")
      .map((c) => ({
        ...c,
        _source: "meta" as const,
        _accountId: dashboardData?.adAccountName,
        _accountName: dashboardData?.adAccountName,
      }));
    const tiktokCampaigns: CampaignInsight[] = rawCampaigns
      .filter((c) => (c as any)._source === "tiktok")
      .map((c) => ({
        ...c,
        _source: "tiktok" as const,
        _accountId: (c as any)._accountId || (c as any).advertiserId || "tiktok_default",
        _accountName: (c as any)._accountName || "TikTok Ads",
      }));
    if (dashboardPreview) return [...metaCampaigns, ...tiktokCampaigns];
    const gAdsCampaigns: CampaignInsight[] = (googleAdsInsights?.campaigns || []).map((c: any) => {
      const spend = (c.costMicros || 0) / 1_000_000;
      const clicks = c.clicks || 0;
      const impressions = c.impressions || 0;
      const conversions = c.conversions || 0;
      const leads = isLeadGenMode ? conversions : 0;
      return {
        campaignId: `gads_${c.campaignId}`,
        campaignName: c.campaignName,
        status: c.status || "ACTIVE",
        spend: String(spend),
        impressions: String(impressions),
        clicks: String(clicks),
        cpc: String(clicks > 0 ? spend / clicks : 0),
        cpm: String(impressions > 0 ? (spend / impressions) * 1000 : 0),
        ctr: String(c.ctr || 0),
        reach: "0",
        leads: String(leads),
        costPerLead: String(leads > 0 ? spend / leads : 0),
        actions: [],
        costPerActionType:
          c.conversions > 0
            ? [{ actionType: "omni_purchase", value: String(c.costPerConversion || 0) }]
            : [],
        purchaseValue: String(c.conversionsValue || 0),
        purchaseRoas: String(spend > 0 ? (c.conversionsValue || 0) / spend : 0),
        trials: "0",
        costPerTrial: "0",
        subscriptions: "0",
        costPerSubscription: "0",
        _source: "google" as const,
        _accountId: c._accountId || googleAdsInsights?.customerId,
        _accountName: c._accountName || googleAdsInsights?.customerName || "Google Ads",
      };
    });
    return [...metaCampaigns, ...tiktokCampaigns, ...gAdsCampaigns];
  }, [
    dashboardData?.campaigns,
    dashboardData?.adAccountName,
    googleAdsInsights,
    dashboardPreview,
    isLeadGenMode,
  ]);

  // Filter campaigns by keyword
  const filteredCampaigns = useMemo(() => {
    if (!allCampaigns.length) return [];
    let result = allCampaigns;
    if (campaignFilter.trim()) {
      const kw = campaignFilter.trim().toLowerCase();
      result = result.filter((c) => c.campaignName.toLowerCase().includes(kw));
    }
    return result;
  }, [allCampaigns, campaignFilter]);

  const logoutMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/logout"),
    onSuccess: () => {
      queryClient.clear();
      window.location.href = "/";
    },
  });

  // Lead quality analysis mutation
  const [analyzingQuality, setAnalyzingQuality] = useState(false);
  const analyzeQualityMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/analytics-settings/lead-quality/analyze"),
    onSuccess: () => {
      setAnalyzingQuality(false);
      refetchLeadQuality();
    },
    onError: () => {
      setAnalyzingQuality(false);
    },
  });

  return (
    <div className={`${resolvedTheme} h-full overflow-y-auto relative bg-background`}>
      <div className="fixed inset-0 w-screen h-screen pointer-events-none -z-10 bg-[radial-gradient(1200px_600px_at_15%_-10%,rgba(239,62,41,0.10),transparent_65%),radial-gradient(900px_500px_at_85%_10%,rgba(239,62,41,0.06),transparent_70%),linear-gradient(180deg,#07080c_0%,#05060a_100%)] dark:block hidden" />
      {/* Header */}
      <header className="sticky top-0 z-10 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          {/* Top row: logo/back + title + user actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-dense min-w-0">
              {!isDashboardOnly && !embedded ? (
                <Link href="/">
                  <button className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors shrink-0">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                </Link>
              ) : isDashboardOnly ? (
                <div className="flex items-center gap-2 shrink-0">
                  <img
                    src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
                    alt="Viral Partners Logo"
                    className="w-7 h-7 object-contain"
                  />
                  <div className="hidden sm:block">
                    <span className="text-foreground font-semibold text-sm">VIRAL PARTNERS</span>
                    <span className="text-muted-foreground text-xs ml-2">MarketingOS</span>
                  </div>
                </div>
              ) : null}
              <div className="min-w-0">
                <div className="flex items-center gap-2 md:gap-dense">
                  <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-[#ef3e29] flex items-center justify-center shrink-0">
                    <BarChart3 className="w-3.5 h-3.5 md:w-4 md:h-4 text-foreground" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-base md:text-lg font-bold text-foreground">Dashboard</h1>
                    <p className="text-[10px] md:text-xs text-muted-foreground truncate">
                      {selectedClient
                        ? selectedClient.name
                        : dashboardData?.adAccountName || "Meta Ads"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {isDashboardOnly && user && (
              <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
                <div className="w-6 h-6 bg-[#ef3e29] rounded-full flex items-center justify-center">
                  <User className="w-3 h-3 text-foreground" />
                </div>
                <span className="text-xs text-muted-foreground hidden sm:inline">{user.email}</span>
                <Link href="/integracje">
                  <button
                    className="p-1.5 text-muted-foreground hover:text-orange-400 hover:bg-accent rounded-lg transition-colors"
                    title="Integracje API"
                  >
                    <Plug className="w-4 h-4" />
                  </button>
                </Link>
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                  title="Ustawienia"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => logoutMutation.mutate()}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                  title={t("dashboard.logoutTitle")}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
            {!isDashboardOnly && user && (
              <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                  title="Ustawienia"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Controls row: date, compare, account - stacks on mobile */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {/* Client Selector for employees/agency owners */}
            {isEmployee && myClients.length > 0 && (
              <Popover
                open={clientDropdownOpen}
                onOpenChange={(open) => {
                  setClientDropdownOpen(open);
                  if (!open) setClientSearch("");
                }}
              >
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 w-full sm:w-56 bg-muted border border-border text-foreground text-sm rounded-md hover:bg-accent transition-colors">
                    <Users className="w-3.5 h-3.5 text-[#ef3e29] shrink-0" />
                    <span className="truncate flex-1 text-left">
                      {selectedClient ? selectedClient.name : "Wybierz klienta"}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0 bg-card border-border" align="start">
                  <div className="p-2 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Szukaj klienta..."
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        className="pl-8 h-8 bg-accent border-border text-foreground text-xs placeholder:text-muted-foreground"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto py-1">
                    {myClients
                      .filter(
                        (c) =>
                          !clientSearch ||
                          c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                          (c.email && c.email.toLowerCase().includes(clientSearch.toLowerCase()))
                      )
                      .map((c) => (
                        <button
                          key={c.id}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent ${
                            selectedClientUserId === c.userId
                              ? "text-foreground bg-accent"
                              : "text-foreground"
                          }`}
                          onClick={() => {
                            setSelectedClient({ id: c.id, userId: c.userId, name: c.name });
                            setSelectedAdAccountId("");
                            setClientDropdownOpen(false);
                            setClientSearch("");
                          }}
                        >
                          <div className="font-medium">{c.name}</div>
                          {c.email && (
                            <div className="text-[10px] text-muted-foreground">{c.email}</div>
                          )}
                        </button>
                      ))}
                    {clientSearch &&
                      myClients.filter(
                        (c) =>
                          c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                          (c.email && c.email.toLowerCase().includes(clientSearch.toLowerCase()))
                      ).length === 0 && (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          {t("dashboard.account.noResults")}
                        </div>
                      )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {/* Ad Account Selector with search */}
            {adAccounts.length > 1 && (
              <Popover
                open={accountDropdownOpen}
                onOpenChange={(open) => {
                  setAccountDropdownOpen(open);
                  if (!open) setAccountSearch("");
                }}
              >
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 w-full sm:w-56 bg-muted border border-border text-foreground text-sm rounded-md hover:bg-accent transition-colors">
                    <span className="truncate flex-1 text-left">
                      {selectedAdAccountId
                        ? adAccounts.find((a) => a.id === selectedAdAccountId)?.name ||
                          selectedAdAccountId
                        : t("dashboard.account.default")}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0 bg-card border-border" align="start">
                  <div className="p-2 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Szukaj konta..."
                        value={accountSearch}
                        onChange={(e) => setAccountSearch(e.target.value)}
                        className="pl-8 h-8 bg-accent border-border text-foreground text-xs placeholder:text-muted-foreground"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto py-1">
                    {(!accountSearch ||
                      t("dashboard.account.default")
                        .toLowerCase()
                        .includes(accountSearch.toLowerCase())) && (
                      <button
                        className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent ${
                          !selectedAdAccountId ? "text-foreground bg-accent" : "text-foreground"
                        }`}
                        onClick={() => {
                          setSelectedAdAccountId("");
                          setAccountDropdownOpen(false);
                          setAccountSearch("");
                        }}
                      >
                        {t("dashboard.account.default")}
                      </button>
                    )}
                    {adAccounts
                      .filter(
                        (acc) =>
                          !accountSearch ||
                          acc.name.toLowerCase().includes(accountSearch.toLowerCase()) ||
                          acc.id.includes(accountSearch)
                      )
                      .map((acc) => (
                        <button
                          key={acc.id}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent ${
                            selectedAdAccountId === acc.id
                              ? "text-foreground bg-accent"
                              : "text-foreground"
                          }`}
                          onClick={() => {
                            setSelectedAdAccountId(acc.id);
                            setAccountDropdownOpen(false);
                            setAccountSearch("");
                          }}
                        >
                          {acc.name}
                        </button>
                      ))}
                    {accountSearch &&
                      adAccounts.filter(
                        (acc) =>
                          acc.name.toLowerCase().includes(accountSearch.toLowerCase()) ||
                          acc.id.includes(accountSearch)
                      ).length === 0 && (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          {t("dashboard.account.noResults")}
                        </div>
                      )}
                  </div>
                  <div className="p-2 border-t border-border">
                    <button
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-md bg-muted/60 border border-border text-foreground hover:bg-accent transition-colors"
                      onClick={() => {
                        setAccountDropdownOpen(false);
                        setAccountSearch("");
                        setSettingsOpen(true);
                      }}
                    >
                      <Plug className="w-3.5 h-3.5" />
                      Dodaj konto reklamowe
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            <DashboardControlsMenubar
              datePreset={datePreset}
              onDatePresetChange={(v) => {
                setDatePreset(v);
                if (v !== "custom") {
                  setCustomFrom(undefined);
                  setCustomTo(undefined);
                }
              }}
              comparePreset={comparePreset}
              onComparePresetChange={(v) => {
                setComparePreset(v);
                if (v !== "custom") {
                  setCompareFrom(undefined);
                  setCompareTo(undefined);
                }
              }}
              attributionModel={attributionModel}
              onAttributionModelChange={setAttributionModel}
              isAllegroMode={isAllegroMode}
            />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-dense">
          <Popover
            open={filterPanelOpen}
            onOpenChange={(open) => {
              setFilterPanelOpen(open);
              if (open) setFilterDraft(campaignFilter);
            }}
          >
            <PopoverTrigger asChild>
              <button
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  campaignFilter
                    ? "bg-[#ef3e29]/10 border-[#ef3e29]/40 text-foreground"
                    : "bg-muted border-border text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                <Filter className="w-4 h-4" />
                {t("dashboard.filters")}
                {campaignFilter && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#ef3e29] text-foreground rounded-full">
                    1
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-card border-border" align="start">
              <div className="p-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Filter className="w-3.5 h-3.5 text-[#ef3e29]" />
                  {t("dashboard.filtersCampaigns")}
                </h3>
              </div>
              <div className="p-3 space-y-3">
                <Field>
                  <FieldLabel className="text-muted-foreground">
                    {t("dashboard.filterByCampaignName")}
                  </FieldLabel>
                  <Input
                    placeholder={t("dashboard.filterKeywordPlaceholder")}
                    value={filterDraft}
                    onChange={(e) => setFilterDraft(e.target.value)}
                    className="bg-accent border-border text-foreground text-sm placeholder:text-muted-foreground h-9"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setCampaignFilter(filterDraft);
                        setFilterPanelOpen(false);
                      }
                    }}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t("dashboard.filterKeywordHint")}
                  </p>
                </Field>
              </div>
              <div className="p-3 border-t border-border flex items-center gap-2">
                {campaignFilter && (
                  <button
                    onClick={() => {
                      setCampaignFilter("");
                      setFilterDraft("");
                      setFilterPanelOpen(false);
                      try {
                        localStorage.removeItem("dashboard_campaign_filter");
                      } catch {}
                    }}
                    className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
                  >
                    {t("dashboard.filterClear")}
                  </button>
                )}
                <div className="flex-1" />
                <button
                  onClick={() => {
                    setCampaignFilter(filterDraft);
                    setFilterPanelOpen(false);
                  }}
                  className="px-3 py-1.5 text-xs text-foreground bg-[#ef3e29] hover:bg-[#d63520] rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Check className="w-3 h-3" />
                  {t("dashboard.filterApply")}
                </button>
                <button
                  onClick={() => {
                    setCampaignFilter(filterDraft);
                    setFilterPanelOpen(false);
                    try {
                      localStorage.setItem("dashboard_campaign_filter", filterDraft);
                    } catch {}
                  }}
                  className="px-3 py-1.5 text-xs text-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Save className="w-3 h-3" />
                  {t("dashboard.filterSave")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
          {campaignFilter && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-muted border border-border rounded-lg">
              <span className="text-xs text-muted-foreground">
                {t("dashboard.filterNameLabel")}
              </span>
              <span className="text-xs text-foreground font-medium">{campaignFilter}</span>
              <button
                onClick={() => {
                  setCampaignFilter("");
                  setFilterDraft("");
                  try {
                    localStorage.removeItem("dashboard_campaign_filter");
                  } catch {}
                }}
                className="ml-1 p-0.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
              <span className="text-[10px] text-muted-foreground ml-1">
                {filteredCampaigns.length}/{allCampaigns?.length || 0}
              </span>
            </div>
          )}
        </div>

        {/* Custom date range pickers */}
        {(datePreset === "custom" || comparePreset === "custom") && (
          <div className="flex flex-wrap gap-4 p-4 rounded-xl bg-muted/50 border border-border">
            {datePreset === "custom" && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Okres:</span>
                <DropdownRangeDatePicker
                  from={customFrom}
                  to={customTo}
                  onApply={(f, t) => {
                    setCustomFrom(f);
                    setCustomTo(t);
                  }}
                />
              </div>
            )}
            {comparePreset === "custom" && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {t("dashboard.account.compareWith")}
                </span>
                <DropdownRangeDatePicker
                  from={compareFrom}
                  to={compareTo}
                  onApply={(f, t) => {
                    setCompareFrom(f);
                    setCompareTo(t);
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Comparison period label */}
        {dashboardData?.comparison?.label && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/60 border border-border">
            <ArrowUpDown className="w-3.5 h-3.5 text-[#ef3e29]" />
            <span className="text-xs text-muted-foreground">
              {t("dashboard.comparisonWith")}{" "}
              <span className="text-foreground font-medium">{dashboardData.comparison.label}</span>
            </span>
          </div>
        )}

        {/* Dashboard mode badge */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            {t("dashboard.modeLabel")}:
          </span>
          <span className="px-2 py-0.5 text-[10px] font-medium text-muted-foreground bg-muted border border-border rounded-full">
            {isAllegroMode
              ? t("dashboard.modes.ecommerceAllegro")
              : DASHBOARD_MODES.find((m) => m.value === dashboardMode)?.label ||
                t("dashboard.modes.leadGenBasic")}
          </span>
        </div>

        {/* Tab navigation */}
        {!isAllegroMode && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 p-1 bg-muted border border-border rounded-lg w-full sm:w-fit">
              <button
                onClick={() => setActiveTab("overview")}
                className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === "overview"
                    ? "bg-[#ef3e29] text-foreground"
                    : "text-muted-foreground hover:text-foreground bg-accent hover:bg-[#1f2330]"
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5 inline mr-1.5" />
                {t("dashboard.tabs.overview")}
              </button>
              {dashboardMode === "lead_gen_advanced" && (
                <button
                  onClick={() => setActiveTab("setter")}
                  className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === "setter"
                      ? "bg-[#ef3e29] text-foreground"
                      : "text-muted-foreground hover:text-foreground bg-accent hover:bg-[#1f2330]"
                  }`}
                >
                  <PhoneCall className="w-3.5 h-3.5 inline mr-1.5" />
                  {t("dashboard.tabs.setter")}
                </button>
              )}
              {showLeadQuality && (
                <button
                  onClick={() => setActiveTab("lead_quality")}
                  className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === "lead_quality"
                      ? "bg-purple-600 text-foreground"
                      : "text-muted-foreground hover:text-foreground bg-accent hover:bg-[#1f2330]"
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5 inline mr-1.5" />
                  {t("dashboard.tabs.leadQuality")}
                </button>
              )}
              <button
                onClick={() => setActiveTab("dane")}
                className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === "dane"
                    ? "bg-[#ef3e29] text-foreground"
                    : "text-muted-foreground hover:text-foreground bg-accent hover:bg-[#1f2330]"
                }`}
              >
                <Layers className="w-3.5 h-3.5 inline mr-1.5" />
                {t("dashboard.tabs.data")}
              </button>
              <button
                onClick={() => setActiveTab("roas_world")}
                className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                  activeTab === "roas_world"
                    ? "bg-[#ef3e29] text-foreground"
                    : "text-muted-foreground hover:text-foreground bg-accent hover:bg-[#1f2330]"
                }`}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                {t("dashboard.tabs.roasWorld")}
              </button>
            </div>
            <button
              onClick={() => setReportOpen(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-muted border border-border text-muted-foreground hover:text-foreground hover:bg-[#1f2330] flex items-center gap-1.5"
              title={t("dashboard.reportTitle")}
            >
              <FileText className="w-3.5 h-3.5" />
              {t("dashboard.report")}
            </button>
          </div>
        )}

        {dashboardPreview && (
          <PreviewBanner
            title={previewBannerConfig.title}
            description={previewBannerConfig.description}
            ctaLabel={previewBannerConfig.ctaLabel}
            ctaHref={previewBannerConfig.ctaHref}
            showConnectCta={previewBannerConfig.showConnectCta}
            secondaryLabel={t("dashboard.preview.openSettings")}
            onSecondaryClick={() => setSettingsOpen(true)}
            agencyHint={
              (user as any)?.role === "agency_owner" &&
              (dashboardData?.preview?.reason === "no_accounts" ||
                dashboardData?.preview?.reason === "no_agency")
                ? {
                    body: t("dashboard.preview.agencyHintBody"),
                    ctaLabel: t("dashboard.preview.agencyHintCta"),
                    ctaHref: "/moiklienci",
                  }
                : undefined
            }
          />
        )}

        {dashboardMode === "saas" && (
          <SaasFunnelCards
            datePreset={datePreset}
            customRange={
              datePreset === "custom" && customFrom && customTo
                ? { since: fmtDate(customFrom), until: fmtDate(customTo) }
                : undefined
            }
            spend={totals?.spend ?? 0}
          />
        )}

        {isAllegroMode ? (
          /* ═══ ALLEGRO DASHBOARD ═══ */
          isAllegroLoading ? (
            <DashboardSkeleton
              kpiCount={4}
              gridClassName="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-dense"
            />
          ) : allegroError ? (
            <div className="flex items-center justify-center py-32">
              <Card className="bg-card border-border max-w-md">
                <CardContent className="p-6 text-center">
                  <ShoppingCart className="w-10 h-10 text-[#FF5A00]/40 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-2">
                    {t("dashboard.allegro.connectTitle")}
                  </p>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t("dashboard.allegro.connectDesc")}
                  </p>
                  <button
                    onClick={() => setSettingsOpen(true)}
                    className="px-4 py-2 bg-[#FF5A00] hover:bg-[#e55200] text-foreground text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    {t("dashboard.allegro.connectButton")}
                  </button>
                </CardContent>
              </Card>
            </div>
          ) : allegroData ? (
            (() => {
              const comp = allegroData.comparison;
              const compT = comp?.totals;
              const compA = comp?.ads;
              const adsInterest = allegroData.ads?.interest || 0;
              const adsConversions = allegroData.ads?.conversions || 0;
              const organicRevenue = Math.max(0, allegroData.totals.revenue - adsInterest);
              const organicUnits = Math.max(0, allegroData.totals.unitsSold - adsConversions);
              const compAdsInterest = compA?.interest || 0;
              const compOrganicRevenue = compT ? Math.max(0, compT.revenue - compAdsInterest) : 0;
              const compOrganicUnits = compT
                ? Math.max(0, compT.unitsSold - (compA?.conversions || 0))
                : 0;

              // Build daily organic data by subtracting ads from store totals
              const adsDaily = allegroData.ads?.daily || [];
              const adsDailyMap: Record<string, (typeof adsDaily)[0]> = {};
              for (const d of adsDaily) adsDailyMap[d.date] = d;
              const dailyOrganic = (allegroData.dailyOrders || []).map((d) => {
                const ad = adsDailyMap[d.date];
                return {
                  date: d.date,
                  revenue: Math.max(0, d.revenue - (ad?.interest || 0)),
                  unitsSold: Math.max(0, d.unitsSold - (ad?.conversions || 0)),
                };
              });

              // Build ads daily with CPC and ROAS computed
              const adsDailyEnriched = adsDaily.map((d) => ({
                ...d,
                cpc: d.clicks > 0 ? d.spend / d.clicks : 0,
                roas: d.spend > 0 ? d.interest / d.spend : 0,
              }));

              return (
                <div className="space-y-6">
                  {/* Allegro Account Header */}
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#FF5A00] flex items-center justify-center">
                      <span className="text-foreground font-bold text-sm">A</span>
                    </div>
                    <span className="text-sm text-muted-foreground">Konto Allegro:</span>
                    <span className="text-sm text-foreground font-medium">
                      {allegroData.allegroLogin}
                    </span>
                  </div>

                  {/* ═══ KPI CARDS ═══ */}
                  <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#FF5A00]" />
                      {t("dashboard.allegroSection.revenueAndSales")}
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-dense">
                      <DashboardMetricCard
                        label={t("dashboard.allegroSection.revenueTotal")}
                        value={formatCurrency(allegroData.totals.revenue)}
                        icon={DollarSign}
                        variant="primary"
                        delta={compT ? calcDelta(allegroData.totals.revenue, compT.revenue) : null}
                      />
                      <DashboardMetricCard
                        label={t("dashboard.allegroSection.revenueAds")}
                        value={formatCurrency(adsInterest)}
                        icon={Target}
                        variant="primary"
                        delta={compA ? calcDelta(adsInterest, compAdsInterest) : null}
                      />
                      <DashboardMetricCard
                        label={t("dashboard.allegroSection.revenueOrganic")}
                        value={formatCurrency(organicRevenue)}
                        icon={TrendingUp}
                        variant="highlight"
                        delta={compT ? calcDelta(organicRevenue, compOrganicRevenue) : null}
                      />
                      <DashboardMetricCard
                        label="ROAS (Ads)"
                        value={
                          allegroData.ads && allegroData.ads.returnOnAdSpend > 0
                            ? `${allegroData.ads.returnOnAdSpend.toFixed(2)}x`
                            : "—"
                        }
                        icon={TrendingUp}
                        variant="highlight"
                        delta={
                          compA && compA.returnOnAdSpend > 0 && allegroData.ads
                            ? calcDelta(allegroData.ads.returnOnAdSpend, compA.returnOnAdSpend)
                            : null
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-dense">
                      <DashboardMetricCard
                        label={t("dashboard.allegroSection.salesTotal")}
                        value={formatNumber(allegroData.totals.unitsSold)}
                        icon={ShoppingCart}
                        delta={
                          compT ? calcDelta(allegroData.totals.unitsSold, compT.unitsSold) : null
                        }
                      />
                      <DashboardMetricCard
                        label={t("dashboard.allegroSection.salesAds")}
                        value={formatNumber(adsConversions)}
                        icon={Target}
                        delta={compA ? calcDelta(adsConversions, compA.conversions) : null}
                      />
                      <DashboardMetricCard
                        label={t("dashboard.allegroSection.salesOrganic")}
                        value={formatNumber(organicUnits)}
                        icon={TrendingUp}
                        delta={compT ? calcDelta(organicUnits, compOrganicUnits) : null}
                      />
                      <DashboardMetricCard
                        label="Wydatki Ads"
                        value={formatCurrency(allegroData.ads?.spend || 0)}
                        icon={DollarSign}
                        invertDelta
                        delta={compA ? calcDelta(allegroData.ads?.spend || 0, compA.spend) : null}
                      />
                    </div>
                  </div>

                  {/* WYKRES 1: CALY SKLEP */}
                  <AllegroChart
                    title={t("dashboard.allegroSection.chartStore")}
                    chartId="store"
                    data={allegroData.dailyOrders || []}
                    metrics={[
                      {
                        key: "revenue",
                        label: t("dashboard.allegroSection.chartRevenue"),
                        color: "#FF5A00",
                        format: "currency",
                        yAxis: "left",
                      },
                      {
                        key: "unitsSold",
                        label: "Sprzedane szt.",
                        color: "#a855f7",
                        format: "number",
                        yAxis: "right",
                      },
                    ]}
                  />

                  {/* ═══ WYKRES 2: ADS ONLY ═══ */}
                  {allegroData.ads && (
                    <AllegroChart
                      title="Allegro Ads — wykres dzienny"
                      chartId="ads"
                      data={adsDailyEnriched}
                      metrics={[
                        {
                          key: "spend",
                          label: "Wydatki",
                          color: "#FF5A00",
                          format: "currency",
                          yAxis: "left",
                        },
                        {
                          key: "interest",
                          label: t("dashboard.allegroSection.chartInterest"),
                          color: "#22c55e",
                          format: "currency",
                          yAxis: "left",
                        },
                        {
                          key: "clicks",
                          label: t("dashboard.allegroSection.colClicks"),
                          color: "#3b82f6",
                          format: "number",
                          yAxis: "right",
                        },
                        {
                          key: "cpc",
                          label: "CPC",
                          color: "#f59e0b",
                          format: "currency",
                          yAxis: "left",
                        },
                        {
                          key: "roas",
                          label: "ROAS",
                          color: "#06b6d4",
                          format: "number",
                          yAxis: "left",
                        },
                        {
                          key: "conversions",
                          label: "Konwersje",
                          color: "#ec4899",
                          format: "number",
                          yAxis: "right",
                        },
                      ]}
                    />
                  )}

                  {/* ═══ WYKRES 3: ORGANIC ONLY ═══ */}
                  <AllegroChart
                    title="Organic — wykres dzienny"
                    chartId="organic"
                    data={dailyOrganic}
                    metrics={[
                      {
                        key: "revenue",
                        label: t("dashboard.allegroSection.chartOrganicRevenue"),
                        color: "#22c55e",
                        format: "currency",
                        yAxis: "left",
                      },
                      {
                        key: "unitsSold",
                        label: "Sprzedane szt. organic",
                        color: "#a855f7",
                        format: "number",
                        yAxis: "right",
                      },
                    ]}
                  />

                  {/* ═══ KAMPANIE REKLAMOWE ═══ */}
                  {allegroData.ads && allegroData.ads.campaigns.length > 0 && (
                    <div className="space-y-3">
                      <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-[#FF5A00]" />
                        Kampanie reklamowe
                      </h2>
                      <div className="bg-muted/40 border border-border rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="text-left text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  Kampania
                                </th>
                                <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  Status
                                </th>
                                <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  Wydatki
                                </th>
                                <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  {t("dashboard.allegroSection.colRevenue")}
                                </th>
                                <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  ROAS
                                </th>
                                <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  {t("dashboard.allegroSection.colClicks")}
                                </th>
                                <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  CTR
                                </th>
                                <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                  CPC
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {allegroData.ads.campaigns
                                .sort((a, b) => b.spend - a.spend)
                                .map((c, idx) => (
                                  <tr
                                    key={c.id}
                                    className={`border-b border-border hover:bg-muted/50 transition-colors ${idx % 2 === 0 ? "bg-muted/30" : ""}`}
                                  >
                                    <td className="px-4 py-3 text-xs text-foreground font-medium max-w-[250px] truncate">
                                      {c.name}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded ${c.status === "ACTIVE" ? "bg-emerald-500/20 text-emerald-400" : "bg-accent text-muted-foreground"}`}
                                      >
                                        {c.status === "ACTIVE" ? "Aktywna" : c.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs text-foreground font-medium">
                                      {formatCurrency(c.spend)}
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs text-emerald-400 font-medium">
                                      {formatCurrency(c.interest)}
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs text-foreground font-medium">
                                      {c.spend > 0 ? `${(c.interest / c.spend).toFixed(2)}x` : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                                      {formatNumber(c.clicks)}
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                                      {c.ctr.toFixed(2)}%
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                                      {formatCurrency(c.avgCpc)}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Offers Table */}
                  <div className="space-y-3">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#FF5A00]" />
                      Top oferty
                    </h2>
                    <div className="bg-muted/40 border border-border rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                Oferta
                              </th>
                              <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                Cena
                              </th>
                              <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                Sprzedano
                              </th>
                              <th className="text-right text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-4 py-3">
                                Odwiedziny
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {allegroData.topOffers.map((offer, idx) => (
                              <tr
                                key={offer.id}
                                className={`border-b border-border hover:bg-muted/50 transition-colors ${idx % 2 === 0 ? "bg-muted/30" : ""}`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-dense min-w-0">
                                    {offer.image && (
                                      <img
                                        src={offer.image}
                                        alt=""
                                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-border"
                                      />
                                    )}
                                    <div className="min-w-0">
                                      <a
                                        href={`https://allegro.pl/oferta/${offer.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-foreground text-xs font-medium hover:text-[#FF5A00] transition-colors truncate block max-w-[300px]"
                                      >
                                        {offer.name}
                                      </a>
                                      <span
                                        className={`text-[10px] ${offer.status === "ACTIVE" ? "text-emerald-400" : "text-muted-foreground"}`}
                                      >
                                        {offer.status === "ACTIVE" ? "Aktywna" : offer.status}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right text-xs text-foreground font-medium">
                                  {offer.price || "—"}
                                </td>
                                <td className="px-4 py-3 text-right text-xs font-semibold text-[#FF5A00]">
                                  {formatNumber(offer.sold)}
                                </td>
                                <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                                  {formatNumber(offer.visits)}
                                </td>
                              </tr>
                            ))}
                            {allegroData.topOffers.length === 0 && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-4 py-8 text-center text-muted-foreground text-sm"
                                >
                                  {t("dashboard.allegroSection.noOffers")}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : null
        ) : isLoading ? (
          <DashboardSkeleton kpiCount={6} />
        ) : error || metaStatus?.tokenExpired ? (
          <div className="flex items-center justify-center py-32">
            <Card className="bg-card border-border max-w-md">
              <CardContent className="p-6 text-center">
                <BarChart3 className="w-10 h-10 text-[#ef3e29]/40 mx-auto mb-3" />
                <p className="text-muted-foreground mb-2">
                  {metaStatus?.tokenExpired || (error as Error)?.message?.includes("tokenExpired")
                    ? metaStatus?.assignedByAdmin
                      ? t("dashboard.metaConnect.tokenExpiredAdmin")
                      : t("dashboard.metaConnect.noConnection")
                    : selectedClient
                      ? t("dashboard.metaConnect.noAccountClient", { name: selectedClient.name })
                      : t("dashboard.metaConnect.noConnection")}
                </p>
                <p className="text-muted-foreground text-sm mb-4">
                  {metaStatus?.tokenExpired || (error as Error)?.message?.includes("tokenExpired")
                    ? metaStatus?.assignedByAdmin
                      ? t("dashboard.metaConnect.renewHintAdmin")
                      : t("dashboard.metaConnect.connectHint")
                    : selectedClient
                      ? t("dashboard.metaConnect.assignHintClient", {
                          name: selectedClient?.name || "",
                        })
                      : t("dashboard.metaConnect.connectHint")}
                </p>
                {!metaStatus?.assignedByAdmin && (
                  <button
                    onClick={() => setSettingsOpen(true)}
                    className="px-4 py-2 bg-[#ef3e29] hover:bg-[#d63520] text-foreground text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    {metaStatus?.tokenExpired || (error as Error)?.message?.includes("tokenExpired")
                      ? t("dashboard.metaConnect.renewButton")
                      : t("dashboard.metaConnect.connectButton")}
                  </button>
                )}
              </CardContent>
            </Card>
          </div>
        ) : totals ? (
          <>
            {/* Setter Tab Content (lead_gen_advanced only) */}
            {dashboardMode === "lead_gen_advanced" && activeTab === "setter" ? (
              <SetterTabContent
                stats={effectiveSetterStats}
                isLoading={isSetterLoading && !dashboardPreview}
                isError={isSetterError && !dashboardPreview}
                onRetry={() => refetchSetterStats()}
                onOpenSettings={() => setSettingsOpen(true)}
                metaTotals={totals}
                metaDaily={dailyData?.daily || []}
                forUserId={forClientUserId}
                aiAnalysisEnabled={analyticsSettingsData?.aiCallAnalysisEnabled}
                aiAnalysisModel={analyticsSettingsData?.aiCallAnalysisModel}
                setterCallSource={analyticsSettingsData?.setterCallSource}
              />
            ) : activeTab === "dane" ? (
              /* ═══ Dane Tab - Daily Data Table (ExcelTable) ═══ */
              (() => {
                // Prefer Supabase-cached full history; fall back to localStorage
                // cache + current period if the history endpoint hasn't loaded yet.
                const historyDays = dailyHistoryData?.daily || [];
                const cachedDays = Object.values(daneCache);
                const currentDays = dailyData?.daily || [];
                const mergedMap: Record<string, DailyInsight> = {};
                for (const d of cachedDays) mergedMap[d.date] = d;
                for (const d of currentDays) mergedMap[d.date] = d;
                for (const d of historyDays) mergedMap[d.date] = d;
                // The merged set spans the full cached history; clip it to the selected
                // date range so the table mirrors the chosen preset (e.g. last_30d must
                // not show March). Open ranges (incomplete custom) skip clipping.
                const { since: daneSince, until: daneUntil } = resolveDaneRange(
                  datePreset,
                  customFrom,
                  customTo
                );
                const daily = Object.values(mergedMap)
                  .filter(
                    (d) =>
                      (!daneSince || d.date >= daneSince) && (!daneUntil || d.date <= daneUntil)
                  )
                  .sort((a, b) => a.date.localeCompare(b.date));

                const setterByDay = new Map<
                  string,
                  {
                    attempts: number;
                    answered: number;
                    booked: number;
                    showed: number;
                    sales: number;
                  }
                >();
                if (effectiveSetterStats?.dailyStats) {
                  for (const d of effectiveSetterStats.dailyStats) setterByDay.set(d.date, d);
                }

                const isAutowebinar = dashboardMode === "autowebinar_book_a_call";
                const isVsl = dashboardMode === "vsl_funnel";
                const isFunnelMode = isAutowebinar || isVsl;
                const isAdvanced = dashboardMode === "lead_gen_advanced";

                // Webinar daily attendance data
                const webinarByDay = dailyData?.webinarDaily ?? {};

                const dayNames = (t("dashboard.daneTab.dayNames", {
                  returnObjects: true,
                }) as string[]) || ["Nd", "Pon", "Wt", "Sr", "Czw", "Pt", "Sob"];
                const fmtPLN = (v: number) => {
                  const n = v ?? 0;
                  return n === 0 ? "—" : dc.fmt(n);
                };
                const fmtNum = (v: number) => {
                  const n = v ?? 0;
                  return n === 0 ? "—" : n.toLocaleString("pl-PL");
                };
                const fmtPct = (v: number) => {
                  const n = v ?? 0;
                  return n === 0 ? "—" : n.toFixed(2) + "%";
                };

                // Build headers
                const headers = [
                  t("dashboard.daneTab.colDay"),
                  t("dashboard.daneTab.colDate"),
                  t("dashboard.daneTab.colSpend"),
                  t("dashboard.daneTab.colImpressions"),
                  "CPM",
                  t("dashboard.daneTab.colClicks"),
                  "CTR%",
                  "CPC",
                  t("dashboard.daneTab.colLeads"),
                  "CLC%",
                  "CPL",
                  ...(isAutowebinar
                    ? [
                        t("dashboard.daneTab.colAttended"),
                        t("dashboard.daneTab.colViewRate"),
                        t("dashboard.daneTab.colCpAtt"),
                      ]
                    : []),
                  ...(isFunnelMode
                    ? [
                        t("dashboard.daneTab.colNewBooked"),
                        "LBCR%",
                        "CPBC",
                        t("dashboard.daneTab.colBookedToday"),
                        t("dashboard.daneTab.colShowed"),
                        "SUR%",
                        t("dashboard.daneTab.colCostPerShowed"),
                        t("dashboard.daneTab.colClients"),
                        "CR%",
                        t("dashboard.daneTab.colContracted"),
                        t("dashboard.daneTab.colBooked2"),
                        "CPA",
                        "R-ROAS",
                      ]
                    : []),
                  ...(isAdvanced
                    ? [
                        t("dashboard.daneTab.colAttempts"),
                        t("dashboard.daneTab.colAnswered"),
                        t("dashboard.daneTab.colBooked"),
                        t("dashboard.daneTab.colRealised"),
                        t("dashboard.daneTab.colSales"),
                      ]
                    : []),
                  t("dashboard.daneTab.colNote"),
                ];
                const noteColIdx = headers.length - 1;
                const notePlaceholder = t("dashboard.daneTab.notePlaceholder");
                const truncateNote = (text: string) =>
                  text.length > 60 ? text.slice(0, 57) + "…" : text;

                // Build totals row
                const totRow = daily.reduce(
                  (acc, d) => {
                    const s = setterByDay.get(d.date);
                    const w = webinarByDay[d.date];
                    return {
                      spend: acc.spend + (d.spend ?? 0),
                      impressions: acc.impressions + (d.impressions ?? 0),
                      clicks: acc.clicks + (d.clicks ?? 0),
                      outboundClicks: acc.outboundClicks + (d.outboundClicks ?? 0),
                      uniqueOutboundClicks:
                        acc.uniqueOutboundClicks + (d.uniqueOutboundClicks ?? 0),
                      reach: acc.reach + (d.reach ?? 0),
                      leads: acc.leads + (d.leads ?? 0),
                      booked: acc.booked + (s?.booked ?? d.bookedMeetings ?? 0),
                      showed: acc.showed + (s?.showed ?? 0),
                      sales: acc.sales + (s?.sales ?? 0),
                      attempts: acc.attempts + (s?.attempts ?? 0),
                      answered: acc.answered + (s?.answered ?? 0),
                      attended: acc.attended + (w?.attended ?? 0),
                    };
                  },
                  {
                    spend: 0,
                    impressions: 0,
                    clicks: 0,
                    outboundClicks: 0,
                    uniqueOutboundClicks: 0,
                    reach: 0,
                    leads: 0,
                    booked: 0,
                    showed: 0,
                    sales: 0,
                    attempts: 0,
                    answered: 0,
                    attended: 0,
                  }
                );

                const totCPM =
                  totRow.impressions > 0 ? (totRow.spend / totRow.impressions) * 1000 : 0;
                // PO: CPC = spend / unique outbound; CTR = unique outbound / reach.
                const totCTR =
                  totRow.reach > 0 ? (totRow.uniqueOutboundClicks / totRow.reach) * 100 : 0;
                const totCPC =
                  totRow.uniqueOutboundClicks > 0 ? totRow.spend / totRow.uniqueOutboundClicks : 0;
                const totCLC =
                  totRow.outboundClicks > 0 ? (totRow.leads / totRow.outboundClicks) * 100 : 0;
                const totCPL = totRow.leads > 0 ? totRow.spend / totRow.leads : 0;
                const totLBCR = totRow.leads > 0 ? (totRow.booked / totRow.leads) * 100 : 0;
                const totCPBC = totRow.booked > 0 ? totRow.spend / totRow.booked : 0;
                const totSUR = totRow.booked > 0 ? (totRow.showed / totRow.booked) * 100 : 0;
                const totCPSH = totRow.showed > 0 ? totRow.spend / totRow.showed : 0;
                const totCR = totRow.showed > 0 ? (totRow.sales / totRow.showed) * 100 : 0;
                const totCPA = totRow.sales > 0 ? totRow.spend / totRow.sales : 0;
                const totWebShowRate =
                  totRow.leads > 0 ? (totRow.attended / totRow.leads) * 100 : 0;
                const totCPATT = totRow.attended > 0 ? totRow.spend / totRow.attended : 0;

                const totalsRow: string[] = [
                  t("dashboard.daneTab.totalsSymbol"),
                  t("dashboard.daneTab.totalsLabel"),
                  fmtPLN(totRow.spend),
                  fmtNum(totRow.impressions),
                  fmtPLN(totCPM),
                  fmtNum(totRow.outboundClicks),
                  fmtPct(totCTR),
                  fmtPLN(totCPC),
                  fmtNum(totRow.leads),
                  fmtPct(totCLC),
                  fmtPLN(totCPL),
                  ...(isAutowebinar
                    ? [fmtNum(totRow.attended), fmtPct(totWebShowRate), fmtPLN(totCPATT)]
                    : []),
                  ...(isFunnelMode
                    ? [
                        fmtNum(totRow.booked),
                        fmtPct(totLBCR),
                        fmtPLN(totCPBC),
                        fmtNum(totRow.booked),
                        fmtNum(totRow.showed),
                        fmtPct(totSUR),
                        fmtPLN(totCPSH),
                        fmtNum(totRow.sales),
                        fmtPct(totCR),
                        "—",
                        "—",
                        fmtPLN(totCPA),
                        "—",
                      ]
                    : []),
                  ...(isAdvanced
                    ? [
                        fmtNum(totRow.attempts),
                        fmtNum(totRow.answered),
                        fmtNum(totRow.booked),
                        fmtNum(totRow.showed),
                        fmtNum(totRow.sales),
                      ]
                    : []),
                  "",
                ];

                // Build daily rows
                const dailyRows: string[][] = daily.map((d) => {
                  const dt = new Date(d.date + "T12:00:00");
                  const dayName = dayNames[dt.getDay()];
                  const dateStr = dt.toLocaleDateString("pl-PL", {
                    day: "2-digit",
                    month: "2-digit",
                  });
                  const s = setterByDay.get(d.date);
                  const booked = s?.booked ?? d.bookedMeetings ?? 0;
                  const showed = s?.showed ?? 0;
                  const sales = s?.sales ?? 0;
                  const w = webinarByDay[d.date];
                  const attended = w?.attended ?? 0;
                  const rowCpm = d.impressions > 0 ? (d.spend / d.impressions) * 1000 : 0;
                  // CTR/CPC already unique-outbound from the backend (CTR over reach).
                  const rowCtr = d.ctr;
                  const rowClc = d.outboundClicks > 0 ? (d.leads / d.outboundClicks) * 100 : 0;
                  const rowLbcr = d.leads > 0 ? (booked / d.leads) * 100 : 0;
                  const rowCpbc = booked > 0 ? d.spend / booked : 0;
                  const rowSur = booked > 0 ? (showed / booked) * 100 : 0;
                  const rowCpsh = showed > 0 ? d.spend / showed : 0;
                  const rowCr = showed > 0 ? (sales / showed) * 100 : 0;
                  const rowCpa = sales > 0 ? d.spend / sales : 0;

                  return [
                    dayName,
                    dateStr,
                    fmtPLN(d.spend),
                    fmtNum(d.impressions),
                    fmtPLN(rowCpm),
                    fmtNum(d.outboundClicks),
                    fmtPct(rowCtr),
                    fmtPLN(d.cpc),
                    fmtNum(d.leads),
                    fmtPct(rowClc),
                    d.leads > 0 ? fmtPLN(d.costPerLead) : "—",
                    ...(isAutowebinar
                      ? [
                          fmtNum(attended),
                          d.leads > 0 ? fmtPct((attended / d.leads) * 100) : "—",
                          attended > 0 ? fmtPLN(d.spend / attended) : "—",
                        ]
                      : []),
                    ...(isFunnelMode
                      ? [
                          fmtNum(booked),
                          fmtPct(rowLbcr),
                          booked > 0 ? fmtPLN(rowCpbc) : "—",
                          fmtNum(booked),
                          fmtNum(showed),
                          fmtPct(rowSur),
                          showed > 0 ? fmtPLN(rowCpsh) : "—",
                          fmtNum(sales),
                          fmtPct(rowCr),
                          "—",
                          "—",
                          sales > 0 ? fmtPLN(rowCpa) : "—",
                          "—",
                        ]
                      : []),
                    ...(isAdvanced
                      ? [
                          fmtNum(s?.attempts ?? 0),
                          fmtNum(s?.answered ?? 0),
                          fmtNum(booked),
                          fmtNum(showed),
                          fmtNum(sales),
                        ]
                      : []),
                    dayNotes[d.date] ? truncateNote(dayNotes[d.date]) : notePlaceholder,
                  ];
                });

                const tableData = daily.length ? [totalsRow, ...dailyRows] : [];

                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                        <Layers className="w-4 h-4 text-blue-400" />
                        {t("dashboard.daneTab.title")}
                      </h2>
                      <span className="text-xs text-muted-foreground">
                        {t("dashboard.daneTab.historyCount", { count: daily.length })}
                      </span>
                    </div>
                    {(isDailyLoading || isDailyHistoryLoading) && !daily.length ? (
                      <TableSkeleton rows={8} columns={6} />
                    ) : !daily.length ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground text-sm">
                            {t("dashboard.daneTab.noData")}
                          </p>
                          <p className="text-muted-foreground text-xs mt-1">
                            {t("dashboard.daneTab.noDataHint")}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <ExcelTable
                        data={tableData}
                        headers={headers}
                        editable={false}
                        cellClassName={(row, col, value) => {
                          if (col !== noteColIdx) return undefined;
                          if (row === 0) return "text-left";
                          const isEmpty = value === notePlaceholder;
                          return [
                            "cursor-pointer text-left hover:bg-accent",
                            "whitespace-normal break-words min-w-[220px] max-w-[320px] leading-snug",
                            isEmpty ? "text-[#ef3e29]/70 italic" : "text-foreground font-medium",
                          ].join(" ");
                        }}
                        onCellClick={(row, col) => {
                          if (col !== noteColIdx || row === 0) return;
                          const dayRow = daily[row - 1];
                          if (!dayRow) return;
                          setNoteEditorDate(dayRow.date);
                          setNoteEditorDraft(dayNotes[dayRow.date] ?? "");
                        }}
                      />
                    )}
                  </div>
                );
              })()
            ) : activeTab === "lead_quality" && showLeadQuality ? (
              /* ═══ Lead Quality AI Tab ═══ */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    {t("dashboard.leadQuality.title")}
                  </h2>
                  <button
                    onClick={() => {
                      setAnalyzingQuality(true);
                      analyzeQualityMutation.mutate();
                    }}
                    disabled={analyzingQuality}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-purple-300 bg-purple-500/10 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 transition-colors disabled:opacity-50"
                  >
                    {analyzingQuality ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />{" "}
                        {t("dashboard.leadQuality.analyzing")}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />{" "}
                        {leadQualityData?.hasData
                          ? t("dashboard.leadQuality.refresh")
                          : t("dashboard.leadQuality.analyze")}
                      </>
                    )}
                  </button>
                </div>

                {isLeadQualityLoading ? (
                  <KpiGridSkeleton
                    count={4}
                    className="grid grid-cols-2 sm:grid-cols-4 gap-dense"
                  />
                ) : leadQualityData?.hasData ? (
                  <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-dense">
                      <Card className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/20 rounded-xl">
                        <CardContent className="p-3 md:p-5">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center">
                              <Star className="w-3.5 h-3.5 text-purple-400" />
                            </div>
                            <span className="text-[10px] md:text-xs text-muted-foreground uppercase tracking-wide">
                              {t("dashboard.leadQuality.avgScore")}
                            </span>
                          </div>
                          <div className="text-heading-2 md:text-heading-1 font-extrabold text-foreground">
                            {leadQualityData.averageScore?.toFixed(1)}
                            <span className="text-sm text-muted-foreground font-normal">/10</span>
                          </div>
                        </CardContent>
                      </Card>
                      <DashboardMetricCard
                        label={t("dashboard.leadQuality.scoredLeads")}
                        value={formatNumber(leadQualityData.scoredLeads || 0)}
                        icon={Users}
                      />
                      <DashboardMetricCard
                        label={t("dashboard.leadQuality.hotLeads")}
                        value={formatNumber(
                          leadQualityData.scores?.filter((s) => s.qualityScore >= 7).length || 0
                        )}
                        icon={TrendingUp}
                        variant="highlight"
                      />
                      <DashboardMetricCard
                        label="Zimne (1-3)"
                        value={formatNumber(
                          leadQualityData.scores?.filter((s) => s.qualityScore <= 3).length || 0
                        )}
                        icon={TrendingDown}
                      />
                    </div>

                    {/* Campaign Quality Breakdown */}
                    {leadQualityData.byCampaign &&
                      Object.keys(leadQualityData.byCampaign).length > 0 && (
                        <div className="space-y-2">
                          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t("dashboard.leadQuality.byCampaign")}
                          </h3>
                          <div className="space-y-1.5">
                            {Object.entries(leadQualityData.byCampaign)
                              .sort(([, a], [, b]) => b.avg - a.avg)
                              .map(([campaignId, data]) => {
                                const scoreColor =
                                  data.avg >= 7
                                    ? "text-emerald-400"
                                    : data.avg >= 5
                                      ? "text-amber-400"
                                      : "text-red-400";
                                const barWidth = Math.round((data.avg / 10) * 100);
                                const barColor =
                                  data.avg >= 7
                                    ? "bg-emerald-500/40"
                                    : data.avg >= 5
                                      ? "bg-amber-500/40"
                                      : "bg-red-500/40";
                                const campaignName =
                                  leadQualityData.nameMap?.[campaignId] ||
                                  dashboardData?.campaigns?.find((c) => c.campaignId === campaignId)
                                    ?.campaignName ||
                                  `Campaign ${campaignId}`;
                                return (
                                  <div
                                    key={campaignId}
                                    className="flex items-center gap-dense bg-muted/50 border border-border rounded-lg px-3 py-2"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-foreground truncate">
                                        {campaignName}
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <div className="flex-1 h-1.5 bg-accent rounded-full overflow-hidden">
                                          <div
                                            className={`h-full ${barColor} rounded-full transition-all`}
                                            style={{ width: `${barWidth}%` }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <span className={`text-sm font-bold ${scoreColor}`}>
                                        {data.avg.toFixed(1)}
                                      </span>
                                      <span className="text-[10px] text-muted-foreground ml-0.5">
                                        /10
                                      </span>
                                      <div className="text-[10px] text-muted-foreground">
                                        {t("dashboard.leadQuality.leadCount", {
                                          count: data.count,
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                    {/* Adset Quality Breakdown */}
                    {leadQualityData.byAdset && Object.keys(leadQualityData.byAdset).length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t("dashboard.leadQuality.byAdset")}
                        </h3>
                        <div className="space-y-1.5">
                          {Object.entries(leadQualityData.byAdset)
                            .sort(([, a], [, b]) => b.avg - a.avg)
                            .slice(0, 10)
                            .map(([adsetId, data]) => {
                              const scoreColor =
                                data.avg >= 7
                                  ? "text-emerald-400"
                                  : data.avg >= 5
                                    ? "text-amber-400"
                                    : "text-red-400";
                              const barWidth = Math.round((data.avg / 10) * 100);
                              const barColor =
                                data.avg >= 7
                                  ? "bg-emerald-500/40"
                                  : data.avg >= 5
                                    ? "bg-amber-500/40"
                                    : "bg-red-500/40";
                              return (
                                <div
                                  key={adsetId}
                                  className="flex items-center gap-dense bg-muted/50 border border-border rounded-lg px-3 py-2"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs text-foreground truncate">
                                      {leadQualityData.nameMap?.[adsetId] || `Adset ${adsetId}`}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <div className="flex-1 h-1.5 bg-accent rounded-full overflow-hidden">
                                        <div
                                          className={`h-full ${barColor} rounded-full transition-all`}
                                          style={{ width: `${barWidth}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <span className={`text-sm font-bold ${scoreColor}`}>
                                      {data.avg.toFixed(1)}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground ml-0.5">
                                      /10
                                    </span>
                                    <div className="text-[10px] text-muted-foreground">
                                      {t("dashboard.leadQuality.leadCount", { count: data.count })}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Individual Lead Scores */}
                    {leadQualityData.scores && leadQualityData.scores.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t("dashboard.leadQuality.byLead")}
                        </h3>
                        <div className="space-y-1">
                          {leadQualityData.scores
                            .sort((a, b) => b.qualityScore - a.qualityScore)
                            .map((lead, idx) => {
                              const scoreColor =
                                lead.qualityScore >= 7
                                  ? "text-emerald-400"
                                  : lead.qualityScore >= 5
                                    ? "text-amber-400"
                                    : "text-red-400";
                              const scoreBg =
                                lead.qualityScore >= 7
                                  ? "bg-emerald-500/20"
                                  : lead.qualityScore >= 5
                                    ? "bg-amber-500/20"
                                    : "bg-red-500/20";
                              const adsetName = lead.adsetId
                                ? leadQualityData.nameMap?.[lead.adsetId] || `Adset ${lead.adsetId}`
                                : null;
                              return (
                                <div
                                  key={idx}
                                  className="flex items-start gap-dense bg-muted/50 border border-border rounded-lg px-3 py-2"
                                >
                                  <div
                                    className={`w-8 h-8 rounded-lg ${scoreBg} flex items-center justify-center shrink-0 mt-0.5`}
                                  >
                                    <span className={`text-sm font-bold ${scoreColor}`}>
                                      {lead.qualityScore}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-foreground font-medium">
                                      {lead.leadName}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      {lead.reasoning}
                                    </div>
                                    {adsetName && (
                                      <div className="text-[10px] text-purple-400/60 mt-0.5">
                                        {adsetName}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {leadQualityData.analyzedAt && (
                      <p className="text-[10px] text-muted-foreground text-right">
                        Ostatnia analiza:{" "}
                        {new Date(leadQualityData.analyzedAt).toLocaleString("pl-PL")}
                      </p>
                    )}
                  </>
                ) : (
                  <Card className="bg-muted/40 border border-border rounded-xl">
                    <CardContent className="p-10 text-center">
                      <Sparkles className="w-10 h-10 text-purple-400/50 mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">
                        {t("dashboard.leadQuality.noData")}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Tani model AI przeskanuje odpowiedzi i notatki handlowca
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : activeTab === "roas_world" ? (
              dashboardPreview ? (
                <Card className="bg-muted/40 border-border rounded-xl">
                  <CardContent className="p-6 space-y-5">
                    <div className="space-y-2">
                      <div className="inline-flex items-center gap-2 rounded-full border border-[#ef3e29]/20 bg-[#ef3e29]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#ffb0a5]">
                        <Sparkles className="w-3.5 h-3.5" />
                        {t("dashboard.roasWorld.previewTitle")}
                      </div>
                      <p className="text-sm text-white/75">
                        {t("dashboard.roasWorld.previewDesc")}
                      </p>
                    </div>
                    <div className="grid gap-3 md:grid-cols-3">
                      {(dashboardData?.utmSourceBreakdown || []).slice(0, 3).map((source) => (
                        <div
                          key={source.utmSource}
                          className="rounded-xl border border-white/[0.08] bg-black/20 p-4"
                        >
                          <p className="text-[11px] uppercase tracking-[0.14em] text-white/40">
                            {source.utmSource}
                          </p>
                          <p className="mt-2 text-2xl font-semibold text-white">
                            {formatNumber(source.leads)}
                          </p>
                          <p className="text-xs text-white/55">
                            {t("dashboard.roasWorld.leadsHighPotential")}
                          </p>
                          <p className="mt-3 text-sm text-emerald-300">
                            {formatCurrency(source.revenue)} przychodu
                          </p>
                        </div>
                      ))}
                    </div>
                    {(dashboardData?.organicSourceGroups?.length || 0) > 0 && (
                      <div className="rounded-xl border border-white/[0.08] bg-black/20 p-4">
                        <p className="text-xs font-medium text-white/80">
                          {t("dashboard.roasWorld.organicSources")}
                        </p>
                        <div className="mt-3 space-y-2">
                          {(dashboardData?.organicSourceGroups || []).slice(0, 4).map((group) => (
                            <div
                              key={`${group.utmSource}-${group.utmMedium}`}
                              className="flex items-center justify-between gap-3 text-sm"
                            >
                              <span className="text-white/65">
                                {group.utmSource} / {group.utmMedium || "organic"}
                              </span>
                              <span className="text-white">
                                {formatNumber(group.leads)} {t("dashboard.roasWorld.leads")}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <RoasWorldTab
                  isExpertPlan={isExpertPlan}
                  dashboardMode={dashboardMode}
                  adAccountId={selectedAdAccountId}
                  datePreset={datePreset}
                  customRange={
                    datePreset === "custom" && customFrom && customTo
                      ? {
                          // toLocalYmd (VP-210) — toISOString na lokalnej północy
                          // cofa dzień w strefie PL; parytet z resztą call-site'ów
                          since: toLocalYmd(customFrom),
                          until: toLocalYmd(customTo),
                        }
                      : undefined
                  }
                  forClientUserId={forClientUserId}
                />
              )
            ) : (
              <>
                {/* VP-222: blok „Sklep" (produkty / zamówienia / zwroty) — tylko
                    tryb e-commerce na Shopify. Allegro i podgląd demo go nie
                    mają: pierwszy nie ma sklepu Shopify, drugi nie uderza w
                    prawdziwe API. canAccessShopifyIntegration MUSI zostać:
                    endpointy /shop/* siedzą za `requireShopifyAccess` (VP-223,
                    whitelist), więc bez tego warunku reszta userów dostaje 403
                    i czerwoną kartę błędu na dashboardzie. */}
                {dashboardMode === "ecommerce" &&
                  !isAllegroMode &&
                  !dashboardPreview &&
                  canAccessShopifyIntegration(user as ShopifyAccessUser | null) && (
                    <ShopSection
                      datePreset={datePreset}
                      customRange={
                        datePreset === "custom" && customFrom && customTo
                          ? { since: toLocalYmd(customFrom), until: toLocalYmd(customTo) }
                          : undefined
                      }
                      forClientUserId={forClientUserId}
                    />
                  )}

                {/* Primary KPIs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-dense md:gap-balanced">
                  <DashboardMetricCard
                    title={t("dashboard.kpi.spend")}
                    value={formatCurrency(combinedTotals.spend)}
                    icon={DollarSign}
                    breakdowns={
                      platformBreakdowns.spend.length >= 1 ? platformBreakdowns.spend : undefined
                    }
                    {...deltaToTrendProps(
                      compTotals ? calcDelta(combinedTotals.spend, compTotals.spend) : null,
                      true
                    )}
                  />
                  {dashboardMode === "ecommerce" ? (
                    <>
                      {/* Conversion Value + ROAS from INTEGRATION revenue (Shopify/pixel/CRM →
                      totals.revenue/revenueRoas), never the Meta-pixel purchaseValue/roas. */}
                      <DashboardMetricCard
                        title="Conversion Value"
                        value={
                          combinedTotals.revenue > 0 ? formatCurrency(combinedTotals.revenue) : "—"
                        }
                        icon={Wallet}
                        {...deltaToTrendProps(
                          compTotals ? calcDelta(combinedTotals.revenue, compTotals.revenue) : null
                        )}
                      />
                      <DashboardMetricCard
                        title="ROAS"
                        value={
                          combinedTotals.revenueRoas > 0
                            ? `${combinedTotals.revenueRoas.toFixed(2)}x`
                            : "—"
                        }
                        icon={TrendingUp}
                        {...deltaToTrendProps(
                          compTotals
                            ? calcDelta(combinedTotals.revenueRoas, compTotals.revenueRoas)
                            : null
                        )}
                      />
                    </>
                  ) : dashboardMode === "saas" ? (
                    <>
                      <DashboardMetricCard
                        title="Triale"
                        value={formatNumber(combinedTotals.trials)}
                        icon={Users}
                        {...deltaToTrendProps(
                          compTotals ? calcDelta(combinedTotals.trials, compTotals.trials) : null
                        )}
                      />
                      <DashboardMetricCard
                        title="CPA Trial"
                        value={
                          combinedTotals.costPerTrial > 0
                            ? formatCurrency(combinedTotals.costPerTrial)
                            : "—"
                        }
                        icon={DollarSign}
                        {...deltaToTrendProps(
                          compTotals
                            ? calcDelta(combinedTotals.costPerTrial, compTotals.costPerTrial)
                            : null,
                          true
                        )}
                      />
                    </>
                  ) : (
                    <>
                      <DashboardMetricCard
                        title={t("dashboard.kpi.leads")}
                        value={formatNumber(effectiveTotalLeads)}
                        icon={Users}
                        breakdowns={
                          effectiveLeadBreakdowns.length >= 1 ? effectiveLeadBreakdowns : undefined
                        }
                        {...deltaToTrendProps(
                          compTotals ? calcDelta(effectiveTotalLeads, compTotals.leads) : null
                        )}
                      />
                      <DashboardMetricCard
                        title={t("dashboard.kpi.impressions")}
                        value={formatNumber(combinedTotals.impressions)}
                        icon={Eye}
                        breakdowns={
                          platformBreakdowns.impressions.length >= 1
                            ? platformBreakdowns.impressions
                            : undefined
                        }
                        {...deltaToTrendProps(
                          compTotals
                            ? calcDelta(combinedTotals.impressions, compTotals.impressions)
                            : null
                        )}
                      />
                    </>
                  )}
                </div>

                {/* Setter KPIs in overview (lead_gen_advanced only) */}
                {dashboardMode === "lead_gen_advanced" && effectiveSetterStats && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-dense">
                    <DashboardMetricCard
                      label={t("dashboard.setterMetrics.attempts")}
                      value={formatNumber(effectiveSetterStats.outgoingCallAttempts)}
                      icon={PhoneCall}
                      variant="highlight"
                    />
                    <DashboardMetricCard
                      label="Odebrane"
                      value={formatNumber(effectiveSetterStats.answeredCalls)}
                      icon={Phone}
                      variant="highlight"
                    />
                    <DashboardMetricCard
                      label={t("dashboard.setterMetrics.booked")}
                      value={formatNumber(effectiveSetterStats.bookedMeetings)}
                      icon={CalendarCheck}
                      variant="highlight"
                    />
                    <DashboardMetricCard
                      label="Odbyte (showed)"
                      value={formatNumber(effectiveSetterStats.showedMeetings)}
                      icon={UserCheck}
                      variant="highlight"
                    />
                    <DashboardMetricCard
                      label={t("dashboard.setterMetrics.sales")}
                      value={formatNumber(effectiveSetterStats.sales)}
                      icon={TrendingUp}
                      variant="highlight"
                    />
                    <DashboardMetricCard
                      label="Close rate"
                      value={formatPercent(effectiveSetterStats.closeRate)}
                      icon={Percent}
                    />
                  </div>
                )}

                {/* SaaS Unit Economics */}
                {dashboardMode === "saas" && (
                  <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-[#ef3e29]" />
                      Metryki SaaS
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-dense">
                      <DashboardMetricCard
                        label="Triale"
                        value={formatNumber(combinedTotals.trials)}
                        icon={Users}
                        variant="highlight"
                        delta={
                          compTotals ? calcDelta(combinedTotals.trials, compTotals.trials) : null
                        }
                      />
                      <DashboardMetricCard
                        label="CPA Trial"
                        value={
                          combinedTotals.costPerTrial > 0
                            ? formatCurrency(combinedTotals.costPerTrial)
                            : "—"
                        }
                        icon={DollarSign}
                        variant="highlight"
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.costPerTrial, compTotals.costPerTrial)
                            : null
                        }
                        invertDelta
                      />
                      <DashboardMetricCard
                        label="Subskrypcje"
                        value={
                          combinedTotals.subscriptions > 0
                            ? formatNumber(combinedTotals.subscriptions)
                            : "—"
                        }
                        icon={UserCheck}
                        variant="highlight"
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.subscriptions, compTotals.subscriptions)
                            : null
                        }
                      />
                      <DashboardMetricCard
                        label="CPA Subskrypcja"
                        value={
                          combinedTotals.costPerSubscription > 0
                            ? formatCurrency(combinedTotals.costPerSubscription)
                            : "—"
                        }
                        icon={DollarSign}
                        variant="highlight"
                        delta={
                          compTotals
                            ? calcDelta(
                                combinedTotals.costPerSubscription,
                                compTotals.costPerSubscription
                              )
                            : null
                        }
                        invertDelta
                      />
                      <DashboardMetricCard
                        label="CR Trial → Sub"
                        value={
                          combinedTotals.trials > 0 && combinedTotals.subscriptions > 0
                            ? formatPercent(
                                (combinedTotals.subscriptions / combinedTotals.trials) * 100
                              )
                            : "—"
                        }
                        icon={TrendingUp}
                        variant="highlight"
                        delta={
                          compTotals &&
                          compTotals.trials > 0 &&
                          compTotals.subscriptions > 0 &&
                          combinedTotals.trials > 0 &&
                          combinedTotals.subscriptions > 0
                            ? calcDelta(
                                (combinedTotals.subscriptions / combinedTotals.trials) * 100,
                                (compTotals.subscriptions / compTotals.trials) * 100
                              )
                            : null
                        }
                      />
                      <DashboardMetricCard
                        label="CR klik → trial"
                        value={
                          combinedTotals.clicks > 0 && combinedTotals.trials > 0
                            ? formatPercent((combinedTotals.trials / combinedTotals.clicks) * 100)
                            : "—"
                        }
                        icon={TrendingUp}
                        delta={
                          compTotals &&
                          compTotals.clicks > 0 &&
                          compTotals.trials > 0 &&
                          combinedTotals.clicks > 0 &&
                          combinedTotals.trials > 0
                            ? calcDelta(
                                (combinedTotals.trials / combinedTotals.clicks) * 100,
                                (compTotals.trials / compTotals.clicks) * 100
                              )
                            : null
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-dense">
                      <DashboardMetricCard
                        label="Landing page views"
                        value={formatNumber(combinedTotals.landingPageViews)}
                        icon={Eye}
                        delta={
                          compTotals
                            ? calcDelta(
                                combinedTotals.landingPageViews,
                                compTotals.landingPageViews
                              )
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.landingPageViews.length >= 1
                            ? platformBreakdowns.landingPageViews
                            : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="CPC (outbound)"
                        value={formatCurrency(combinedTotals.cpc)}
                        icon={MousePointerClick}
                        delta={compTotals ? calcDelta(combinedTotals.cpc, compTotals.cpc) : null}
                        invertDelta
                        breakdowns={
                          platformBreakdowns.cpc.length >= 1 ? platformBreakdowns.cpc : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="CPM"
                        value={formatCurrency(combinedTotals.cpm)}
                        icon={Target}
                        delta={compTotals ? calcDelta(combinedTotals.cpm, compTotals.cpm) : null}
                        invertDelta
                        breakdowns={
                          platformBreakdowns.cpm.length >= 1 ? platformBreakdowns.cpm : undefined
                        }
                      />
                      <DashboardMetricCard
                        label={t("dashboard.kpi.clicks")}
                        value={formatNumber(combinedTotals.outboundClicks)}
                        icon={MousePointerClick}
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.outboundClicks, compTotals.outboundClicks)
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.clicks.length >= 1
                            ? platformBreakdowns.clicks
                            : undefined
                        }
                      />
                    </div>
                    {combinedTotals.ltv === 0 && (
                      <p className="text-[10px] text-muted-foreground pl-1">
                        {t("dashboard.kpi.ltvHint")}
                      </p>
                    )}
                  </div>
                )}

                {dashboardMode === "ecommerce" ? (
                  <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-[#ef3e29]" />
                      Metryki Ecommerce
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-dense">
                      {/* Zakupy/CPA/AOV from INTEGRATION (customers + revenue), never Meta-pixel
                      purchases/purchaseValue. AOV uses revenue÷customers (same integration source). */}
                      <DashboardMetricCard
                        label="CPA"
                        value={
                          combinedTotals.customers > 0
                            ? formatCurrency(combinedTotals.spend / combinedTotals.customers)
                            : "—"
                        }
                        icon={DollarSign}
                        variant="highlight"
                        delta={
                          compTotals && compTotals.customers > 0 && combinedTotals.customers > 0
                            ? calcDelta(
                                combinedTotals.spend / combinedTotals.customers,
                                compTotals.spend / compTotals.customers
                              )
                            : null
                        }
                        invertDelta
                        breakdowns={
                          platformBreakdowns.cpa.length >= 1 ? platformBreakdowns.cpa : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="AOV"
                        value={
                          combinedTotals.customers > 0
                            ? formatCurrency(combinedTotals.revenue / combinedTotals.customers)
                            : "—"
                        }
                        icon={Wallet}
                        variant="highlight"
                        delta={
                          compTotals && compTotals.customers > 0 && combinedTotals.customers > 0
                            ? calcDelta(
                                combinedTotals.revenue / combinedTotals.customers,
                                compTotals.revenue / compTotals.customers
                              )
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.aov.length >= 1 ? platformBreakdowns.aov : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="Zakupy"
                        value={
                          combinedTotals.customers > 0
                            ? formatNumber(combinedTotals.customers)
                            : "—"
                        }
                        icon={TrendingUp}
                        variant="highlight"
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.customers, compTotals.customers)
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.purchases.length >= 1
                            ? platformBreakdowns.purchases
                            : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="CTR"
                        value={`${combinedTotals.ctr.toFixed(2)}%`}
                        icon={MousePointerClick}
                        variant="highlight"
                        delta={compTotals ? calcDelta(combinedTotals.ctr, compTotals.ctr) : null}
                        breakdowns={
                          platformBreakdowns.ctr.length >= 1 ? platformBreakdowns.ctr : undefined
                        }
                      />
                      <DashboardMetricCard
                        label={t("dashboard.kpi.clicks")}
                        value={formatNumber(combinedTotals.outboundClicks)}
                        icon={Eye}
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.outboundClicks, compTotals.outboundClicks)
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.clicks.length >= 1
                            ? platformBreakdowns.clicks
                            : undefined
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-dense">
                      <DashboardMetricCard
                        label="CPM"
                        value={formatCurrency(combinedTotals.cpm)}
                        icon={Target}
                        delta={compTotals ? calcDelta(combinedTotals.cpm, compTotals.cpm) : null}
                        invertDelta
                        breakdowns={
                          platformBreakdowns.cpm.length >= 1 ? platformBreakdowns.cpm : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="CPC (outbound)"
                        value={formatCurrency(combinedTotals.cpc)}
                        icon={MousePointerClick}
                        delta={compTotals ? calcDelta(combinedTotals.cpc, compTotals.cpc) : null}
                        invertDelta
                        breakdowns={
                          platformBreakdowns.cpc.length >= 1 ? platformBreakdowns.cpc : undefined
                        }
                      />
                      <DashboardMetricCard
                        label={t("dashboard.kpi.impressions")}
                        value={formatNumber(combinedTotals.impressions)}
                        icon={Eye}
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.impressions, compTotals.impressions)
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.impressions.length >= 1
                            ? platformBreakdowns.impressions
                            : undefined
                        }
                      />
                    </div>
                  </div>
                ) : dashboardMode !== "saas" ? (
                  <>
                    <div className="space-y-2">
                      <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-[#ef3e29]" />
                        ROAS & Metryki Biznesowe
                        {dashboardData?.revenueMode === "attribution" && (
                          <span className="text-[10px] font-medium normal-case tracking-normal px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            Atrybucja reklam (first-touch)
                          </span>
                        )}
                        {dashboardData?.revenueMode === "cash" && (
                          <span className="text-[10px] font-medium normal-case tracking-normal px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                            Cash-basis (brak pixela)
                          </span>
                        )}
                      </h2>
                      {dashboardData?.revenueMode === "attribution" && (
                        <p className="text-[11px] text-muted-foreground pl-1">
                          Revenue liczone kasowo:{" "}
                          <strong>sprzedaze zamkniete w wybranym okresie</strong> / spend okresu.
                          Kazda sprzedaz przypisana do konkretnej reklamy przez first-touch. Liczby
                          zgadzaja sie z tabela Sales Data.
                        </p>
                      )}
                      {dashboardData?.revenueMode === "cash" && (
                        <p className="text-[11px] text-amber-400/80 pl-1">
                          Brak danych z pixela — pokazujemy cash-basis (sprzedaze zamkniete w
                          okresie / spend okresu). ROAS moze byc zawyzony w krotkich oknach.
                          Zainstaluj tracking pixel zeby zobaczyc prawdziwa atrybucje.
                        </p>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-dense">
                        <DashboardMetricCard
                          label="ROAS"
                          value={
                            combinedTotals.revenueRoas > 0
                              ? `${combinedTotals.revenueRoas.toFixed(2)}x`
                              : "—"
                          }
                          icon={TrendingUp}
                          variant="highlight"
                          delta={
                            compTotals
                              ? calcDelta(combinedTotals.revenueRoas, compTotals.revenueRoas)
                              : null
                          }
                          warning={
                            combinedTotals.revenueRoas > 15
                              ? dashboardData?.revenueMode === "cash"
                                ? "Podejrzanie wysoki — cash-basis w krótkim oknie zawyża ROAS. Zainstaluj pixel dla realnej atrybucji."
                                : "Podejrzanie wysoki — w krótkim oknie sprzedaż mogła zamknąć się z wydatku sprzed okresu (cash-basis)."
                              : undefined
                          }
                        />
                        <DashboardMetricCard
                          label={t("dashboard.kpi.ltv")}
                          value={combinedTotals.ltv > 0 ? formatCurrency(combinedTotals.ltv) : "—"}
                          icon={UserCheck}
                          variant="highlight"
                          delta={compTotals ? calcDelta(combinedTotals.ltv, compTotals.ltv) : null}
                        />
                        <DashboardMetricCard
                          label="LTV:CAC"
                          value={
                            combinedTotals.ltvCac > 0 ? `${combinedTotals.ltvCac.toFixed(2)}x` : "—"
                          }
                          icon={TrendingUp}
                          variant="highlight"
                          delta={
                            compTotals ? calcDelta(combinedTotals.ltvCac, compTotals.ltvCac) : null
                          }
                        />
                        <DashboardMetricCard
                          label={t("dashboard.kpi.revenue")}
                          value={
                            combinedTotals.revenue > 0
                              ? formatCurrency(combinedTotals.revenue)
                              : "—"
                          }
                          icon={Wallet}
                          variant="highlight"
                          delta={
                            compTotals
                              ? calcDelta(combinedTotals.revenue, compTotals.revenue)
                              : null
                          }
                          warning={
                            combinedTotals.revenue === 0 && combinedTotals.sales > 0
                              ? t("dashboard.kpi.salesNoValue", { count: combinedTotals.sales })
                              : undefined
                          }
                        />
                      </div>
                      {combinedTotals.revenueRoas === 0 && (
                        <p className="text-[10px] text-muted-foreground pl-1">
                          {t("dashboard.kpi.roasHint")}
                        </p>
                      )}
                    </div>

                    {/* Secondary KPIs */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-dense">
                      <DashboardMetricCard
                        label="CPM"
                        value={formatCurrency(combinedTotals.cpm)}
                        icon={Target}
                        delta={compTotals ? calcDelta(combinedTotals.cpm, compTotals.cpm) : null}
                        invertDelta
                        breakdowns={
                          platformBreakdowns.cpm.length >= 1 ? platformBreakdowns.cpm : undefined
                        }
                      />
                      <DashboardMetricCard
                        label={t("dashboard.kpi.clicks")}
                        value={formatNumber(combinedTotals.outboundClicks)}
                        icon={MousePointerClick}
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.outboundClicks, compTotals.outboundClicks)
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.clicks.length >= 1
                            ? platformBreakdowns.clicks
                            : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="Landing page views"
                        value={formatNumber(combinedTotals.landingPageViews)}
                        icon={Eye}
                        delta={
                          compTotals
                            ? calcDelta(
                                combinedTotals.landingPageViews,
                                compTotals.landingPageViews
                              )
                            : null
                        }
                        breakdowns={
                          platformBreakdowns.landingPageViews.length >= 1
                            ? platformBreakdowns.landingPageViews
                            : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="Koszt za leada"
                        value={
                          combinedTotals.costPerLead > 0
                            ? formatCurrency(combinedTotals.costPerLead)
                            : "-"
                        }
                        icon={DollarSign}
                        delta={
                          compTotals
                            ? calcDelta(combinedTotals.costPerLead, compTotals.costPerLead)
                            : null
                        }
                        invertDelta
                        breakdowns={
                          platformBreakdowns.costPerLead.length >= 1
                            ? platformBreakdowns.costPerLead
                            : undefined
                        }
                      />
                      <DashboardMetricCard
                        label="CPC (outbound)"
                        value={formatCurrency(combinedTotals.cpc)}
                        icon={MousePointerClick}
                        delta={compTotals ? calcDelta(combinedTotals.cpc, compTotals.cpc) : null}
                        invertDelta
                        breakdowns={
                          platformBreakdowns.cpc.length >= 1 ? platformBreakdowns.cpc : undefined
                        }
                      />
                    </div>

                    {/* Conversion Rates & Appointments */}
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 pl-1">
                        <CalendarCheck className="w-3.5 h-3.5 text-[#ef3e29] mt-0.5 shrink-0" />
                        <p className="text-[11px] text-muted-foreground leading-tight">
                          Rozmowy liczone wg <strong>daty umówienia</strong> w wybranym okresie.
                          Zakładka <strong>Setter</strong> liczy wg <strong>dnia rozmowy</strong>, a
                          tabela kampanii — tylko te z atrybucją Meta, więc liczby mogą się różnić
                          (to różne populacje, nie błąd).
                        </p>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-dense">
                        <DashboardMetricCard
                          label={t("dashboard.kpi.bookedCalls")}
                          value={formatNumber(combinedTotals.bookedAppointments)}
                          icon={CalendarCheck}
                        />
                        <DashboardMetricCard
                          label="Odbyte (showed)"
                          value={formatNumber(combinedTotals.showedAppointments || 0)}
                          icon={UserCheck}
                          variant="highlight"
                        />
                        <DashboardMetricCard
                          label="No-show"
                          value={formatNumber(combinedTotals.noShowAppointments || 0)}
                          icon={PhoneOff}
                        />
                        <DashboardMetricCard
                          label="Show rate"
                          // '—' when there are calls but no resolved outcomes (setters mark neither
                          // showed nor no-show) — avoids a misleading 100%/0%.
                          value={
                            combinedTotals.showedAppointments + combinedTotals.noShowAppointments >
                            0
                              ? formatPercent(combinedTotals.showRate || 0)
                              : combinedTotals.bookedAppointments > 0
                                ? "—"
                                : formatPercent(0)
                          }
                          icon={Percent}
                          variant="primary"
                          delta={
                            compTotals
                              ? calcDelta(combinedTotals.showRate || 0, compTotals.showRate || 0)
                              : null
                          }
                        />
                        <DashboardMetricCard
                          label="CR lead → rozmowa"
                          value={formatPercent(combinedTotals.crLeadToBooking || 0)}
                          icon={UserCheck}
                          delta={
                            compTotals
                              ? calcDelta(
                                  combinedTotals.crLeadToBooking || 0,
                                  compTotals.crLeadToBooking || 0
                                )
                              : null
                          }
                        />
                        <DashboardMetricCard
                          label={t("dashboard.kpi.crClickToLead")}
                          value={formatPercent(combinedTotals.crClickToLead)}
                          icon={TrendingUp}
                          delta={
                            compTotals
                              ? calcDelta(combinedTotals.crClickToLead, compTotals.crClickToLead)
                              : null
                          }
                        />
                        <DashboardMetricCard
                          label={t("dashboard.kpi.salesCycle")}
                          value={
                            combinedTotals.avgSalesCycleDays > 0
                              ? `${combinedTotals.avgSalesCycleDays} dni`
                              : "—"
                          }
                          icon={Clock}
                          delta={
                            compTotals
                              ? calcDelta(
                                  combinedTotals.avgSalesCycleDays,
                                  compTotals.avgSalesCycleDays || 0
                                )
                              : null
                          }
                          invertDelta
                        />
                      </div>
                      {/* VP-140: czemu Show rate = '—' — są rozmowy, ale brak mianownika (odbyte+no-show=0):
                albo jeszcze się nie odbyły, albo odbyły się, lecz nikt nie oznaczył wyniku w GHL. */}
                      {combinedTotals.bookedAppointments > 0 &&
                        combinedTotals.showedAppointments + combinedTotals.noShowAppointments ===
                          0 && (
                          <p className="text-[11px] text-amber-300/90 leading-tight mt-2">
                            Show rate pojawi się, gdy zaplanowane rozmowy się odbędą i zostaną
                            oznaczone w GHL jako odbyte / no-show.
                          </p>
                        )}
                    </div>
                  </>
                ) : null}

                {/* KPI Forecast Card (all modes) */}
                {(analyticsSettingsData?.quarterlyTargets ||
                  analyticsSettingsData?.monthlyTargets) && (
                  <QuarterlyForecastCard
                    quarterlyTargets={analyticsSettingsData.quarterlyTargets ?? null}
                    monthlyTargets={analyticsSettingsData.monthlyTargets ?? null}
                    dashboardMode={dashboardMode}
                    actualLeads={combinedTotals.leads}
                    actualCalls={combinedTotals.bookedAppointments}
                    actualSales={combinedTotals.sales}
                    actualPurchaseValue={combinedTotals.revenue}
                    actualPurchases={combinedTotals.customers}
                    actualRoas={combinedTotals.revenueRoas}
                    actualTrials={combinedTotals.trials}
                    actualPaid={combinedTotals.subscriptions}
                  />
                )}

                {/* Metrics Chart */}
                <MetricsChart
                  daily={enrichDailyWithSetter(dailyData?.daily || [], effectiveSetterStats)}
                  isLoading={isDailyLoading}
                  dashboardMode={dashboardMode}
                />

                {/* Campaign Table with expandable ad sets / ads */}
                <CampaignTable
                  campaigns={filteredCampaigns}
                  datePreset={datePreset}
                  customRange={
                    customFrom && customTo
                      ? { since: fmtDate(customFrom), until: fmtDate(customTo) }
                      : undefined
                  }
                  adAccountId={selectedAdAccountId}
                  dashboardMode={dashboardMode}
                  totals={totals}
                  utmAttribution={dashboardData?.utmAttribution}
                  adUtmAttribution={dashboardData?.adUtmAttribution}
                  leadsByAdSet={dashboardData?.leadsByAdSet}
                  leadsByAd={dashboardData?.leadsByAd}
                  ecomRevenue={{
                    revenueByCampaign: dashboardData?.revenueByCampaign,
                    revenueByAdSet: dashboardData?.revenueByAdSet,
                    revenueByAd: dashboardData?.revenueByAd,
                    salesByCampaign: dashboardData?.salesByCampaign,
                    salesByAdSet: dashboardData?.salesByAdSet,
                    salesByAd: dashboardData?.salesByAd,
                  }}
                  metricsV2On={!!dashboardData?.metricsV2}
                  webinarStats={dashboardData?.webinarStats}
                  forClientUserId={forClientUserId}
                  organicSourceGroups={dashboardData?.organicSourceGroups}
                  googleAdsAdGroups={googleAdsInsights?.adGroups}
                  previewEnabled={dashboardPreview}
                  attributionModel={attributionModel}
                />
              </>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center py-32">
            <Card className="bg-card border-border max-w-md">
              <CardContent className="p-6 text-center">
                <BarChart3 className="w-10 h-10 text-[#ef3e29]/40 mx-auto mb-3" />
                <p className="text-muted-foreground mb-1">Brak aktywnych kampanii w tym okresie</p>
                <p className="text-muted-foreground text-sm mb-4">
                  {t("dashboard.noCampaignsHint")}
                </p>
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="px-4 py-2 bg-[#ef3e29] hover:bg-[#d63520] text-foreground text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Skonfiguruj kampanie →
                </button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Settings Modal for dashboard-only users */}
      <AnalyticsSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        forClientUserId={forClientUserId}
      />

      {/* Report Dialog */}
      <DashboardReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        totals={combinedTotals}
        effectiveTotalLeads={effectiveTotalLeads}
        accountName={selectedClient?.name || dashboardData?.adAccountName || "Meta Ads"}
        dateLabel={DATE_PRESETS.find((p) => p.value === datePreset)?.label || datePreset}
        dashboardMode={dashboardMode}
        previewEnabled={dashboardPreview}
      />

      {/* Day note editor */}
      <Dialog
        open={!!noteEditorDate}
        onOpenChange={(open) => {
          if (!open) {
            setNoteEditorDate(null);
            setNoteEditorDraft("");
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Notatka —{" "}
              {noteEditorDate
                ? new Date(noteEditorDate + "T12:00:00").toLocaleDateString("pl-PL", {
                    weekday: "long",
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : ""}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={noteEditorDraft}
            onChange={(e) => setNoteEditorDraft(e.target.value)}
            placeholder={t("dashboard.dayNote.placeholder")}
            className="min-h-[140px]"
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => {
                setNoteEditorDate(null);
                setNoteEditorDraft("");
              }}
              className="px-3 py-1.5 text-xs bg-accent text-muted-foreground rounded-lg border border-border hover:text-foreground transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={() => {
                if (!noteEditorDate) return;
                setDayNote(noteEditorDate, noteEditorDraft);
                setNoteEditorDate(null);
                setNoteEditorDraft("");
              }}
              className="px-3 py-1.5 text-xs bg-[#ef3e29]/20 text-[#ef3e29] rounded-lg border border-[#ef3e29]/30 hover:bg-[#ef3e29]/30 transition-colors"
            >
              Zapisz
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
